import { A as UnorderListIcon, B as H1Icon, Bt as CropIcon, F as QuoteIcon, Ft as EyeOffIcon, Ht as ClockIcon, I as OrderListIcon, It as EyeIcon, Kt as CircleOffIcon, L as ItalicIcon, M as UnderlineIcon, N as SquareImageIcon, O as RotateCCWIcon, P as RedoIcon, Qt as ChevronLeftIcon, R as ImageIcon, Ut as CircleXIcon, V as BoldIcon, Vt as CopyIcon, Zt as ChevronRightIcon, dt as ListIcon, en as CheckIcon, ft as LinkIcon, gt as InfoIcon, h as Trash2Icon, hn as Icon, j as UndoIcon, kt as FileIcon, p as UploadIcon, qt as CircleCheckIcon, r as VerticalGripIcon, rn as CalendarDaysIcon, z as H2Icon } from "./icons-CPQCRIUN.js";
import { AbsoluteCenter, Accordion, ActionBar, Alert, Badge, Box, Box as Box$1, Button, ButtonGroup, Checkbox, CheckboxCard, CheckboxGroup, Clipboard, CloseButton, Collapsible, ColorPicker, Combobox, DatePicker, Dialog, DownloadTrigger, Drawer, Field, FileUpload, Flex, Flex as Flex$1, Grid, Grid as Grid$1, Group, Heading, Highlight, IconButton, IconButton as IconButton$1, Image as Image$2, Input, InputGroup, Link, Menu, Pagination, Popover, Popover as Popover$1, Portal, Portal as Portal$1, Progress, ProgressCircle, QrCode, RadioCard, RadioGroup, ScrollArea, SegmentGroup, Select, Separator, Skeleton, SkeletonCircle, SkeletonText, Spinner, Switch, Table, Tabs, TagsInput, Text, Text as Text$1, Textarea, Toast, Toaster, Tooltip, VisuallyHidden, chakra, createListCollection, createToaster, mergeRefs, parseColor, parseDate, useCombobox, useComboboxContext, useControllableState, useFileUploadContext, useFilter, useListCollection, useTagsInput } from "@chakra-ui/react";
import { Children, Fragment, cloneElement, createContext, createElement, forwardRef, isValidElement, memo, useCallback, useContext, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { addDays, addHours, addMinutes, addMonths, addWeeks, eachDayOfInterval, endOfDay, endOfMonth, endOfWeek, format, getDay, isToday, min, parse, startOfDay, startOfMonth, startOfWeek } from "date-fns";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { es, fr } from "date-fns/locale";
import { FloatingFocusManager, FloatingPortal, autoUpdate, flip, offset, shift, size, useDismiss, useFloating, useInteractions, useRole } from "@floating-ui/react";
import { flushSync } from "react-dom";
import { useMaskInput } from "use-mask-input";
import { DndContext, DragOverlay, closestCenter, closestCorners, useDroppable } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Cropper } from "react-cropper";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import { generateHTML, generateJSON } from "@tiptap/core";
import { DraftConverter } from "@tiptap/draftjs-to-tiptap";
import Image from "@tiptap/extension-image";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions/placeholder";
// src/components/action-bar.tsx
const ActionBar$1 = Object.assign(ActionBar.Root, {
	Positioner: ActionBar.Positioner,
	Content: ActionBar.Content,
	CloseTrigger: ActionBar.CloseTrigger,
	SelectionTrigger: ActionBar.SelectionTrigger,
	Separator: ActionBar.Separator
});
// src/components/calendar/calendar-context.ts
const CalendarContext = createContext(null);
function useCalendarContext() {
	const context = useContext(CalendarContext);
	if (context === null) throw new Error("useCalendarContext must be used within a Calendar");
	return context;
}
// src/components/calendar/calendar-event-chip.tsx
const STRIPES = "repeating-linear-gradient(45deg, transparent 0 10px, rgba(0, 0, 0, 0.2) 10px 20px)";
function CalendarEventChip({ eventColor = "colorPalette.solid", striped, ...props }) {
	return /* @__PURE__ */ jsx(Box, {
		height: "full",
		paddingX: "1.5",
		paddingY: "0.5",
		borderRadius: "lg",
		textStyle: "xs",
		fontWeight: "medium",
		truncate: true,
		background: eventColor,
		color: "colorPalette.contrast",
		backgroundImage: striped ? STRIPES : void 0,
		...props
	});
}
/** Start time shown before the title, e.g. in month view */
function CalendarEventTime({ date }) {
	return /* @__PURE__ */ jsx(Box, {
		as: "span",
		fontWeight: "normal",
		children: `${format(date, "HH:mm")} `
	});
}
// src/components/calendar/localizer.ts
const DATE_FNS_LOCALES = {
	fr,
	es
};
const localizer = dateFnsLocalizer({
	format,
	parse,
	startOfWeek,
	getDay,
	locales: DATE_FNS_LOCALES
});
// src/components/calendar/calendar-headers.tsx
const DAY_NUMBER_SIZES = {
	week: {
		textStyle: "md",
		minWidth: "7",
		paddingX: "1.5"
	},
	month: {
		textStyle: "xs",
		minWidth: "5",
		paddingX: "1"
	}
};
function getDayNumberCss(date, size) {
	return {
		...DAY_NUMBER_SIZES[size],
		display: "inline-block",
		borderRadius: "md",
		textAlign: "center",
		...isToday(date) && {
			background: "brand.solid",
			color: "brand.contrast",
			fontWeight: "bold"
		}
	};
}
function WeekdayLabel({ date }) {
	const { locale } = useCalendarContext();
	const weekday = format(date, "EEEE", { locale: DATE_FNS_LOCALES[locale] });
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Text, {
		as: "span",
		hideFrom: "md",
		children: weekday.at(0)?.toUpperCase()
	}), /* @__PURE__ */ jsx(Text, {
		as: "span",
		hideBelow: "md",
		children: weekday.toUpperCase()
	})] });
}
function MonthHeader({ date }) {
	return /* @__PURE__ */ jsx(Text, {
		textStyle: "xs",
		fontWeight: "semibold",
		paddingY: "2",
		children: /* @__PURE__ */ jsx(WeekdayLabel, { date })
	});
}
function WeekHeader({ date }) {
	return /* @__PURE__ */ jsxs(Flex, {
		flexDirection: "column",
		alignItems: "center",
		gap: "0.5",
		paddingY: "1",
		children: [/* @__PURE__ */ jsx(Text, {
			textStyle: "xs",
			fontWeight: isToday(date) ? "bold" : "semibold",
			children: /* @__PURE__ */ jsx(WeekdayLabel, { date })
		}), /* @__PURE__ */ jsx(Text, {
			css: getDayNumberCss(date, "week"),
			children: format(date, "d")
		})]
	});
}
function MonthDateHeader({ date, drilldownView, onDrillDown }) {
	const label = format(date, "d");
	if (!drilldownView) return /* @__PURE__ */ jsx(Text, {
		css: getDayNumberCss(date, "month"),
		children: label
	});
	return /* @__PURE__ */ jsx(chakra.button, {
		type: "button",
		cursor: "pointer",
		css: getDayNumberCss(date, "month"),
		_hover: isToday(date) ? void 0 : { color: "brand.solid" },
		onClick: onDrillDown,
		children: label
	});
}
// src/components/calendar/calendar-components.tsx
function CalendarEventContent({ event }) {
	const { renderEvent, view } = useCalendarContext();
	return renderEvent(event, { view });
}
const defaultRenderEvent = (event, { view }) => /* @__PURE__ */ jsxs(CalendarEventChip, { children: [view === "month" && /* @__PURE__ */ jsx(CalendarEventTime, { date: event.start }), event.title] });
const CALENDAR_COMPONENTS = {
	event: CalendarEventContent,
	week: { header: WeekHeader },
	day: { header: WeekHeader },
	month: {
		header: MonthHeader,
		dateHeader: MonthDateHeader
	}
};
// src/components/calendar/popover-positioning.ts
const POPOVER_ATTRIBUTE = "data-calendar-popover";
const MIN_HEIGHT = 200;
const MAX_BELOW_OR_ABOVE_HEIGHT = 500;
const VIEWPORT_PADDING = 8;
const BESIDE_OFFSET = 8;
const POPOVER_BOX_PROPS = {
	beside: {
		padding: "3",
		minWidth: "72",
		maxWidth: "sm"
	},
	belowOrAbove: {
		padding: "3",
		width: "56"
	}
};
const POPOVER_POSITIONING = {
	beside: {
		placement: "right-start",
		middleware: [
			offset(BESIDE_OFFSET),
			flip({
				crossAxis: false,
				fallbackPlacements: [
					"left-start",
					"bottom",
					"top"
				],
				padding: VIEWPORT_PADDING
			}),
			shift({ padding: VIEWPORT_PADDING }),
			size({
				padding: VIEWPORT_PADDING,
				apply({ availableHeight, elements }) {
					elements.floating.style.maxHeight = `${Math.max(MIN_HEIGHT, availableHeight)}px`;
				}
			})
		]
	},
	belowOrAbove: {
		placement: "bottom",
		middleware: [
			offset(4),
			flip({
				fallbackPlacements: ["top"],
				padding: VIEWPORT_PADDING
			}),
			size({
				padding: VIEWPORT_PADDING,
				apply({ availableHeight, elements }) {
					elements.floating.style.maxHeight = `${Math.min(MAX_BELOW_OR_ABOVE_HEIGHT, availableHeight)}px`;
				}
			}),
			shift({
				crossAxis: true,
				padding: VIEWPORT_PADDING
			})
		]
	}
};
const toRect = (left, width, top, height) => ({
	x: left,
	y: top,
	left,
	top,
	width,
	height,
	right: left + width,
	bottom: top + height
});
/**
* Anchor for a popover opened from an element inside another calendar
* popover: vertically the element, horizontally the enclosing popover, so the
* new popover sits flush against it instead of overlapping it
*/
function getAnchorBesidePopover(element) {
	const popover = element.closest(`[${POPOVER_ATTRIBUTE}]`);
	if (!popover) return;
	return {
		contextElement: element,
		getBoundingClientRect: () => {
			const { left, right } = popover.getBoundingClientRect();
			const { top, height } = element.getBoundingClientRect();
			return toRect(left + BESIDE_OFFSET, right - left - 16, top, height);
		}
	};
}
/**
* Anchor of the "+N" list: vertically the link, horizontally its whole day
* column (the link is narrower than the cell), so the list stays centered on
* the cell
*/
function getShowMoreAnchor(showMoreLink) {
	const column = showMoreLink.closest(".rbc-row-segment") ?? showMoreLink;
	return {
		contextElement: showMoreLink,
		getBoundingClientRect: () => {
			const { left, width } = column.getBoundingClientRect();
			const { top, height } = showMoreLink.getBoundingClientRect();
			return toRect(left, width, top, height);
		}
	};
}
// src/components/calendar/calendar-popover.tsx
const isOutsideCalendarPopovers = (event) => !event.target?.closest?.(`[${POPOVER_ATTRIBUTE}]`);
function CalendarPopover({ store, positioning, render }) {
	const state = useSyncExternalStore(store.subscribe, store.get);
	const { refs: { setFloating, setPositionReference }, floatingStyles, context } = useFloating({
		open: state !== null,
		onOpenChange: (open) => {
			if (!open) store.close();
		},
		elements: { reference: state?.anchor ?? null },
		...POPOVER_POSITIONING[positioning],
		whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, () => {
			if (store.get()?.anchor.isConnected === false) {
				store.close();
				return;
			}
			update();
		})
	});
	useLayoutEffect(() => {
		setPositionReference(state?.positionAnchor ?? null);
	}, [state, setPositionReference]);
	const dismiss = useDismiss(context, { outsidePress: isOutsideCalendarPopovers });
	const role = useRole(context, { role: "dialog" });
	const { getFloatingProps } = useInteractions([dismiss, role]);
	if (state === null) return null;
	return /* @__PURE__ */ jsx(FloatingPortal, { children: /* @__PURE__ */ jsx(FloatingFocusManager, {
		context,
		modal: false,
		closeOnFocusOut: false,
		order: ["floating", "content"],
		children: /* @__PURE__ */ jsx(Box, {
			ref: setFloating,
			style: floatingStyles,
			zIndex: "popover",
			background: "bg.panel",
			borderWidth: "1px",
			borderRadius: "l3",
			boxShadow: "lg",
			...POPOVER_BOX_PROPS[positioning],
			overflowY: "auto",
			outline: "none",
			[POPOVER_ATTRIBUTE]: "",
			...getFloatingProps(),
			children: render(state.data, { close: store.close })
		})
	}) });
}
// src/components/inputs/date-picker.tsx
const VIEW_TABLE = {
	day: DatePicker.DayTable,
	month: DatePicker.MonthTable,
	year: DatePicker.YearTable
};
const DEFAULT_VIEWS$1 = [
	"day",
	"month",
	"year"
];
function DatePickerRoot(props) {
	return /* @__PURE__ */ jsx(DatePicker.Root, {
		openOnClick: true,
		fixedWeeks: true,
		positioning: { placement: "bottom-start" },
		...props
	});
}
function DatePickerContent({ views = DEFAULT_VIEWS$1, ...props }) {
	return /* @__PURE__ */ jsx(DatePicker.Content, {
		...props,
		children: views.map((view) => {
			const Table = VIEW_TABLE[view];
			return /* @__PURE__ */ jsxs(DatePicker.View, {
				view,
				children: [/* @__PURE__ */ jsx(DatePicker.Header, {}), /* @__PURE__ */ jsx(Table, {})]
			}, view);
		})
	});
}
const DatePicker$1 = {
	...DatePicker,
	Root: DatePickerRoot,
	Content: DatePickerContent
};
// src/components/inputs/field.tsx
const Label = forwardRef(function Label(props, ref) {
	return /* @__PURE__ */ jsx(Field.Label, {
		ref,
		...props,
		color: "brand.solid"
	});
});
const Root$1 = forwardRef(function Root(props, ref) {
	return /* @__PURE__ */ jsx(Field.Root, {
		ref,
		...props,
		colorPalette: "brand"
	});
});
const RequiredIndicator = forwardRef(function RequiredIndicator(props, ref) {
	return /* @__PURE__ */ jsx(Field.RequiredIndicator, {
		ref,
		...props,
		color: "brand.solid"
	});
});
const Field$1 = {
	Root: Root$1,
	Label,
	ErrorText: Field.ErrorText,
	RequiredIndicator,
	Context: Field.Context,
	HelperText: Field.HelperText
};
// src/components/inputs/password-input.tsx
const PasswordInput = forwardRef(function PasswordInput(props, ref) {
	const { rootProps, defaultVisible, visible: visibleProp, onVisibleChange, visibilityIcon = {
		on: /* @__PURE__ */ jsx(EyeIcon, {}),
		off: /* @__PURE__ */ jsx(EyeOffIcon, {})
	}, ...rest } = props;
	const [visible, setVisible] = useControllableState({
		value: visibleProp,
		defaultValue: defaultVisible || false,
		onChange: onVisibleChange
	});
	const inputRef = useRef(null);
	return /* @__PURE__ */ jsx(InputGroup, {
		endElement: /* @__PURE__ */ jsx(VisibilityTrigger, {
			disabled: rest.disabled,
			onPointerDown: (event) => {
				if (rest.disabled) return;
				if (event.button !== 0) return;
				event.preventDefault();
				setVisible(!visible);
			},
			children: visible ? visibilityIcon.off : visibilityIcon.on
		}),
		...rootProps,
		children: /* @__PURE__ */ jsx(Input, {
			...rest,
			ref: mergeRefs(ref, inputRef),
			type: visible ? "text" : "password"
		})
	});
});
const VisibilityTrigger = forwardRef(function VisibilityTrigger(props, ref) {
	return /* @__PURE__ */ jsx(IconButton$1, {
		tabIndex: -1,
		ref,
		me: "-2",
		aspectRatio: "square",
		size: "sm",
		variant: "ghost",
		height: "calc(100% - {spacing.2})",
		"aria-label": "Toggle password visibility",
		...props
	});
});
const PasswordStrengthMeter = forwardRef(function PasswordStrengthMeter(props, ref) {
	const { max = 4, value, labels } = props;
	const { label, colorPalette } = getColorPalette(value / max * 100, labels);
	return /* @__PURE__ */ jsxs(Flex$1, {
		flexDirection: "column",
		align: "flex-end",
		gap: "1",
		ref,
		children: [/* @__PURE__ */ jsx(Flex$1, {
			flexDirection: "row",
			gap: 2,
			width: "full",
			children: Array.from({ length: max }).map((_, index) => /* @__PURE__ */ jsx(Box$1, {
				height: "1",
				flex: "1",
				rounded: "sm",
				"data-selected": index < value ? "" : void 0,
				layerStyle: "fill.subtle",
				colorPalette: "gray",
				_selected: {
					colorPalette,
					layerStyle: "fill.solid"
				}
			}, index))
		}), label && /* @__PURE__ */ jsx(Box$1, {
			textStyle: "xs",
			children: label
		})]
	});
});
function getColorPalette(percent, customLabels) {
	const texts = customLabels ?? {
		low: "Low",
		medium: "Medium",
		high: "High"
	};
	if (percent < 33) return {
		label: texts.low,
		colorPalette: "red"
	};
	if (percent < 66) return {
		label: texts.medium,
		colorPalette: "orange"
	};
	return {
		label: texts.high,
		colorPalette: "green"
	};
}
// src/components/inputs/radio-card.tsx
const RadioCardItem = forwardRef(function RadioCardItem(props, ref) {
	const { inputProps, label, description, addon, icon, indicator = /* @__PURE__ */ jsx(RadioCard.ItemIndicator, {}), indicatorPlacement = "end", ...rest } = props;
	const hasContent = label || description || icon;
	const ContentWrapper = indicator ? RadioCard.ItemContent : Fragment;
	return /* @__PURE__ */ jsxs(RadioCard.Item, {
		...rest,
		children: [
			/* @__PURE__ */ jsx(RadioCard.ItemHiddenInput, {
				ref,
				...inputProps
			}),
			/* @__PURE__ */ jsxs(RadioCard.ItemControl, { children: [
				indicatorPlacement === "start" && indicator,
				hasContent && /* @__PURE__ */ jsxs(ContentWrapper, { children: [
					icon,
					label && /* @__PURE__ */ jsx(RadioCard.ItemText, { children: label }),
					description && /* @__PURE__ */ jsx(RadioCard.ItemDescription, { children: description }),
					indicatorPlacement === "inside" && indicator
				] }),
				indicatorPlacement === "end" && indicator
			] }),
			addon && /* @__PURE__ */ jsx(RadioCard.ItemAddon, { children: addon })
		]
	});
});
const RadioCard$1 = {
	Root: RadioCard.Root,
	Label: RadioCard.Label,
	Item: RadioCard.Item,
	ClosedItem: RadioCardItem,
	ItemIndicator: RadioCard.ItemIndicator,
	ItemHiddenInput: RadioCard.ItemHiddenInput,
	ItemControl: RadioCard.ItemControl,
	ItemContent: RadioCard.ItemContent,
	ItemText: RadioCard.ItemText,
	ItemDescription: RadioCard.ItemDescription,
	ItemAddon: RadioCard.ItemAddon
};
// src/components/inputs/radio-card-collapsible/radio-card-collapsible-context.tsx
const RadioCardCollapsibleContext = createContext(null);
const useRadioCardCollapsible = () => {
	const ctx = useContext(RadioCardCollapsibleContext);
	if (!ctx) throw new Error("Must be used within RadioCardCollapsible");
	return ctx;
};
// src/components/inputs/radio-card-collapsible/radio-card-collapsible-item.tsx
const CHECKED_BOX_SHADOW = "0 -1px 0 0 var(--chakra-colors-brand-solid), 1px 0 0 0 var(--chakra-colors-brand-solid), -1px 0 0 0 var(--chakra-colors-brand-solid)";
const RadioCardCollapsibleItem = ({ value, title, icon, description, children }) => {
	const { selectedValue, size } = useRadioCardCollapsible();
	const isSelected = value === selectedValue;
	return /* @__PURE__ */ jsxs(Box$1, {
		width: "full",
		children: [/* @__PURE__ */ jsxs(RadioCard$1.Item, {
			value,
			width: "full",
			background: isSelected ? "gray.50" : "transparent",
			borderBottomRadius: isSelected ? "0" : "md",
			borderBottom: isSelected ? "none" : void 0,
			zIndex: isSelected ? 2 : 1,
			_checked: { boxShadow: CHECKED_BOX_SHADOW },
			children: [/* @__PURE__ */ jsx(RadioCard$1.ItemHiddenInput, {}), /* @__PURE__ */ jsxs(RadioCard$1.ItemControl, {
				borderBottom: 0,
				_checked: { boxShadow: CHECKED_BOX_SHADOW },
				children: [/* @__PURE__ */ jsxs(RadioCard$1.ItemContent, { children: [/* @__PURE__ */ jsxs(Flex$1, {
					alignItems: "center",
					gap: 2,
					children: [/* @__PURE__ */ jsx(Icon, {
						size: size === "sm" ? "sm" : "md",
						color: "fg.muted",
						children: icon
					}), /* @__PURE__ */ jsx(RadioCard$1.ItemText, { children: title })]
				}), description && /* @__PURE__ */ jsx(RadioCard$1.ItemDescription, {
					marginLeft: "7",
					children: description
				})] }), /* @__PURE__ */ jsx(RadioCard$1.ItemIndicator, {})]
			})]
		}), /* @__PURE__ */ jsx(Collapsible.Root, {
			open: isSelected,
			opacity: isSelected ? 1 : 0,
			border: "1px solid",
			borderColor: "brand.solid",
			boxShadow: "0 1px 0 0 var(--chakra-colors-brand-solid), 1px 0 0 0 var(--chakra-colors-brand-solid), -1px 0 0 0 var(--chakra-colors-brand-solid)",
			borderTopColor: "gray.200",
			borderBottomLeftRadius: "sm",
			borderBottomRightRadius: "sm",
			children: /* @__PURE__ */ jsx(Collapsible.Content, { children: /* @__PURE__ */ jsx(Box$1, {
				borderBottomRadius: "md",
				bg: "white",
				onClick: (e) => e.stopPropagation(),
				children
			}) })
		})]
	});
};
// src/components/inputs/radio-card-collapsible/radio-card-collapsible-root.tsx
const RadioCardCollapsibleRoot = ({ children, defaultValue, label, size, onChange }) => {
	const [selectedValue, setSelectedValue] = useState(defaultValue);
	const onValueChange = ({ value }) => {
		if (!value) return;
		setSelectedValue(value);
		onChange?.(value);
	};
	return /* @__PURE__ */ jsx(RadioCardCollapsibleContext.Provider, {
		value: {
			selectedValue,
			setSelectedValue,
			size
		},
		children: /* @__PURE__ */ jsxs(RadioCard$1.Root, {
			value: selectedValue,
			onValueChange,
			size,
			width: "full",
			gap: "1.5",
			children: [label && /* @__PURE__ */ jsx(RadioCard$1.Label, { children: label }), /* @__PURE__ */ jsx(Group, {
				orientation: "vertical",
				children
			})]
		})
	});
};
// src/components/inputs/radio-card-collapsible/index.ts
const RadioCardCollapsible = {
	Root: RadioCardCollapsibleRoot,
	Item: RadioCardCollapsibleItem
};
// src/components/inputs/radio-group.tsx
const Radio = forwardRef(function Radio(props, ref) {
	const { children, inputProps, rootRef, ...rest } = props;
	return /* @__PURE__ */ jsxs(RadioGroup.Item, {
		ref: rootRef,
		...rest,
		children: [
			/* @__PURE__ */ jsx(RadioGroup.ItemHiddenInput, {
				ref,
				...inputProps
			}),
			/* @__PURE__ */ jsx(RadioGroup.ItemIndicator, {}),
			children && /* @__PURE__ */ jsx(RadioGroup.ItemText, { children })
		]
	});
});
const RadioGroup$1 = {
	Root: RadioGroup.Root,
	Radio
};
// src/components/inputs/tags-input.tsx
function Items(props) {
	return /* @__PURE__ */ jsx(TagsInput.Context, { children: ({ value }) => value.map((tag, index) => /* @__PURE__ */ jsxs(TagsInput.Item, {
		index,
		value: tag,
		colorPalette: "gray",
		...props,
		children: [/* @__PURE__ */ jsxs(TagsInput.ItemPreview, {
			borderRadius: "md",
			children: [/* @__PURE__ */ jsx(TagsInput.ItemText, { children: tag }), /* @__PURE__ */ jsx(TagsInput.ItemDeleteTrigger, {})]
		}), /* @__PURE__ */ jsx(TagsInput.ItemInput, {})]
	}, index)) });
}
const TagsInput$1 = {
	...TagsInput,
	Items
};
// ../node_modules/@zag-js/date-input/dist/chunk-QZ7TP4HQ.mjs
var __defProp$2 = Object.defineProperty;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$2(obj, key, {
	enumerable: true,
	configurable: true,
	writable: true,
	value
}) : obj[key] = value;
var __publicField$1 = (obj, key, value) => __defNormalProp$1(obj, typeof key !== "symbol" ? key + "" : key, value);
// ../node_modules/@zag-js/anatomy/dist/create-anatomy.mjs
var createAnatomy = (name, parts = []) => ({
	parts: (...values) => {
		if (isEmpty(parts)) return createAnatomy(name, values);
		throw new Error("createAnatomy().parts(...) should only be called once. Did you mean to use .extendWith(...) ?");
	},
	extendWith: (...values) => createAnatomy(name, [...parts, ...values]),
	omit: (...values) => createAnatomy(name, parts.filter((part) => !values.includes(part))),
	rename: (newName) => createAnatomy(newName, parts),
	keys: () => parts,
	build: () => [...new Set(parts)].reduce((prev, part) => Object.assign(prev, { [part]: {
		selector: [`&[data-scope="${toKebabCase(name)}"][data-part="${toKebabCase(part)}"]`, `& [data-scope="${toKebabCase(name)}"][data-part="${toKebabCase(part)}"]`].join(", "),
		attrs: {
			"data-scope": toKebabCase(name),
			"data-part": toKebabCase(part)
		}
	} }), {})
});
var toKebabCase = (value) => value.replace(/([A-Z])([A-Z])/g, "$1-$2").replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();
var isEmpty = (v) => v.length === 0;
var parts = createAnatomy("date-input").parts("root", "label", "control", "segmentGroup", "segment", "hiddenInput").build();
// ../node_modules/@zag-js/dom-query/dist/chunk-QZ7TP4HQ.mjs
var __defProp$1 = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp$1(obj, key, {
	enumerable: true,
	configurable: true,
	writable: true,
	value
}) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
// ../node_modules/@zag-js/dom-query/dist/shared.mjs
var isObject = (v) => typeof v === "object" && v !== null;
var dataAttr = (guard) => guard ? "" : void 0;
var ariaAttr = (guard) => guard ? "true" : void 0;
// ../node_modules/@zag-js/dom-query/dist/node.mjs
var ELEMENT_NODE = 1;
var DOCUMENT_NODE = 9;
var DOCUMENT_FRAGMENT_NODE = 11;
var isHTMLElement = (el) => isObject(el) && el.nodeType === ELEMENT_NODE && typeof el.nodeName === "string";
var isDocument = (el) => isObject(el) && el.nodeType === DOCUMENT_NODE;
var isWindow = (el) => isObject(el) && el === el.window;
var isNode = (el) => isObject(el) && el.nodeType !== void 0;
var isShadowRoot = (el) => isNode(el) && el.nodeType === DOCUMENT_FRAGMENT_NODE && "host" in el;
function isActiveElement(element) {
	if (!element) return false;
	return getActiveElement(element.getRootNode()) === element;
}
function contains(parent, child) {
	if (!parent || !child) return false;
	if (!isHTMLElement(parent) || !isNode(child)) return false;
	if (isHTMLElement(child) && parent === child) return true;
	if (parent.contains(child)) return true;
	const rootNode = child.getRootNode?.();
	if (rootNode && isShadowRoot(rootNode)) {
		let next = child;
		while (next) {
			if (parent === next) return true;
			next = next.parentNode || next.host;
		}
	}
	return false;
}
function getDocument(el) {
	if (isDocument(el)) return el;
	if (isWindow(el)) return el.document;
	return el?.ownerDocument ?? document;
}
function getActiveElement(rootNode) {
	let activeElement = rootNode.activeElement;
	while (activeElement?.shadowRoot) {
		const el = activeElement.shadowRoot.activeElement;
		if (!el || el === activeElement) break;
		else activeElement = el;
	}
	return activeElement;
}
// ../node_modules/@zag-js/dom-query/dist/event.mjs
function isComposingEvent(event) {
	return getNativeEvent(event).isComposing || event.keyCode === 229;
}
var keyMap = {
	Up: "ArrowUp",
	Down: "ArrowDown",
	Esc: "Escape",
	" ": "Space",
	",": "Comma",
	Left: "ArrowLeft",
	Right: "ArrowRight"
};
var rtlKeyMap = {
	ArrowLeft: "ArrowRight",
	ArrowRight: "ArrowLeft"
};
function getEventKey(event, options = {}) {
	const { dir = "ltr", orientation = "horizontal" } = options;
	let key = event.key;
	key = keyMap[key] ?? key;
	if (dir === "rtl" && orientation === "horizontal" && key in rtlKeyMap) key = rtlKeyMap[key];
	return key;
}
function getNativeEvent(event) {
	return event.nativeEvent ?? event;
}
// ../node_modules/@zag-js/dom-query/dist/raf.mjs
var AnimationFrame = class _AnimationFrame {
	constructor() {
		__publicField(this, "id", null);
		__publicField(this, "fn_cleanup");
		__publicField(this, "cleanup", () => {
			this.cancel();
		});
	}
	static create() {
		return new _AnimationFrame();
	}
	request(fn) {
		this.cancel();
		this.id = globalThis.requestAnimationFrame(() => {
			this.id = null;
			this.fn_cleanup = fn?.();
		});
	}
	cancel() {
		if (this.id !== null) {
			globalThis.cancelAnimationFrame(this.id);
			this.id = null;
		}
		this.fn_cleanup?.();
		this.fn_cleanup = void 0;
	}
	isActive() {
		return this.id !== null;
	}
};
function raf(fn) {
	const frame = AnimationFrame.create();
	frame.request(fn);
	return frame.cleanup;
}
// ../node_modules/@zag-js/dom-query/dist/query.mjs
function queryAll(root, selector) {
	return Array.from(root?.querySelectorAll(selector) ?? []);
}
// ../node_modules/@zag-js/dom-query/dist/visually-hidden.mjs
var visuallyHiddenStyle = {
	border: "0",
	clip: "rect(0 0 0 0)",
	height: "1px",
	margin: "-1px",
	overflow: "hidden",
	padding: "0",
	position: "absolute",
	width: "1px",
	whiteSpace: "nowrap",
	wordWrap: "normal"
};
// ../node_modules/@zag-js/date-input/dist/date-input.dom.mjs
var getRootId = (ctx) => ctx.ids?.root ?? `date-input:${ctx.id}`;
var getLabelId = (ctx, index) => ctx.ids?.label?.(index) ?? `date-input:${ctx.id}:label:${index}`;
var getControlId = (ctx) => ctx.ids?.control ?? `date-input:${ctx.id}:control`;
var getSegmentGroupId = (ctx, index) => ctx.ids?.segmentGroup?.(index) ?? `date-input:${ctx.id}:segment-group:${index}`;
var getHiddenInputId = (ctx, index) => ctx.ids?.hiddenInput?.(index) ?? `date-input:${ctx.id}:hidden-input:${index}`;
var getControlEl = (ctx) => ctx.getById(getControlId(ctx));
var getSegmentEls = (ctx) => queryAll(getControlEl(ctx), `[data-part=segment]`);
// ../node_modules/@internationalized/date/dist/private/utils.mjs
function $09ec6a572d60460f$export$842a2cf37af977e1(amount, numerator) {
	return amount - numerator * Math.floor(amount / numerator);
}
// ../node_modules/@internationalized/date/dist/private/calendars/GregorianCalendar.mjs
const $93635573935797de$var$EPOCH = 1721426;
function $93635573935797de$export$f297eb839006d339(era, year, month, day) {
	year = $93635573935797de$export$c36e0ecb2d4fa69d(era, year);
	let y1 = year - 1;
	let monthOffset = -2;
	if (month <= 2) monthOffset = 0;
	else if ($93635573935797de$export$553d7fa8e3805fc0(year)) monthOffset = -1;
	return 1721425 + 365 * y1 + Math.floor(y1 / 4) - Math.floor(y1 / 100) + Math.floor(y1 / 400) + Math.floor((367 * month - 362) / 12 + monthOffset + day);
}
function $93635573935797de$export$553d7fa8e3805fc0(year) {
	return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}
function $93635573935797de$export$c36e0ecb2d4fa69d(era, year) {
	return era === "BC" ? 1 - year : year;
}
function $93635573935797de$export$4475b7e617eb123c(year) {
	let era = "AD";
	if (year <= 0) {
		era = "BC";
		year = 1 - year;
	}
	return [era, year];
}
const $93635573935797de$var$daysInMonth = {
	standard: [
		31,
		28,
		31,
		30,
		31,
		30,
		31,
		31,
		30,
		31,
		30,
		31
	],
	leapyear: [
		31,
		29,
		31,
		30,
		31,
		30,
		31,
		31,
		30,
		31,
		30,
		31
	]
};
var $93635573935797de$export$80ee6245ec4f29ec = class {
	fromJulianDay(jd) {
		let jd0 = jd;
		let depoch = jd0 - $93635573935797de$var$EPOCH;
		let quadricent = Math.floor(depoch / 146097);
		let dqc = $09ec6a572d60460f$export$842a2cf37af977e1(depoch, 146097);
		let cent = Math.floor(dqc / 36524);
		let dcent = $09ec6a572d60460f$export$842a2cf37af977e1(dqc, 36524);
		let quad = Math.floor(dcent / 1461);
		let dquad = $09ec6a572d60460f$export$842a2cf37af977e1(dcent, 1461);
		let yindex = Math.floor(dquad / 365);
		let [era, year] = $93635573935797de$export$4475b7e617eb123c(quadricent * 400 + cent * 100 + quad * 4 + yindex + (cent !== 4 && yindex !== 4 ? 1 : 0));
		let yearDay = jd0 - $93635573935797de$export$f297eb839006d339(era, year, 1, 1);
		let leapAdj = 2;
		if (jd0 < $93635573935797de$export$f297eb839006d339(era, year, 3, 1)) leapAdj = 0;
		else if ($93635573935797de$export$553d7fa8e3805fc0(year)) leapAdj = 1;
		let month = Math.floor(((yearDay + leapAdj) * 12 + 373) / 367);
		return new $2aaf608024c21ca1$export$99faa760c7908e4f(era, year, month, jd0 - $93635573935797de$export$f297eb839006d339(era, year, month, 1) + 1);
	}
	toJulianDay(date) {
		return $93635573935797de$export$f297eb839006d339(date.era, date.year, date.month, date.day);
	}
	getDaysInMonth(date) {
		return $93635573935797de$var$daysInMonth[$93635573935797de$export$553d7fa8e3805fc0(date.year) ? "leapyear" : "standard"][date.month - 1];
	}
	getMonthsInYear(date) {
		return 12;
	}
	getDaysInYear(date) {
		return $93635573935797de$export$553d7fa8e3805fc0(date.year) ? 366 : 365;
	}
	getMaximumMonthsInYear() {
		return 12;
	}
	getMaximumDaysInMonth() {
		return 31;
	}
	getYearsInEra(date) {
		return 9999;
	}
	getEras() {
		return ["BC", "AD"];
	}
	isInverseEra(date) {
		return date.era === "BC";
	}
	balanceDate(date) {
		if (date.year <= 0) {
			date.era = date.era === "BC" ? "AD" : "BC";
			date.year = 1 - date.year;
		}
	}
	constructor() {
		this.identifier = "gregory";
	}
};
// ../node_modules/@internationalized/date/dist/private/queries.mjs
function $ad063034c8620db8$export$ea39ec197993aef0(a, b) {
	b = $d07e34cce18680fd$export$b4a036af3fc0b032(b, a.calendar);
	return a.era === b.era && a.year === b.year && a.month === b.month && a.day === b.day;
}
function $ad063034c8620db8$export$dbc69fd56b53d5e(a, b) {
	return a.isEqual?.(b) ?? b.isEqual?.(a) ?? a.identifier === b.identifier;
}
function $ad063034c8620db8$export$461939dd4422153(timeZone) {
	return $d07e34cce18680fd$export$1b96692a1ba042ac(Date.now(), timeZone);
}
function $ad063034c8620db8$export$d0bdf45af03a6ea3(timeZone) {
	return $d07e34cce18680fd$export$93522d1a439f3617($ad063034c8620db8$export$461939dd4422153(timeZone));
}
function $ad063034c8620db8$export$68781ddf31c0090f(a, b) {
	return a.calendar.toJulianDay(a) - b.calendar.toJulianDay(b);
}
function $ad063034c8620db8$export$c19a80a9721b80f6(a, b) {
	return $ad063034c8620db8$var$timeToMs(a) - $ad063034c8620db8$var$timeToMs(b);
}
function $ad063034c8620db8$var$timeToMs(a) {
	return a.hour * 36e5 + a.minute * 6e4 + a.second * 1e3 + a.millisecond;
}
let $ad063034c8620db8$var$localTimeZone = null;
let $ad063034c8620db8$var$localTimeZoneOverride = false;
function $ad063034c8620db8$export$aa8b41735afcabd2() {
	if ($ad063034c8620db8$var$localTimeZone == null) $ad063034c8620db8$var$localTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
	return $ad063034c8620db8$var$localTimeZone;
}
function $ad063034c8620db8$export$6ab69b273755230b() {
	return $ad063034c8620db8$var$localTimeZoneOverride;
}
// ../node_modules/@internationalized/date/dist/private/conversion.mjs
function $d07e34cce18680fd$export$bd4fb2bc8bb06fb(date) {
	date = $d07e34cce18680fd$export$b4a036af3fc0b032(date, new $93635573935797de$export$80ee6245ec4f29ec());
	return $d07e34cce18680fd$var$epochFromParts($93635573935797de$export$c36e0ecb2d4fa69d(date.era, date.year), date.month, date.day, date.hour, date.minute, date.second, date.millisecond);
}
function $d07e34cce18680fd$var$epochFromParts(year, month, day, hour, minute, second, millisecond) {
	let date = /* @__PURE__ */ new Date();
	date.setUTCHours(hour, minute, second, millisecond);
	date.setUTCFullYear(year, month - 1, day);
	return date.getTime();
}
function $d07e34cce18680fd$export$59c99f3515d3493f(ms, timeZone) {
	if (timeZone === "UTC") return 0;
	if (ms > 0 && timeZone === $ad063034c8620db8$export$aa8b41735afcabd2() && !$ad063034c8620db8$export$6ab69b273755230b()) return new Date(ms).getTimezoneOffset() * -6e4;
	let { year, month, day, hour, minute, second } = $d07e34cce18680fd$var$getTimeZoneParts(ms, timeZone);
	return $d07e34cce18680fd$var$epochFromParts(year, month, day, hour, minute, second, 0) - Math.floor(ms / 1e3) * 1e3;
}
const $d07e34cce18680fd$var$formattersByTimeZone = /* @__PURE__ */ new Map();
function $d07e34cce18680fd$var$getTimeZoneParts(ms, timeZone) {
	let formatter = $d07e34cce18680fd$var$formattersByTimeZone.get(timeZone);
	if (!formatter) {
		formatter = new Intl.DateTimeFormat("en-US", {
			timeZone,
			hour12: false,
			era: "short",
			year: "numeric",
			month: "numeric",
			day: "numeric",
			hour: "numeric",
			minute: "numeric",
			second: "numeric"
		});
		$d07e34cce18680fd$var$formattersByTimeZone.set(timeZone, formatter);
	}
	let parts = formatter.formatToParts(new Date(ms));
	let namedParts = {};
	for (let part of parts) if (part.type !== "literal") namedParts[part.type] = part.value;
	return {
		year: namedParts.era === "BC" || namedParts.era === "B" ? -namedParts.year + 1 : +namedParts.year,
		month: +namedParts.month,
		day: +namedParts.day,
		hour: namedParts.hour === "24" ? 0 : +namedParts.hour,
		minute: +namedParts.minute,
		second: +namedParts.second
	};
}
const $d07e34cce18680fd$var$DAYMILLIS = 864e5;
function $d07e34cce18680fd$var$getValidWallTimes(date, timeZone, earlier, later) {
	return (earlier === later ? [earlier] : [earlier, later]).filter((absolute) => $d07e34cce18680fd$var$isValidWallTime(date, timeZone, absolute));
}
function $d07e34cce18680fd$var$isValidWallTime(date, timeZone, absolute) {
	let parts = $d07e34cce18680fd$var$getTimeZoneParts(absolute, timeZone);
	return date.year === parts.year && date.month === parts.month && date.day === parts.day && date.hour === parts.hour && date.minute === parts.minute && date.second === parts.second;
}
function $d07e34cce18680fd$export$5107c82f94518f5c(date, timeZone, disambiguation = "compatible") {
	let dateTime = $d07e34cce18680fd$export$b21e0b124e224484(date);
	if (timeZone === "UTC") return $d07e34cce18680fd$export$bd4fb2bc8bb06fb(dateTime);
	if (timeZone === $ad063034c8620db8$export$aa8b41735afcabd2() && disambiguation === "compatible" && !$ad063034c8620db8$export$6ab69b273755230b()) {
		dateTime = $d07e34cce18680fd$export$b4a036af3fc0b032(dateTime, new $93635573935797de$export$80ee6245ec4f29ec());
		let date = /* @__PURE__ */ new Date();
		let year = $93635573935797de$export$c36e0ecb2d4fa69d(dateTime.era, dateTime.year);
		date.setFullYear(year, dateTime.month - 1, dateTime.day);
		date.setHours(dateTime.hour, dateTime.minute, dateTime.second, dateTime.millisecond);
		return date.getTime();
	}
	let ms = $d07e34cce18680fd$export$bd4fb2bc8bb06fb(dateTime);
	let offsetBefore = $d07e34cce18680fd$export$59c99f3515d3493f(ms - $d07e34cce18680fd$var$DAYMILLIS, timeZone);
	let offsetAfter = $d07e34cce18680fd$export$59c99f3515d3493f(ms + $d07e34cce18680fd$var$DAYMILLIS, timeZone);
	let valid = $d07e34cce18680fd$var$getValidWallTimes(dateTime, timeZone, ms - offsetBefore, ms - offsetAfter);
	if (valid.length === 1) return valid[0];
	if (valid.length > 1) switch (disambiguation) {
		case "compatible":
		case "earlier": return valid[0];
		case "later": return valid[valid.length - 1];
		case "reject": throw new RangeError("Multiple possible absolute times found");
	}
	switch (disambiguation) {
		case "earlier": return Math.min(ms - offsetBefore, ms - offsetAfter);
		case "compatible":
		case "later": return Math.max(ms - offsetBefore, ms - offsetAfter);
		case "reject": throw new RangeError("No such absolute time found");
	}
}
function $d07e34cce18680fd$export$e67a095c620b86fe(dateTime, timeZone, disambiguation = "compatible") {
	return new Date($d07e34cce18680fd$export$5107c82f94518f5c(dateTime, timeZone, disambiguation));
}
function $d07e34cce18680fd$export$1b96692a1ba042ac(ms, timeZone) {
	let offset = $d07e34cce18680fd$export$59c99f3515d3493f(ms, timeZone);
	let date = new Date(ms + offset);
	let year = date.getUTCFullYear();
	let month = date.getUTCMonth() + 1;
	let day = date.getUTCDate();
	let hour = date.getUTCHours();
	let minute = date.getUTCMinutes();
	let second = date.getUTCSeconds();
	let millisecond = date.getUTCMilliseconds();
	return new $2aaf608024c21ca1$export$d3b7288e7994edea(year < 1 ? "BC" : "AD", year < 1 ? -year + 1 : year, month, day, timeZone, offset, hour, minute, second, millisecond);
}
function $d07e34cce18680fd$export$93522d1a439f3617(dateTime) {
	return new $2aaf608024c21ca1$export$99faa760c7908e4f(dateTime.calendar, dateTime.era, dateTime.year, dateTime.month, dateTime.day);
}
function $d07e34cce18680fd$export$b21e0b124e224484(date, time) {
	let hour = 0, minute = 0, second = 0, millisecond = 0;
	if ("timeZone" in date) ({hour: hour, minute: minute, second: second, millisecond: millisecond} = date);
	else if ("hour" in date && !time) return date;
	if (time) ({hour: hour, minute: minute, second: second, millisecond: millisecond} = time);
	return new $2aaf608024c21ca1$export$ca871e8dbb80966f(date.calendar, date.era, date.year, date.month, date.day, hour, minute, second, millisecond);
}
function $d07e34cce18680fd$export$b4a036af3fc0b032(date, calendar) {
	if ($ad063034c8620db8$export$dbc69fd56b53d5e(date.calendar, calendar)) return date;
	let calendarDate = calendar.fromJulianDay(date.calendar.toJulianDay(date));
	let copy = date.copy();
	copy.calendar = calendar;
	copy.era = calendarDate.era;
	copy.year = calendarDate.year;
	copy.month = calendarDate.month;
	copy.day = calendarDate.day;
	$435a2ceaa8778ed8$export$c4e2ecac49351ef2(copy);
	return copy;
}
function $d07e34cce18680fd$export$84c95a83c799e074(date, timeZone, disambiguation) {
	if (date instanceof $2aaf608024c21ca1$export$d3b7288e7994edea) {
		if (date.timeZone === timeZone) return date;
		return $d07e34cce18680fd$export$538b00033cc11c75(date, timeZone);
	}
	return $d07e34cce18680fd$export$1b96692a1ba042ac($d07e34cce18680fd$export$5107c82f94518f5c(date, timeZone, disambiguation), timeZone);
}
function $d07e34cce18680fd$export$83aac07b4c37b25(date) {
	let ms = $d07e34cce18680fd$export$bd4fb2bc8bb06fb(date) - date.offset;
	return new Date(ms);
}
function $d07e34cce18680fd$export$538b00033cc11c75(date, timeZone) {
	return $d07e34cce18680fd$export$b4a036af3fc0b032($d07e34cce18680fd$export$1b96692a1ba042ac($d07e34cce18680fd$export$bd4fb2bc8bb06fb(date) - date.offset, timeZone), date.calendar);
}
// ../node_modules/@internationalized/date/dist/private/manipulation.mjs
const $435a2ceaa8778ed8$var$ONE_HOUR = 36e5;
function $435a2ceaa8778ed8$export$e16d8520af44a096(date, duration) {
	let mutableDate = date.copy();
	let days = "hour" in mutableDate ? $435a2ceaa8778ed8$var$addTimeFields(mutableDate, duration) : 0;
	$435a2ceaa8778ed8$var$addYears(mutableDate, duration.years || 0);
	if (mutableDate.calendar.balanceYearMonth) mutableDate.calendar.balanceYearMonth(mutableDate, date);
	mutableDate.month += duration.months || 0;
	$435a2ceaa8778ed8$var$balanceYearMonth(mutableDate);
	$435a2ceaa8778ed8$var$constrainMonthDay(mutableDate);
	mutableDate.day += (duration.weeks || 0) * 7;
	mutableDate.day += duration.days || 0;
	mutableDate.day += days;
	$435a2ceaa8778ed8$var$balanceDay(mutableDate);
	if (mutableDate.calendar.balanceDate) mutableDate.calendar.balanceDate(mutableDate);
	if (mutableDate.year < 1) {
		mutableDate.year = 1;
		mutableDate.month = 1;
		mutableDate.day = 1;
	}
	let maxYear = mutableDate.calendar.getYearsInEra(mutableDate);
	if (mutableDate.year > maxYear) {
		let isInverseEra = mutableDate.calendar.isInverseEra?.(mutableDate);
		mutableDate.year = maxYear;
		mutableDate.month = isInverseEra ? 1 : mutableDate.calendar.getMonthsInYear(mutableDate);
		mutableDate.day = isInverseEra ? 1 : mutableDate.calendar.getDaysInMonth(mutableDate);
	}
	if (mutableDate.month < 1) {
		mutableDate.month = 1;
		mutableDate.day = 1;
	}
	let maxMonth = mutableDate.calendar.getMonthsInYear(mutableDate);
	if (mutableDate.month > maxMonth) {
		mutableDate.month = maxMonth;
		mutableDate.day = mutableDate.calendar.getDaysInMonth(mutableDate);
	}
	mutableDate.day = Math.max(1, Math.min(mutableDate.calendar.getDaysInMonth(mutableDate), mutableDate.day));
	return mutableDate;
}
function $435a2ceaa8778ed8$var$addYears(date, years) {
	if (date.calendar.isInverseEra?.(date)) years = -years;
	date.year += years;
}
function $435a2ceaa8778ed8$var$balanceYearMonth(date) {
	while (date.month < 1) {
		$435a2ceaa8778ed8$var$addYears(date, -1);
		date.month += date.calendar.getMonthsInYear(date);
	}
	let monthsInYear = 0;
	while (date.month > (monthsInYear = date.calendar.getMonthsInYear(date))) {
		date.month -= monthsInYear;
		$435a2ceaa8778ed8$var$addYears(date, 1);
	}
}
function $435a2ceaa8778ed8$var$balanceDay(date) {
	while (date.day < 1) {
		date.month--;
		$435a2ceaa8778ed8$var$balanceYearMonth(date);
		date.day += date.calendar.getDaysInMonth(date);
	}
	while (date.day > date.calendar.getDaysInMonth(date)) {
		date.day -= date.calendar.getDaysInMonth(date);
		date.month++;
		$435a2ceaa8778ed8$var$balanceYearMonth(date);
	}
}
function $435a2ceaa8778ed8$var$constrainMonthDay(date) {
	date.month = Math.max(1, Math.min(date.calendar.getMonthsInYear(date), date.month));
	date.day = Math.max(1, Math.min(date.calendar.getDaysInMonth(date), date.day));
}
function $435a2ceaa8778ed8$export$c4e2ecac49351ef2(date) {
	if (date.calendar.constrainDate) date.calendar.constrainDate(date);
	date.year = Math.max(1, Math.min(date.calendar.getYearsInEra(date), date.year));
	$435a2ceaa8778ed8$var$constrainMonthDay(date);
}
function $435a2ceaa8778ed8$export$3e2544e88a25bff8(duration) {
	let inverseDuration = {};
	for (let key in duration) if (typeof duration[key] === "number") inverseDuration[key] = -duration[key];
	return inverseDuration;
}
function $435a2ceaa8778ed8$export$4e2d2ead65e5f7e3(date, duration) {
	return $435a2ceaa8778ed8$export$e16d8520af44a096(date, $435a2ceaa8778ed8$export$3e2544e88a25bff8(duration));
}
function $435a2ceaa8778ed8$export$adaa4cf7ef1b65be(date, fields) {
	let mutableDate = date.copy();
	if (fields.era != null) mutableDate.era = fields.era;
	if (fields.year != null) mutableDate.year = fields.year;
	if (fields.month != null) mutableDate.month = fields.month;
	if (fields.day != null) mutableDate.day = fields.day;
	$435a2ceaa8778ed8$export$c4e2ecac49351ef2(mutableDate);
	return mutableDate;
}
function $435a2ceaa8778ed8$export$e5d5e1c1822b6e56(value, fields) {
	let mutableValue = value.copy();
	if (fields.hour != null) mutableValue.hour = fields.hour;
	if (fields.minute != null) mutableValue.minute = fields.minute;
	if (fields.second != null) mutableValue.second = fields.second;
	if (fields.millisecond != null) mutableValue.millisecond = fields.millisecond;
	$435a2ceaa8778ed8$export$7555de1e070510cb(mutableValue);
	return mutableValue;
}
function $435a2ceaa8778ed8$var$balanceTime(time) {
	time.second += Math.floor(time.millisecond / 1e3);
	time.millisecond = $435a2ceaa8778ed8$var$nonNegativeMod(time.millisecond, 1e3);
	time.minute += Math.floor(time.second / 60);
	time.second = $435a2ceaa8778ed8$var$nonNegativeMod(time.second, 60);
	time.hour += Math.floor(time.minute / 60);
	time.minute = $435a2ceaa8778ed8$var$nonNegativeMod(time.minute, 60);
	let days = Math.floor(time.hour / 24);
	time.hour = $435a2ceaa8778ed8$var$nonNegativeMod(time.hour, 24);
	return days;
}
function $435a2ceaa8778ed8$export$7555de1e070510cb(time) {
	time.millisecond = Math.max(0, Math.min(time.millisecond, 999));
	time.second = Math.max(0, Math.min(time.second, 59));
	time.minute = Math.max(0, Math.min(time.minute, 59));
	time.hour = Math.max(0, Math.min(time.hour, 23));
}
function $435a2ceaa8778ed8$var$nonNegativeMod(a, b) {
	let result = a % b;
	if (result < 0) result += b;
	return result;
}
function $435a2ceaa8778ed8$var$addTimeFields(time, duration) {
	time.hour += duration.hours || 0;
	time.minute += duration.minutes || 0;
	time.second += duration.seconds || 0;
	time.millisecond += duration.milliseconds || 0;
	return $435a2ceaa8778ed8$var$balanceTime(time);
}
function $435a2ceaa8778ed8$export$d52ced6badfb9a4c(value, field, amount, options) {
	let mutable = value.copy();
	switch (field) {
		case "era": {
			let eras = value.calendar.getEras();
			let eraIndex = eras.indexOf(value.era);
			if (eraIndex < 0) throw new Error("Invalid era: " + value.era);
			eraIndex = $435a2ceaa8778ed8$var$cycleValue(eraIndex, amount, 0, eras.length - 1, options?.round);
			mutable.era = eras[eraIndex];
			$435a2ceaa8778ed8$export$c4e2ecac49351ef2(mutable);
			break;
		}
		case "year":
			if (mutable.calendar.isInverseEra?.(mutable)) amount = -amount;
			mutable.year = $435a2ceaa8778ed8$var$cycleValue(value.year, amount, -Infinity, 9999, options?.round);
			if (mutable.year === -Infinity) mutable.year = 1;
			if (mutable.calendar.balanceYearMonth) mutable.calendar.balanceYearMonth(mutable, value);
			break;
		case "month":
			mutable.month = $435a2ceaa8778ed8$var$cycleValue(value.month, amount, 1, value.calendar.getMonthsInYear(value), options?.round);
			break;
		case "day":
			mutable.day = $435a2ceaa8778ed8$var$cycleValue(value.day, amount, 1, value.calendar.getDaysInMonth(value), options?.round);
			break;
		default: throw new Error("Unsupported field " + field);
	}
	if (value.calendar.balanceDate) value.calendar.balanceDate(mutable);
	$435a2ceaa8778ed8$export$c4e2ecac49351ef2(mutable);
	return mutable;
}
function $435a2ceaa8778ed8$export$dd02b3e0007dfe28(value, field, amount, options) {
	let mutable = value.copy();
	switch (field) {
		case "hour": {
			let hours = value.hour;
			let min = 0;
			let max = 23;
			if (options?.hourCycle === 12) {
				let isPM = hours >= 12;
				min = isPM ? 12 : 0;
				max = isPM ? 23 : 11;
			}
			mutable.hour = $435a2ceaa8778ed8$var$cycleValue(hours, amount, min, max, options?.round);
			break;
		}
		case "minute":
			mutable.minute = $435a2ceaa8778ed8$var$cycleValue(value.minute, amount, 0, 59, options?.round);
			break;
		case "second":
			mutable.second = $435a2ceaa8778ed8$var$cycleValue(value.second, amount, 0, 59, options?.round);
			break;
		case "millisecond":
			mutable.millisecond = $435a2ceaa8778ed8$var$cycleValue(value.millisecond, amount, 0, 999, options?.round);
			break;
		default: throw new Error("Unsupported field " + field);
	}
	return mutable;
}
function $435a2ceaa8778ed8$var$cycleValue(value, amount, min, max, round = false) {
	if (round) {
		value += Math.sign(amount);
		if (value < min) value = max;
		let div = Math.abs(amount);
		if (amount > 0) value = Math.ceil(value / div) * div;
		else value = Math.floor(value / div) * div;
		if (value > max) value = min;
	} else {
		value += amount;
		if (value < min) value = max - (min - value - 1);
		else if (value > max) value = min + (value - max - 1);
	}
	return value;
}
function $435a2ceaa8778ed8$export$96b1d28349274637(dateTime, duration) {
	let ms;
	if (duration.years != null && duration.years !== 0 || duration.months != null && duration.months !== 0 || duration.weeks != null && duration.weeks !== 0 || duration.days != null && duration.days !== 0) ms = $d07e34cce18680fd$export$5107c82f94518f5c($435a2ceaa8778ed8$export$e16d8520af44a096($d07e34cce18680fd$export$b21e0b124e224484(dateTime), {
		years: duration.years,
		months: duration.months,
		weeks: duration.weeks,
		days: duration.days
	}), dateTime.timeZone);
	else ms = $d07e34cce18680fd$export$bd4fb2bc8bb06fb(dateTime) - dateTime.offset;
	ms += duration.milliseconds || 0;
	ms += (duration.seconds || 0) * 1e3;
	ms += (duration.minutes || 0) * 6e4;
	ms += (duration.hours || 0) * 36e5;
	return $d07e34cce18680fd$export$b4a036af3fc0b032($d07e34cce18680fd$export$1b96692a1ba042ac(ms, dateTime.timeZone), dateTime.calendar);
}
function $435a2ceaa8778ed8$export$6814caac34ca03c7(dateTime, duration) {
	return $435a2ceaa8778ed8$export$96b1d28349274637(dateTime, $435a2ceaa8778ed8$export$3e2544e88a25bff8(duration));
}
function $435a2ceaa8778ed8$export$9a297d111fc86b79(dateTime, field, amount, options) {
	switch (field) {
		case "hour": {
			let min = 0;
			let max = 23;
			if (options?.hourCycle === 12) {
				let isPM = dateTime.hour >= 12;
				min = isPM ? 12 : 0;
				max = isPM ? 23 : 11;
			}
			let plainDateTime = $d07e34cce18680fd$export$b21e0b124e224484(dateTime);
			let minDate = $d07e34cce18680fd$export$b4a036af3fc0b032($435a2ceaa8778ed8$export$e5d5e1c1822b6e56(plainDateTime, { hour: min }), new $93635573935797de$export$80ee6245ec4f29ec());
			let minAbsolute = [$d07e34cce18680fd$export$5107c82f94518f5c(minDate, dateTime.timeZone, "earlier"), $d07e34cce18680fd$export$5107c82f94518f5c(minDate, dateTime.timeZone, "later")].filter((ms) => $d07e34cce18680fd$export$1b96692a1ba042ac(ms, dateTime.timeZone).day === minDate.day)[0];
			let maxDate = $d07e34cce18680fd$export$b4a036af3fc0b032($435a2ceaa8778ed8$export$e5d5e1c1822b6e56(plainDateTime, { hour: max }), new $93635573935797de$export$80ee6245ec4f29ec());
			let maxAbsolute = [$d07e34cce18680fd$export$5107c82f94518f5c(maxDate, dateTime.timeZone, "earlier"), $d07e34cce18680fd$export$5107c82f94518f5c(maxDate, dateTime.timeZone, "later")].filter((ms) => $d07e34cce18680fd$export$1b96692a1ba042ac(ms, dateTime.timeZone).day === maxDate.day).pop();
			let ms = $d07e34cce18680fd$export$bd4fb2bc8bb06fb(dateTime) - dateTime.offset;
			let hours = Math.floor(ms / $435a2ceaa8778ed8$var$ONE_HOUR);
			let remainder = ms % $435a2ceaa8778ed8$var$ONE_HOUR;
			ms = $435a2ceaa8778ed8$var$cycleValue(hours, amount, Math.floor(minAbsolute / $435a2ceaa8778ed8$var$ONE_HOUR), Math.floor(maxAbsolute / $435a2ceaa8778ed8$var$ONE_HOUR), options?.round) * $435a2ceaa8778ed8$var$ONE_HOUR + remainder;
			return $d07e34cce18680fd$export$b4a036af3fc0b032($d07e34cce18680fd$export$1b96692a1ba042ac(ms, dateTime.timeZone), dateTime.calendar);
		}
		case "minute":
		case "second":
		case "millisecond": return $435a2ceaa8778ed8$export$dd02b3e0007dfe28(dateTime, field, amount, options);
		case "era":
		case "year":
		case "month":
		case "day": return $d07e34cce18680fd$export$b4a036af3fc0b032($d07e34cce18680fd$export$1b96692a1ba042ac($d07e34cce18680fd$export$5107c82f94518f5c($435a2ceaa8778ed8$export$d52ced6badfb9a4c($d07e34cce18680fd$export$b21e0b124e224484(dateTime), field, amount, options), dateTime.timeZone), dateTime.timeZone), dateTime.calendar);
		default: throw new Error("Unsupported field " + field);
	}
}
function $435a2ceaa8778ed8$export$31b5430eb18be4f8(dateTime, fields, disambiguation) {
	let plainDateTime = $d07e34cce18680fd$export$b21e0b124e224484(dateTime);
	let res = $435a2ceaa8778ed8$export$e5d5e1c1822b6e56($435a2ceaa8778ed8$export$adaa4cf7ef1b65be(plainDateTime, fields), fields);
	if (res.compare(plainDateTime) === 0) return dateTime;
	return $d07e34cce18680fd$export$b4a036af3fc0b032($d07e34cce18680fd$export$1b96692a1ba042ac($d07e34cce18680fd$export$5107c82f94518f5c(res, dateTime.timeZone, disambiguation), dateTime.timeZone), dateTime.calendar);
}
// ../node_modules/@internationalized/date/dist/private/string.mjs
const $58246871e4652552$var$DATE_RE = /^([+-]\d{6}|\d{4})-(\d{2})-(\d{2})$/;
const $58246871e4652552$var$ABSOLUTE_RE = /^([+-]\d{6}|\d{4})-(\d{2})-(\d{2})(?:T(\d{2}))?(?::(\d{2}))?(?::(\d{2}))?(\.\d+)?(?:(?:([+-]\d{2})(?::?(\d{2}))?)|Z)$/;
function $58246871e4652552$export$6b862160d295c8e(value) {
	let m = value.match($58246871e4652552$var$DATE_RE);
	if (!m) {
		if ($58246871e4652552$var$ABSOLUTE_RE.test(value)) throw new Error(`Invalid ISO 8601 date string: ${value}. Use parseAbsolute() instead.`);
		throw new Error("Invalid ISO 8601 date string: " + value);
	}
	let date = new $2aaf608024c21ca1$export$99faa760c7908e4f($58246871e4652552$var$parseNumber(m[1], 0, 9999), $58246871e4652552$var$parseNumber(m[2], 1, 12), 1);
	date.day = $58246871e4652552$var$parseNumber(m[3], 1, date.calendar.getDaysInMonth(date));
	return date;
}
function $58246871e4652552$var$parseNumber(value, min, max) {
	let val = Number(value);
	if (val < min || val > max) throw new RangeError(`Value out of range: ${min} <= ${val} <= ${max}`);
	return val;
}
function $58246871e4652552$export$f59dee82248f5ad4(time) {
	return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}:${String(time.second).padStart(2, "0")}${time.millisecond ? String(time.millisecond / 1e3).slice(1) : ""}`;
}
function $58246871e4652552$export$60dfd74aa96791bd(date) {
	let gregorianDate = $d07e34cce18680fd$export$b4a036af3fc0b032(date, new $93635573935797de$export$80ee6245ec4f29ec());
	let year;
	if (gregorianDate.era === "BC") year = gregorianDate.year === 1 ? "0000" : "-" + String(Math.abs(1 - gregorianDate.year)).padStart(6, "00");
	else year = String(gregorianDate.year).padStart(4, "0");
	return `${year}-${String(gregorianDate.month).padStart(2, "0")}-${String(gregorianDate.day).padStart(2, "0")}`;
}
function $58246871e4652552$export$4223de14708adc63(date) {
	return `${$58246871e4652552$export$60dfd74aa96791bd(date)}T${$58246871e4652552$export$f59dee82248f5ad4(date)}`;
}
function $58246871e4652552$var$offsetToString(offset) {
	let sign = Math.sign(offset) < 0 ? "-" : "+";
	offset = Math.abs(offset);
	let offsetHours = Math.floor(offset / 36e5);
	let offsetMinutes = Math.floor(offset % 36e5 / 6e4);
	let offsetSeconds = Math.floor(offset % 36e5 % 6e4 / 1e3);
	let stringOffset = `${sign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutes).padStart(2, "0")}`;
	if (offsetSeconds !== 0) stringOffset += `:${String(offsetSeconds).padStart(2, "0")}`;
	return stringOffset;
}
function $58246871e4652552$export$bf79f1ebf4b18792(date) {
	return `${$58246871e4652552$export$4223de14708adc63(date)}${$58246871e4652552$var$offsetToString(date.offset)}[${date.timeZone}]`;
}
// ../node_modules/@internationalized/date/dist/private/CalendarDate.mjs
function $2aaf608024c21ca1$var$shiftArgs(args) {
	let calendar = typeof args[0] === "object" ? args.shift() : new $93635573935797de$export$80ee6245ec4f29ec();
	let era;
	if (typeof args[0] === "string") era = args.shift();
	else {
		let eras = calendar.getEras();
		era = eras[eras.length - 1];
	}
	let year = args.shift();
	let month = args.shift();
	let day = args.shift();
	return [
		calendar,
		era,
		year,
		month,
		day
	];
}
var $2aaf608024c21ca1$export$99faa760c7908e4f = class $2aaf608024c21ca1$export$99faa760c7908e4f {
	#type;
	constructor(...args) {
		let [calendar, era, year, month, day] = $2aaf608024c21ca1$var$shiftArgs(args);
		this.calendar = calendar;
		this.era = era;
		this.year = year;
		this.month = month;
		this.day = day;
		$435a2ceaa8778ed8$export$c4e2ecac49351ef2(this);
	}
	/** Returns a copy of this date. */ copy() {
		if (this.era) return new $2aaf608024c21ca1$export$99faa760c7908e4f(this.calendar, this.era, this.year, this.month, this.day);
		else return new $2aaf608024c21ca1$export$99faa760c7908e4f(this.calendar, this.year, this.month, this.day);
	}
	/** Returns a new `CalendarDate` with the given duration added to it. */ add(duration) {
		return $435a2ceaa8778ed8$export$e16d8520af44a096(this, duration);
	}
	/** Returns a new `CalendarDate` with the given duration subtracted from it. */ subtract(duration) {
		return $435a2ceaa8778ed8$export$4e2d2ead65e5f7e3(this, duration);
	}
	/**
	* Returns a new `CalendarDate` with the given fields set to the provided values. Other fields
	* will be constrained accordingly.
	*/ set(fields) {
		return $435a2ceaa8778ed8$export$adaa4cf7ef1b65be(this, fields);
	}
	/**
	* Returns a new `CalendarDate` with the given field adjusted by a specified amount.
	* When the resulting value reaches the limits of the field, it wraps around.
	*/ cycle(field, amount, options) {
		return $435a2ceaa8778ed8$export$d52ced6badfb9a4c(this, field, amount, options);
	}
	/**
	* Converts the date to a native JavaScript Date object, with the time set to midnight in the
	* given time zone.
	*/ toDate(timeZone) {
		return $d07e34cce18680fd$export$e67a095c620b86fe(this, timeZone);
	}
	/** Converts the date to an ISO 8601 formatted string. */ toString() {
		return $58246871e4652552$export$60dfd74aa96791bd(this);
	}
	/**
	* Compares this date with another. A negative result indicates that this date is before the given
	* one, and a positive date indicates that it is after.
	*/ compare(b) {
		return $ad063034c8620db8$export$68781ddf31c0090f(this, b);
	}
};
var $2aaf608024c21ca1$export$ca871e8dbb80966f = class $2aaf608024c21ca1$export$ca871e8dbb80966f {
	#type;
	constructor(...args) {
		let [calendar, era, year, month, day] = $2aaf608024c21ca1$var$shiftArgs(args);
		this.calendar = calendar;
		this.era = era;
		this.year = year;
		this.month = month;
		this.day = day;
		this.hour = args.shift() || 0;
		this.minute = args.shift() || 0;
		this.second = args.shift() || 0;
		this.millisecond = args.shift() || 0;
		$435a2ceaa8778ed8$export$c4e2ecac49351ef2(this);
	}
	/** Returns a copy of this date. */ copy() {
		if (this.era) return new $2aaf608024c21ca1$export$ca871e8dbb80966f(this.calendar, this.era, this.year, this.month, this.day, this.hour, this.minute, this.second, this.millisecond);
		else return new $2aaf608024c21ca1$export$ca871e8dbb80966f(this.calendar, this.year, this.month, this.day, this.hour, this.minute, this.second, this.millisecond);
	}
	/** Returns a new `CalendarDateTime` with the given duration added to it. */ add(duration) {
		return $435a2ceaa8778ed8$export$e16d8520af44a096(this, duration);
	}
	/** Returns a new `CalendarDateTime` with the given duration subtracted from it. */ subtract(duration) {
		return $435a2ceaa8778ed8$export$4e2d2ead65e5f7e3(this, duration);
	}
	/**
	* Returns a new `CalendarDateTime` with the given fields set to the provided values. Other fields
	* will be constrained accordingly.
	*/ set(fields) {
		return $435a2ceaa8778ed8$export$adaa4cf7ef1b65be($435a2ceaa8778ed8$export$e5d5e1c1822b6e56(this, fields), fields);
	}
	/**
	* Returns a new `CalendarDateTime` with the given field adjusted by a specified amount.
	* When the resulting value reaches the limits of the field, it wraps around.
	*/ cycle(field, amount, options) {
		switch (field) {
			case "era":
			case "year":
			case "month":
			case "day": return $435a2ceaa8778ed8$export$d52ced6badfb9a4c(this, field, amount, options);
			default: return $435a2ceaa8778ed8$export$dd02b3e0007dfe28(this, field, amount, options);
		}
	}
	/** Converts the date to a native JavaScript Date object in the given time zone. */ toDate(timeZone, disambiguation) {
		return $d07e34cce18680fd$export$e67a095c620b86fe(this, timeZone, disambiguation);
	}
	/** Converts the date to an ISO 8601 formatted string. */ toString() {
		return $58246871e4652552$export$4223de14708adc63(this);
	}
	/**
	* Compares this date with another. A negative result indicates that this date is before the given
	* one, and a positive date indicates that it is after.
	*/ compare(b) {
		let res = $ad063034c8620db8$export$68781ddf31c0090f(this, b);
		if (res === 0) return $ad063034c8620db8$export$c19a80a9721b80f6(this, $d07e34cce18680fd$export$b21e0b124e224484(b));
		return res;
	}
};
var $2aaf608024c21ca1$export$d3b7288e7994edea = class $2aaf608024c21ca1$export$d3b7288e7994edea {
	#type;
	constructor(...args) {
		let [calendar, era, year, month, day] = $2aaf608024c21ca1$var$shiftArgs(args);
		let timeZone = args.shift();
		let offset = args.shift();
		this.calendar = calendar;
		this.era = era;
		this.year = year;
		this.month = month;
		this.day = day;
		this.timeZone = timeZone;
		this.offset = offset;
		this.hour = args.shift() || 0;
		this.minute = args.shift() || 0;
		this.second = args.shift() || 0;
		this.millisecond = args.shift() || 0;
		$435a2ceaa8778ed8$export$c4e2ecac49351ef2(this);
	}
	/** Returns a copy of this date. */ copy() {
		if (this.era) return new $2aaf608024c21ca1$export$d3b7288e7994edea(this.calendar, this.era, this.year, this.month, this.day, this.timeZone, this.offset, this.hour, this.minute, this.second, this.millisecond);
		else return new $2aaf608024c21ca1$export$d3b7288e7994edea(this.calendar, this.year, this.month, this.day, this.timeZone, this.offset, this.hour, this.minute, this.second, this.millisecond);
	}
	/** Returns a new `ZonedDateTime` with the given duration added to it. */ add(duration) {
		return $435a2ceaa8778ed8$export$96b1d28349274637(this, duration);
	}
	/** Returns a new `ZonedDateTime` with the given duration subtracted from it. */ subtract(duration) {
		return $435a2ceaa8778ed8$export$6814caac34ca03c7(this, duration);
	}
	/**
	* Returns a new `ZonedDateTime` with the given fields set to the provided values. Other fields
	* will be constrained accordingly.
	*/ set(fields, disambiguation) {
		return $435a2ceaa8778ed8$export$31b5430eb18be4f8(this, fields, disambiguation);
	}
	/**
	* Returns a new `ZonedDateTime` with the given field adjusted by a specified amount.
	* When the resulting value reaches the limits of the field, it wraps around.
	*/ cycle(field, amount, options) {
		return $435a2ceaa8778ed8$export$9a297d111fc86b79(this, field, amount, options);
	}
	/** Converts the date to a native JavaScript Date object. */ toDate() {
		return $d07e34cce18680fd$export$83aac07b4c37b25(this);
	}
	/**
	* Converts the date to an ISO 8601 formatted string, including the UTC offset and time zone
	* identifier.
	*/ toString() {
		return $58246871e4652552$export$bf79f1ebf4b18792(this);
	}
	/** Converts the date to an ISO 8601 formatted string in UTC. */ toAbsoluteString() {
		return this.toDate().toISOString();
	}
	/**
	* Compares this date with another. A negative result indicates that this date is before the given
	* one, and a positive date indicates that it is after.
	*/ compare(b) {
		return this.toDate().getTime() - $d07e34cce18680fd$export$84c95a83c799e074(b, this.timeZone).toDate().getTime();
	}
};
// ../node_modules/@internationalized/date/dist/private/DateFormatter.mjs
let $12a3c853105e5a70$var$formatterCache = /* @__PURE__ */ new Map();
var $12a3c853105e5a70$export$ad991b66133851cf = class {
	constructor(locale, options = {}) {
		this.formatter = $12a3c853105e5a70$var$getCachedDateFormatter(locale, options);
		this.options = options;
	}
	/**
	* Formats a date as a string according to the locale and format options passed to the
	* constructor.
	*/ format(value) {
		return this.formatter.format(value);
	}
	/** Formats a date to an array of parts such as separators, numbers, punctuation, and more. */ formatToParts(value) {
		return this.formatter.formatToParts(value);
	}
	/** Formats a date range as a string. */ formatRange(start, end) {
		if (typeof this.formatter.formatRange === "function") return this.formatter.formatRange(start, end);
		if (end < start) throw new RangeError("End date must be >= start date");
		return `${this.formatter.format(start)} \u{2013} ${this.formatter.format(end)}`;
	}
	/** Formats a date range as an array of parts. */ formatRangeToParts(start, end) {
		if (typeof this.formatter.formatRangeToParts === "function") return this.formatter.formatRangeToParts(start, end);
		if (end < start) throw new RangeError("End date must be >= start date");
		let startParts = this.formatter.formatToParts(start);
		let endParts = this.formatter.formatToParts(end);
		return [
			...startParts.map((p) => ({
				...p,
				source: "startRange"
			})),
			{
				type: "literal",
				value: " – ",
				source: "shared"
			},
			...endParts.map((p) => ({
				...p,
				source: "endRange"
			}))
		];
	}
	/** Returns the resolved formatting options based on the values passed to the constructor. */ resolvedOptions() {
		let resolvedOptions = this.formatter.resolvedOptions();
		if ($12a3c853105e5a70$var$hasBuggyResolvedHourCycle()) {
			if (!this.resolvedHourCycle) this.resolvedHourCycle = $12a3c853105e5a70$var$getResolvedHourCycle(resolvedOptions.locale, this.options);
			resolvedOptions.hourCycle = this.resolvedHourCycle;
			resolvedOptions.hour12 = this.resolvedHourCycle === "h11" || this.resolvedHourCycle === "h12";
		}
		if (resolvedOptions.calendar === "ethiopic-amete-alem") resolvedOptions.calendar = "ethioaa";
		return resolvedOptions;
	}
};
const $12a3c853105e5a70$var$hour12Preferences = {
	true: { ja: "h11" },
	false: {}
};
function $12a3c853105e5a70$var$getCachedDateFormatter(locale, options = {}) {
	if (typeof options.hour12 === "boolean" && $12a3c853105e5a70$var$hasBuggyHour12Behavior()) {
		options = { ...options };
		let pref = $12a3c853105e5a70$var$hour12Preferences[String(options.hour12)][locale.split("-")[0]];
		let defaultHourCycle = options.hour12 ? "h12" : "h23";
		options.hourCycle = pref ?? defaultHourCycle;
		delete options.hour12;
	}
	let cacheKey = locale + (options ? Object.entries(options).sort((a, b) => a[0] < b[0] ? -1 : 1).join() : "");
	if ($12a3c853105e5a70$var$formatterCache.has(cacheKey)) return $12a3c853105e5a70$var$formatterCache.get(cacheKey);
	let numberFormatter = new Intl.DateTimeFormat(locale, options);
	$12a3c853105e5a70$var$formatterCache.set(cacheKey, numberFormatter);
	return numberFormatter;
}
let $12a3c853105e5a70$var$_hasBuggyHour12Behavior = null;
function $12a3c853105e5a70$var$hasBuggyHour12Behavior() {
	if ($12a3c853105e5a70$var$_hasBuggyHour12Behavior == null) $12a3c853105e5a70$var$_hasBuggyHour12Behavior = new Intl.DateTimeFormat("en-US", {
		hour: "numeric",
		hour12: false
	}).format(new Date(2020, 2, 3, 0)) === "24";
	return $12a3c853105e5a70$var$_hasBuggyHour12Behavior;
}
let $12a3c853105e5a70$var$_hasBuggyResolvedHourCycle = null;
function $12a3c853105e5a70$var$hasBuggyResolvedHourCycle() {
	if ($12a3c853105e5a70$var$_hasBuggyResolvedHourCycle == null) $12a3c853105e5a70$var$_hasBuggyResolvedHourCycle = new Intl.DateTimeFormat("fr", {
		hour: "numeric",
		hour12: false
	}).resolvedOptions().hourCycle === "h12";
	return $12a3c853105e5a70$var$_hasBuggyResolvedHourCycle;
}
function $12a3c853105e5a70$var$getResolvedHourCycle(locale, options) {
	if (!options.timeStyle && !options.hour) return void 0;
	locale = locale.replace(/(-u-)?-nu-[a-zA-Z0-9]+/, "");
	locale += (locale.includes("-u-") ? "" : "-u") + "-nu-latn";
	let formatter = $12a3c853105e5a70$var$getCachedDateFormatter(locale, {
		...options,
		timeZone: void 0
	});
	let min = parseInt(formatter.formatToParts(new Date(2020, 2, 3, 0)).find((p) => p.type === "hour").value, 10);
	let max = parseInt(formatter.formatToParts(new Date(2020, 2, 3, 23)).find((p) => p.type === "hour").value, 10);
	if (min === 0 && max === 23) return "h23";
	if (min === 24 && max === 23) return "h24";
	if (min === 0 && max === 11) return "h11";
	if (min === 12 && max === 11) return "h12";
	throw new Error("Unexpected hour cycle result");
}
// ../node_modules/@zag-js/date-utils/dist/constrain.mjs
function constrainSegments(date, minValue, maxValue) {
	const dateOnly = $d07e34cce18680fd$export$93522d1a439f3617(date);
	const minOnly = minValue ? $d07e34cce18680fd$export$93522d1a439f3617(minValue) : void 0;
	const maxOnly = maxValue ? $d07e34cce18680fd$export$93522d1a439f3617(maxValue) : void 0;
	let result = dateOnly;
	if (minOnly && result.compare(minOnly) < 0) {
		if (result.year < minOnly.year) result = result.set({ year: minOnly.year });
		if (result.compare(minOnly) < 0 && result.month < minOnly.month) result = result.set({ month: minOnly.month });
		if (result.compare(minOnly) < 0 && result.day < minOnly.day) result = result.set({ day: minOnly.day });
	}
	if (maxOnly && result.compare(maxOnly) > 0) {
		if (result.year > maxOnly.year) result = result.set({ year: maxOnly.year });
		if (result.compare(maxOnly) > 0 && result.month > maxOnly.month) result = result.set({ month: maxOnly.month });
		if (result.compare(maxOnly) > 0 && result.day > maxOnly.day) result = result.set({ day: maxOnly.day });
	}
	if (result.compare(dateOnly) === 0) return date;
	if ("hour" in date) return date.set({
		year: result.year,
		month: result.month,
		day: result.day
	});
	return result;
}
// ../node_modules/@zag-js/date-utils/dist/assertion.mjs
function isDateEqual(dateA, dateB) {
	if (dateA == null || dateB == null) return dateA === dateB;
	if (!("hour" in dateA) && !("hour" in dateB)) return $ad063034c8620db8$export$ea39ec197993aef0(dateA, dateB);
	return $d07e34cce18680fd$export$b21e0b124e224484(dateA).compare($d07e34cce18680fd$export$b21e0b124e224484(dateB)) === 0;
}
// ../node_modules/@zag-js/date-utils/dist/mutation.mjs
function getTodayDate(timeZone, calendar) {
	const tod = $ad063034c8620db8$export$d0bdf45af03a6ea3(timeZone ?? $ad063034c8620db8$export$aa8b41735afcabd2());
	if (calendar) return $d07e34cce18680fd$export$b4a036af3fc0b032(tod, calendar);
	return tod;
}
// ../node_modules/@zag-js/date-utils/dist/locale.mjs
var digitsCache = /* @__PURE__ */ new Map();
function getLocaleDigits(locale) {
	let digits = digitsCache.get(locale);
	if (digits != null) return digits;
	digits = "0123456789" + new Intl.NumberFormat(locale, { useGrouping: false }).format(1234567890);
	digitsCache.set(locale, digits);
	return digits;
}
var isDigit = (char, locale) => {
	return locale ? getLocaleDigits(locale).includes(char) : /\d/.test(char);
};
var isValidCharacter = (char, separator, locale) => {
	if (!char) return true;
	if (char.length !== 1) return true;
	return isDigit(char, locale) || separator.includes(char);
};
var separatorCache = /* @__PURE__ */ new Map();
function getLocaleSeparator(locale) {
	let separator = separatorCache.get(locale);
	if (separator != null) return separator;
	const literal = new Intl.DateTimeFormat(locale).formatToParts(/* @__PURE__ */ new Date()).find((part) => part.type === "literal");
	separator = literal ? literal.value : "/";
	separatorCache.set(locale, separator);
	return separator;
}
// ../node_modules/@zag-js/date-input/dist/utils/segments.mjs
function needsTimeGranularity(granularity) {
	return granularity === "hour" || granularity === "minute" || granularity === "second";
}
function getFormatterOptions(opts) {
	const { granularity, digitStyle, hourCycle, timeZone, hasTimeZone, hideTimeZone } = opts;
	const options = {
		timeZone,
		day: digitStyle,
		month: digitStyle,
		year: "numeric",
		hourCycle
	};
	if (needsTimeGranularity(granularity)) options.hour = digitStyle;
	if (granularity === "minute" || granularity === "second") options.minute = "2-digit";
	if (granularity === "second") options.second = "2-digit";
	if (hasTimeZone && !hideTimeZone) options.timeZoneName = "short";
	return options;
}
function resolveAllSegments(formatter) {
	const segs = formatter.formatToParts(/* @__PURE__ */ new Date()).filter((seg) => EDITABLE_SEGMENTS[seg.type]).reduce((p, seg) => {
		const key = TYPE_MAPPING[seg.type] || seg.type;
		p[key] = true;
		return p;
	}, {});
	if (segs.year) segs.era = true;
	return segs;
}
var EDITABLE_SEGMENTS = {
	year: true,
	month: true,
	day: true,
	hour: true,
	minute: true,
	second: true,
	dayPeriod: true,
	era: true,
	literal: false,
	timeZoneName: false,
	weekday: false,
	unknown: false,
	fractionalSecond: false
};
var PAGE_STEP = {
	year: 5,
	month: 2,
	day: 7,
	hour: 2,
	minute: 15,
	second: 15,
	dayPeriod: void 0,
	era: void 0,
	literal: void 0,
	timeZoneName: void 0,
	weekday: void 0,
	unknown: void 0,
	fractionalSecond: void 0
};
var SEGMENT_LABELS = {
	era: "Era",
	year: "Year",
	month: "Month",
	day: "Day",
	hour: "Hour",
	minute: "Minute",
	second: "Second",
	dayPeriod: "AM/PM",
	timeZoneName: "Time zone"
};
function getSegmentLabel(type) {
	return SEGMENT_LABELS[type] ?? type;
}
var TYPE_MAPPING = {
	dayperiod: "dayPeriod",
	relatedYear: "year",
	yearName: "literal",
	unknown: "literal"
};
function getSafeType(type) {
	return TYPE_MAPPING[type] ?? type;
}
function getPlaceholder(type, translations, locale) {
	return translations.placeholder?.(locale)?.[type] ?? "";
}
function isEditableSegment(type) {
	return EDITABLE_SEGMENTS[type] === true;
}
function getSafeFormatParts(formatter, dateValue, displayValue) {
	try {
		return formatter.formatToParts(dateValue);
	} catch {
		return formatter.formatToParts(/* @__PURE__ */ new Date()).map((part) => {
			switch (part.type) {
				case "year":
				case "relatedYear": return {
					...part,
					value: String(displayValue.year ?? 0)
				};
				case "month": return {
					...part,
					value: String(displayValue.month ?? 0)
				};
				case "day": return {
					...part,
					value: String(displayValue.day ?? 0)
				};
			}
			return part;
		});
	}
}
function processSegments({ dateValue, displayValue, formatter, locale, translations, granularity }) {
	const timeValue = [
		"hour",
		"minute",
		"second"
	];
	const segments = getSafeFormatParts(formatter, dateValue, displayValue);
	const processedSegments = [];
	for (const segment of segments) {
		const type = getSafeType(segment.type);
		let isEditable = isEditableSegment(type);
		if (type === "era" && displayValue.calendar.getEras().length === 1) isEditable = false;
		const isPlaceholder = isEditable && displayValue[type] == null;
		let placeholder = isEditableSegment(type) ? getPlaceholder(type, translations, locale) : null;
		if ((type === "dayPeriod" || type === "era") && segment.value) placeholder = segment.value;
		const dateSegment = {
			type,
			text: isPlaceholder ? placeholder : segment.value,
			...displayValue.getSegmentLimits(type) ?? {},
			isPlaceholder,
			placeholder,
			isEditable
		};
		if (type === "hour") {
			processedSegments.push({
				type: "literal",
				text: "⁦",
				isPlaceholder: false,
				placeholder: "",
				isEditable: false
			});
			processedSegments.push(dateSegment);
			if (type === granularity) processedSegments.push({
				type: "literal",
				text: "⁩",
				isPlaceholder: false,
				placeholder: "",
				isEditable: false
			});
		} else if (timeValue.includes(type) && type === granularity) {
			processedSegments.push(dateSegment);
			processedSegments.push({
				type: "literal",
				text: "⁩",
				isPlaceholder: false,
				placeholder: "",
				isEditable: false
			});
		} else processedSegments.push(dateSegment);
	}
	return processedSegments;
}
// ../node_modules/@zag-js/date-input/dist/utils/validity.mjs
var isFocusableSegment = (segment) => segment.type !== "literal";
function getGroupCount(selectionMode) {
	return selectionMode === "range" ? 2 : 1;
}
function getGroupOffset(allSegments, index) {
	return allSegments.slice(0, index).reduce((acc, segs) => acc + segs.length, 0);
}
function resolveActiveSegment(ctx) {
	const { context, computed } = ctx;
	const index = context.get("activeIndex");
	const activeSegmentIndex = context.get("activeSegmentIndex");
	const allSegments = computed("segments");
	const offset = getGroupOffset(allSegments, index);
	return {
		allSegments,
		segments: allSegments[index],
		offset,
		localIndex: activeSegmentIndex - offset
	};
}
function getActiveSegment(ctx) {
	const { allSegments, localIndex } = resolveActiveSegment(ctx);
	return allSegments[ctx.context.get("activeIndex")]?.[localIndex];
}
function goToNextSegment(ctx, predicate = isFocusableSegment) {
	const { context } = ctx;
	const index = context.get("activeIndex");
	const { allSegments, segments, offset, localIndex } = resolveActiveSegment(ctx);
	const nextLocalIndex = segments.findIndex((s, i) => i > localIndex && predicate(s));
	if (nextLocalIndex !== -1) {
		context.set("activeSegmentIndex", offset + nextLocalIndex);
		return;
	}
	const nextGroupSegments = allSegments[index + 1];
	if (!nextGroupSegments) return;
	const firstNextGroupLocalIndex = nextGroupSegments.findIndex(predicate);
	if (firstNextGroupLocalIndex === -1) return;
	context.set("activeIndex", index + 1);
	context.set("activeSegmentIndex", offset + segments.length + firstNextGroupLocalIndex);
}
function goToPreviousSegment(ctx, predicate = isFocusableSegment) {
	const { context } = ctx;
	const index = context.get("activeIndex");
	const { allSegments, segments, offset, localIndex } = resolveActiveSegment(ctx);
	const prevLocalIndex = segments.findLastIndex((s, i) => i < localIndex && predicate(s));
	if (prevLocalIndex !== -1) {
		context.set("activeSegmentIndex", offset + prevLocalIndex);
		return;
	}
	const prevGroupIndex = index - 1;
	if (prevGroupIndex < 0) return;
	const prevGroupSegments = allSegments[prevGroupIndex];
	if (!prevGroupSegments) return;
	const lastPrevGroupLocalIndex = prevGroupSegments.findLastIndex(predicate);
	if (lastPrevGroupLocalIndex === -1) return;
	context.set("activeIndex", prevGroupIndex);
	context.set("activeSegmentIndex", getGroupOffset(allSegments, prevGroupIndex) + lastPrevGroupLocalIndex);
}
function getActiveDisplayValue(ctx) {
	const index = ctx.context.get("activeIndex");
	return ctx.context.get("displayValues")[index];
}
function setDisplayValue(ctx, index, dv) {
	const displayValues = [...ctx.context.get("displayValues")];
	displayValues[index] = dv;
	ctx.context.set("displayValues", displayValues);
}
// ../node_modules/@zag-js/date-input/dist/date-input.connect.mjs
function connect(service, normalize) {
	const { state, context, prop, send, computed, scope } = service;
	const disabled = Boolean(prop("disabled"));
	const readOnly = Boolean(prop("readOnly"));
	const isDateUnavailableFn = prop("isDateUnavailable");
	const value = context.get("value");
	const valueAsDate = value.filter((date) => date != null).map((date) => date.toDate(prop("timeZone")));
	const isDateUnavailable = isDateUnavailableFn ? value.some((date) => date != null && isDateUnavailableFn(date, prop("locale"))) : false;
	const invalid = prop("invalid") ?? isDateUnavailable;
	const focused = state.matches("focused");
	const locale = prop("locale");
	const separator = getLocaleSeparator(locale);
	function getSegmentState(props) {
		const { segment, index = 0 } = props;
		const isEditable = !disabled && !readOnly && segment.isEditable;
		const activeIndex = context.get("activeIndex");
		return {
			editable: isEditable,
			focused: focused && activeIndex === index,
			readonly: !segment.isEditable || readOnly
		};
	}
	return {
		focused,
		disabled,
		invalid,
		value,
		valueAsDate,
		valueAsString: computed("valueAsString"),
		placeholderValue: context.get("placeholderValue"),
		displayValues: context.get("displayValues"),
		focus() {
			getSegmentEls(scope)[0]?.focus();
		},
		setValue(values) {
			send({
				type: "VALUE.SET",
				value: values
			});
		},
		clearValue() {
			send({ type: "VALUE.CLEAR" });
		},
		getSegments(props = {}) {
			const { index = 0 } = props;
			const allSegments = computed("segments");
			const segments = allSegments[index] ?? [];
			const enteredKeys = context.get("enteredKeys");
			const activeIndex = context.get("activeIndex");
			const activeSegmentIndex = context.get("activeSegmentIndex");
			if (focused && enteredKeys && index === activeIndex && activeSegmentIndex >= 0) {
				const localActiveSegmentIndex = activeSegmentIndex - getGroupOffset(allSegments, index);
				return segments.map((segment, index2) => {
					if (index2 !== localActiveSegmentIndex) return segment;
					return {
						...segment,
						text: enteredKeys,
						isPlaceholder: false
					};
				});
			}
			return segments;
		},
		getSegmentState,
		getRootProps() {
			return normalize.element({
				...parts.root.attrs,
				dir: prop("dir"),
				id: getRootId(scope),
				"data-disabled": dataAttr(disabled),
				"data-readonly": dataAttr(readOnly),
				"data-invalid": dataAttr(invalid)
			});
		},
		getLabelProps(props = {}) {
			const { index = 0 } = props;
			return normalize.label({
				...parts.label.attrs,
				id: getLabelId(scope, index),
				dir: prop("dir"),
				htmlFor: getHiddenInputId(scope, index),
				"data-disabled": dataAttr(disabled),
				"data-readonly": dataAttr(readOnly),
				"data-invalid": dataAttr(invalid),
				onClick() {
					if (disabled) return;
					getSegmentEls(scope)[0]?.focus();
				}
			});
		},
		getControlProps() {
			return normalize.element({
				...parts.control.attrs,
				dir: prop("dir"),
				id: getControlId(scope),
				"data-disabled": dataAttr(disabled),
				"data-readonly": dataAttr(readOnly),
				"data-invalid": dataAttr(invalid),
				"data-focus": dataAttr(focused)
			});
		},
		getSegmentGroupProps(props = {}) {
			const { index = 0 } = props;
			const activeIndex = context.get("activeIndex");
			return normalize.element({
				...parts.segmentGroup.attrs,
				id: getSegmentGroupId(scope, index),
				dir: prop("dir"),
				role: "group",
				"aria-labelledby": getLabelId(scope, index),
				"data-disabled": dataAttr(disabled),
				"data-readonly": dataAttr(readOnly),
				"data-invalid": dataAttr(invalid),
				"data-focus": dataAttr(focused && activeIndex === index),
				style: { unicodeBidi: "isolate" }
			});
		},
		getSegmentProps(props) {
			const { segment, index = 0 } = props;
			const segmentState = getSegmentState(props);
			if (segment.type === "literal") return normalize.element({
				...parts.segment.attrs,
				dir: prop("dir"),
				"aria-hidden": true,
				"data-type": segment.type,
				"data-readonly": "",
				"data-disabled": ""
			});
			return normalize.element({
				...parts.segment.attrs,
				dir: prop("dir"),
				role: "spinbutton",
				tabIndex: disabled ? void 0 : 0,
				autoComplete: "off",
				spellCheck: segmentState.editable ? "false" : void 0,
				autoCorrect: segmentState.editable ? "off" : void 0,
				contentEditable: segmentState.editable,
				suppressContentEditableWarning: segmentState.editable,
				inputMode: disabled || segment.type === "dayPeriod" || segment.type === "era" || !segmentState.editable ? void 0 : "numeric",
				enterKeyHint: "next",
				"aria-label": getSegmentLabel(segment.type),
				"aria-valuenow": segment.isPlaceholder ? void 0 : segment.value,
				"aria-valuetext": segment.isPlaceholder ? segment.placeholder : segment.text,
				"aria-valuemin": segment.minValue,
				"aria-valuemax": segment.maxValue,
				"aria-invalid": ariaAttr(invalid),
				"aria-readonly": ariaAttr(segmentState.readonly),
				"aria-disabled": ariaAttr(disabled),
				"data-value": segment.value,
				"data-type": segment.type,
				"data-readonly": dataAttr(segmentState.readonly),
				"data-disabled": dataAttr(disabled),
				"data-editable": dataAttr(segment.isEditable && !readOnly && !disabled),
				"data-placeholder-shown": dataAttr(segment.isPlaceholder),
				style: { caretColor: "transparent" },
				onFocus(event) {
					const segmentEls = getSegmentEls(scope);
					const target = event.currentTarget;
					const segmentIndex = segmentEls.indexOf(target);
					send({
						type: "SEGMENT.FOCUS",
						dateIndex: index,
						segmentIndex
					});
					const selection = target.ownerDocument?.getSelection?.();
					if (selection && target) selection.collapse(target);
				},
				onBlur(event) {
					const next = event.relatedTarget;
					if (contains(getControlEl(scope), next)) return;
					send({
						type: "SEGMENT.BLUR",
						index: -1
					});
				},
				onKeyDown(event) {
					if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || readOnly || isComposingEvent(event)) return;
					const exec = {
						ArrowLeft() {
							send({ type: "SEGMENT.ARROW_LEFT" });
						},
						ArrowRight() {
							send({ type: "SEGMENT.ARROW_RIGHT" });
						},
						ArrowUp() {
							send({
								type: "SEGMENT.ADJUST",
								segment,
								amount: 1
							});
						},
						ArrowDown() {
							send({
								type: "SEGMENT.ADJUST",
								segment,
								amount: -1
							});
						},
						PageUp() {
							send({
								type: "SEGMENT.ADJUST",
								segment,
								amount: PAGE_STEP[segment.type] ?? 1
							});
						},
						PageDown() {
							send({
								type: "SEGMENT.ADJUST",
								segment,
								amount: -(PAGE_STEP[segment.type] ?? 1)
							});
						},
						Backspace() {
							send({
								type: "SEGMENT.BACKSPACE",
								segment
							});
						},
						Delete() {
							send({
								type: "SEGMENT.BACKSPACE",
								segment
							});
						},
						Home() {
							send({
								type: "SEGMENT.HOME",
								segment
							});
						},
						End() {
							send({
								type: "SEGMENT.END",
								segment
							});
						}
					}[getEventKey(event, { dir: prop("dir") })];
					if (exec) {
						exec(event);
						event.preventDefault();
						event.stopPropagation();
					}
				},
				onPointerDown(event) {
					event.stopPropagation();
				},
				onMouseDown(event) {
					event.stopPropagation();
				},
				onBeforeInput(event) {
					const { data, inputType } = getNativeEvent(event);
					if (ALLOWED_INPUT_TYPES.includes(inputType)) {
						event.preventDefault();
						return;
					}
					if (inputType === "insertFromPaste") {
						event.preventDefault();
						return;
					}
					if (inputType === "insertCompositionText") {
						if (event.currentTarget || event.target) {
							event.preventDefault();
							if (data != null) send({
								type: "SEGMENT.INPUT",
								segment,
								input: data
							});
						}
						return;
					}
					const isTextSegment = segment.type === "dayPeriod" || segment.type === "era";
					if (data && (isTextSegment || isValidCharacter(data, separator, locale))) {
						event.preventDefault();
						send({
							type: "SEGMENT.INPUT",
							segment,
							input: data
						});
					} else event.preventDefault();
				},
				onPaste(event) {
					event.preventDefault();
					const text = event.clipboardData?.getData("text/plain")?.trim();
					if (text) send({
						type: "SEGMENT.PASTE",
						value: text
					});
				}
			});
		},
		getHiddenInputProps(props = {}) {
			const { index = 0, name } = props;
			const value2 = context.get("value");
			const inputName = name || prop("name");
			return normalize.input({
				...parts.hiddenInput.attrs,
				type: "hidden",
				id: getHiddenInputId(scope, index),
				name: inputName ? value2.length > 1 ? `${inputName}[${index}]` : inputName : void 0,
				form: prop("form"),
				required: prop("required"),
				disabled,
				readOnly,
				value: computed("valueAsString")[index] ?? "",
				style: visuallyHiddenStyle
			});
		}
	};
}
var ALLOWED_INPUT_TYPES = [
	"deleteContentBackward",
	"deleteContentForward",
	"deleteByCut",
	"deleteByDrag"
];
// ../node_modules/@zag-js/utils/dist/array.mjs
function toArray(v) {
	if (v == null) return [];
	return Array.isArray(v) ? v : [v];
}
// ../node_modules/@zag-js/utils/dist/equal.mjs
var isArrayLike = (value) => value?.constructor.name === "Array";
var isArrayEqual = (a, b) => {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) if (!isEqual(a[i], b[i])) return false;
	return true;
};
var isEqual = (a, b) => {
	if (Object.is(a, b)) return true;
	if (a == null && b != null || a != null && b == null) return false;
	if (typeof a?.isEqual === "function" && typeof b?.isEqual === "function") return a.isEqual(b);
	if (typeof a === "function" && typeof b === "function") return a.toString() === b.toString();
	if (isArrayLike(a) && isArrayLike(b)) return isArrayEqual(Array.from(a), Array.from(b));
	if (!(typeof a === "object") || !(typeof b === "object")) return false;
	const keys = Object.keys(b ?? /* @__PURE__ */ Object.create(null));
	const length = keys.length;
	for (let i = 0; i < length; i++) if (!Reflect.has(a, keys[i])) return false;
	for (let i = 0; i < length; i++) {
		const key = keys[i];
		if (!isEqual(a[key], b[key])) return false;
	}
	return true;
};
// ../node_modules/@zag-js/utils/dist/guard.mjs
var isObjectLike = (v) => v != null && typeof v === "object";
var isString = (v) => typeof v === "string";
var isFunction = (v) => typeof v === "function";
var hasProp = (obj, prop) => Object.prototype.hasOwnProperty.call(obj, prop);
var baseGetTag = (v) => Object.prototype.toString.call(v);
var fnToString = Function.prototype.toString;
var objectCtorString = fnToString.call(Object);
var isPlainObject = (v) => {
	if (!isObjectLike(v) || baseGetTag(v) != "[object Object]" || isFrameworkElement(v)) return false;
	const proto = Object.getPrototypeOf(v);
	if (proto === null) return true;
	const Ctor = hasProp(proto, "constructor") && proto.constructor;
	return typeof Ctor == "function" && Ctor instanceof Ctor && fnToString.call(Ctor) == objectCtorString;
};
var isReactElement = (x) => typeof x === "object" && x !== null && "$$typeof" in x && "props" in x;
var isVueElement = (x) => typeof x === "object" && x !== null && "__v_isVNode" in x;
var isFrameworkElement = (x) => isReactElement(x) || isVueElement(x);
// ../node_modules/@zag-js/utils/dist/functions.mjs
var identity = (v) => v();
var callAll = (...fns) => (...a) => {
	fns.forEach(function(fn) {
		fn?.(...a);
	});
};
// ../node_modules/@zag-js/utils/dist/object.mjs
function compact(obj) {
	if (!isPlainObject(obj) || obj === void 0) return obj;
	const keys2 = Reflect.ownKeys(obj).filter((key) => typeof key === "string");
	const filtered = {};
	for (const key of keys2) {
		const value = obj[key];
		if (value !== void 0) filtered[key] = compact(value);
	}
	return filtered;
}
function mergeWithDefault(defaults, overrides) {
	if (!overrides) return defaults;
	const result = { ...defaults };
	const source = overrides;
	for (const key in source) {
		const value = source[key];
		if (value !== void 0) result[key] = value;
	}
	return result;
}
// ../node_modules/@zag-js/utils/dist/warning.mjs
function warn(...a) {
	const m = a.length === 1 ? a[0] : a[1];
	if ((a.length === 2 ? a[0] : true) && true) console.warn(m);
}
function invariant(...a) {
	const m = a.length === 1 ? a[0] : a[1];
	if ((a.length === 2 ? a[0] : true) && true) throw new Error(m);
}
function ensure(c, m) {
	if (c == null) throw new Error(m());
}
// ../node_modules/@zag-js/core/dist/merge-props.mjs
var clsx = (...args) => args.map((str) => str?.trim?.()).filter(Boolean).join(" ");
var ownedBy = (...args) => Array.from(new Set(clsx(...args).split(/\s+/).filter(Boolean))).join(" ");
var CSS_REGEX = /((?:--)?(?:\w+-?)+)\s*:\s*([^;]*)/g;
var serialize = (style) => {
	const res = {};
	let match;
	while (match = CSS_REGEX.exec(style)) res[match[1]] = match[2];
	return res;
};
var css = (a, b) => {
	if (isString(a)) {
		if (isString(b)) return `${a};${b}`;
		a = serialize(a);
	} else if (isString(b)) b = serialize(b);
	return Object.assign({}, a ?? {}, b ?? {});
};
function mergeProps(...args) {
	let result = {};
	for (let props of args) {
		if (!props) continue;
		for (let key in result) {
			if (key.startsWith("on") && typeof result[key] === "function" && typeof props[key] === "function") {
				result[key] = callAll(props[key], result[key]);
				continue;
			}
			if (key === "className" || key === "class") {
				result[key] = clsx(result[key], props[key]);
				continue;
			}
			if (key === "style") {
				result[key] = css(result[key], props[key]);
				continue;
			}
			if (key === "data-ownedby") {
				result[key] = ownedBy(result[key], props[key]);
				continue;
			}
			result[key] = props[key] !== void 0 ? props[key] : result[key];
		}
		for (let key in props) if (result[key] === void 0) result[key] = props[key];
		const symbols = Object.getOwnPropertySymbols(props);
		for (let symbol of symbols) result[symbol] = props[symbol];
	}
	return result;
}
// ../node_modules/@zag-js/core/dist/memo.mjs
function memo$1(getDeps, fn, opts) {
	let deps = [];
	let result;
	return (depArgs) => {
		const newDeps = getDeps(depArgs);
		if (!(newDeps.length !== deps.length || newDeps.some((dep, index) => !isEqual(deps[index], dep)))) return result;
		deps = newDeps;
		result = fn(newDeps, depArgs);
		opts?.onChange?.(result);
		return result;
	};
}
// ../node_modules/@zag-js/core/dist/state.mjs
var STATE_DELIMITER = ".";
var ABSOLUTE_PREFIX = "#";
var stateIndexCache = /* @__PURE__ */ new WeakMap();
var stateIdIndexCache = /* @__PURE__ */ new WeakMap();
function joinStatePath(parts) {
	return parts.join(STATE_DELIMITER);
}
function isAbsoluteStatePath(value) {
	return value.includes(STATE_DELIMITER);
}
function isExplicitAbsoluteStatePath(value) {
	return value.startsWith(ABSOLUTE_PREFIX);
}
function isChildTarget(value) {
	return value.startsWith(STATE_DELIMITER);
}
function stripAbsolutePrefix(value) {
	return isExplicitAbsoluteStatePath(value) ? value.slice(ABSOLUTE_PREFIX.length) : value;
}
function appendStatePath(base, segment) {
	return base ? `${base}${STATE_DELIMITER}${segment}` : segment;
}
function buildStateIndex(machine) {
	const index = /* @__PURE__ */ new Map();
	const idIndex = /* @__PURE__ */ new Map();
	const visit = (basePath, state) => {
		index.set(basePath, state);
		const stateId = state.id;
		if (stateId) {
			if (idIndex.has(stateId)) invariant(`[zag-js] Duplicate state id: "${stateId}"`);
			idIndex.set(stateId, basePath);
		}
		const childStates = state.states;
		if (!childStates) return;
		ensure(state.initial, () => `[zag-js] Compound state "${basePath}" has child states but no "initial" property`);
		if (!(state.initial in childStates)) invariant(`[zag-js] Compound state "${basePath}" has initial "${String(state.initial)}" which is not a child state`);
		for (const [childKey, childState] of Object.entries(childStates)) {
			if (!childState) continue;
			const childPath = appendStatePath(basePath, childKey);
			visit(childPath, childState);
		}
	};
	for (const [topKey, topState] of Object.entries(machine.states)) {
		if (!topState) continue;
		visit(topKey, topState);
	}
	return {
		index,
		idIndex
	};
}
function ensureStateIndex(machine) {
	const cached = stateIndexCache.get(machine);
	if (cached) return cached;
	const { index, idIndex } = buildStateIndex(machine);
	stateIndexCache.set(machine, index);
	stateIdIndexCache.set(machine, idIndex);
	return index;
}
function getStatePathById(machine, stateId) {
	ensureStateIndex(machine);
	return stateIdIndexCache.get(machine)?.get(stateId);
}
function toSegments(value) {
	if (!value) return [];
	return String(value).split(STATE_DELIMITER).filter(Boolean);
}
function getStateChain(machine, state) {
	if (!state) return [];
	const stateIndex = ensureStateIndex(machine);
	const segments = toSegments(state);
	const chain = [];
	const statePath = [];
	for (const segment of segments) {
		statePath.push(segment);
		const path = joinStatePath(statePath);
		const current = stateIndex.get(path);
		if (!current) break;
		chain.push({
			path,
			state: current
		});
	}
	return chain;
}
function resolveAbsoluteStateValue(machine, value) {
	const stateIndex = ensureStateIndex(machine);
	const segments = toSegments(value);
	if (!segments.length) return value;
	const resolved = [];
	for (const segment of segments) {
		resolved.push(segment);
		const path = joinStatePath(resolved);
		if (!stateIndex.has(path)) return value;
	}
	let resolvedPath = joinStatePath(resolved);
	let current = stateIndex.get(resolvedPath);
	while (current?.initial) {
		const nextPath = `${resolvedPath}${STATE_DELIMITER}${current.initial}`;
		const nextState = stateIndex.get(nextPath);
		if (!nextState) break;
		resolvedPath = nextPath;
		current = nextState;
	}
	return resolvedPath;
}
function hasStatePath(machine, value) {
	return ensureStateIndex(machine).has(value);
}
function resolveStateValue(machine, value, source) {
	const stateValue = String(value);
	if (isExplicitAbsoluteStatePath(stateValue)) {
		const stateId = stripAbsolutePrefix(stateValue);
		const statePath = getStatePathById(machine, stateId);
		ensure(statePath, () => `[zag-js] Unknown state id: "${stateId}"`);
		return resolveAbsoluteStateValue(machine, statePath);
	}
	if (isChildTarget(stateValue) && source) return resolveAbsoluteStateValue(machine, appendStatePath(source, stateValue.slice(1)));
	if (!isAbsoluteStatePath(stateValue) && source) {
		const sourceSegments = toSegments(source);
		for (let index = sourceSegments.length - 1; index >= 1; index--) {
			const candidate = appendStatePath(sourceSegments.slice(0, index).join(STATE_DELIMITER), stateValue);
			if (hasStatePath(machine, candidate)) return resolveAbsoluteStateValue(machine, candidate);
		}
		if (hasStatePath(machine, stateValue)) return resolveAbsoluteStateValue(machine, stateValue);
	}
	return resolveAbsoluteStateValue(machine, stateValue);
}
function findTransition(machine, state, eventType) {
	const chain = getStateChain(machine, state);
	for (let index = chain.length - 1; index >= 0; index--) {
		const transition = (chain[index]?.state.on)?.[eventType];
		if (transition) return {
			transitions: transition,
			source: chain[index]?.path
		};
	}
	return {
		transitions: machine.on?.[eventType],
		source: void 0
	};
}
function getExitEnterStates(machine, prevState, nextState, reenter) {
	const prevChain = prevState ? getStateChain(machine, prevState) : [];
	const nextChain = getStateChain(machine, nextState);
	let commonIndex = 0;
	while (commonIndex < prevChain.length && commonIndex < nextChain.length && prevChain[commonIndex]?.path === nextChain[commonIndex]?.path) commonIndex += 1;
	let exiting = prevChain.slice(commonIndex).reverse();
	let entering = nextChain.slice(commonIndex);
	const sameLeaf = prevChain[prevChain.length - 1]?.path === nextChain[nextChain.length - 1]?.path;
	if (reenter && sameLeaf) {
		exiting = prevChain.slice().reverse();
		entering = nextChain;
	}
	return {
		exiting,
		entering
	};
}
function matchesState(current, value) {
	if (!current) return false;
	return current === value || current.startsWith(`${value}${STATE_DELIMITER}`);
}
function hasTag(machine, state, tag) {
	return getStateChain(machine, state).some((item) => item.state.tags?.includes(tag));
}
// ../node_modules/@zag-js/core/dist/create-machine.mjs
function createMachine(config) {
	ensureStateIndex(config);
	return config;
}
// ../node_modules/@zag-js/core/dist/types.mjs
var MachineStatus = /* @__PURE__ */ ((MachineStatus2) => {
	MachineStatus2["NotStarted"] = "Not Started";
	MachineStatus2["Started"] = "Started";
	MachineStatus2["Stopped"] = "Stopped";
	return MachineStatus2;
})(MachineStatus || {});
var INIT_STATE = "__init__";
// ../node_modules/@zag-js/core/dist/scope.mjs
function createScope(props) {
	const getRootNode = () => props.getRootNode?.() ?? document;
	const getDoc = () => getDocument(getRootNode());
	const getWin = () => getDoc().defaultView ?? window;
	const getActiveElementFn = () => getActiveElement(getRootNode());
	const getById = (id) => getRootNode().getElementById(id);
	return {
		...props,
		getRootNode,
		getDoc,
		getWin,
		getActiveElement: getActiveElementFn,
		isActiveElement,
		getById
	};
}
// ../node_modules/@zag-js/live-region/dist/index.mjs
var ID = "__live-region__";
var DEBUG_ID = "__live-region-debug__";
var DEBUG_STYLES = "position:fixed;inset-inline:0;bottom:0;z-index:2147483647;padding:12px 16px;background:black;color:white;font-size:14px;line-height:20px;text-align:center;pointer-events:none;";
function createLiveRegion(opts = {}) {
	const { level = "polite", document: doc = document, root, delay: _delay = 0, debug = false } = opts;
	const win = doc.defaultView ?? window;
	const parent = root ?? doc.body;
	function getDebugRegion() {
		if (!debug) return;
		let region = doc.getElementById(DEBUG_ID);
		if (region) return region;
		region = doc.createElement("div");
		region.id = DEBUG_ID;
		region.dataset.liveAnnouncerDebug = "true";
		region.setAttribute("aria-hidden", "true");
		region.style.cssText = DEBUG_STYLES;
		parent.appendChild(region);
		return region;
	}
	function announce(message, delay) {
		doc.getElementById(ID)?.remove();
		delay = delay ?? _delay;
		const region = doc.createElement("span");
		region.id = ID;
		region.dataset.liveAnnouncer = "true";
		const role = level !== "assertive" ? "status" : "alert";
		region.setAttribute("aria-live", level);
		region.setAttribute("role", role);
		Object.assign(region.style, {
			border: "0",
			clip: "rect(0 0 0 0)",
			height: "1px",
			margin: "-1px",
			overflow: "hidden",
			padding: "0",
			position: "absolute",
			width: "1px",
			whiteSpace: "nowrap",
			wordWrap: "normal"
		});
		parent.appendChild(region);
		win.setTimeout(() => {
			if (!region.isConnected) return;
			region.textContent = message;
			const debugRegion = getDebugRegion();
			if (debugRegion) debugRegion.textContent = message;
		}, delay);
	}
	function destroy() {
		doc.getElementById(ID)?.remove();
		doc.getElementById(DEBUG_ID)?.remove();
	}
	return {
		announce,
		destroy,
		toJSON() {
			return ID;
		}
	};
}
// ../node_modules/@zag-js/date-input/dist/utils/formatting.mjs
function toFormatterDate(date, formatter) {
	return date.toDate(formatter.resolvedOptions().timeZone);
}
function getValueAsString(value, prop) {
	return value.map((date) => {
		if (date == null) return "";
		try {
			return prop("format")(date, {
				locale: prop("locale"),
				timeZone: prop("timeZone")
			});
		} catch {
			return "";
		}
	});
}
function resolveHourCycleProp(hourCycle) {
	if (hourCycle === 12) return "h12";
	if (hourCycle === 24) return "h23";
}
function resolvePlaceholderValue(options, timeZone, granularity, value, defaultValue, calendar) {
	let placeholder = options.placeholderValue || options.defaultPlaceholderValue || value?.[0] || defaultValue?.[0] || getTodayDate(timeZone, calendar);
	placeholder = constrainSegments(placeholder, options.min, options.max);
	if (needsTimeGranularity(granularity) && !("hour" in placeholder)) placeholder = $d07e34cce18680fd$export$b21e0b124e224484(placeholder);
	return placeholder;
}
function createFormatFn(formatter) {
	return (date) => {
		if (date.calendar?.identifier === "gregory" && date.era === "BC") {
			const jsd = date.toDate("UTC");
			const prolYear = jsd.getUTCFullYear();
			const safeDate = new Date(Date.UTC(2e3, jsd.getUTCMonth(), jsd.getUTCDate()));
			return formatter.formatToParts(safeDate).map((p) => p.type === "year" ? String(prolYear) : p.value).join("");
		}
		return formatter.format(toFormatterDate(date, formatter));
	};
}
// ../node_modules/@zag-js/date-input/dist/utils/incomplete-date.mjs
function resolvedHourCycle(formatter) {
	const hc = formatter.resolvedOptions().hourCycle;
	if (hc === "h11" || hc === "h12" || hc === "h23" || hc === "h24") return hc;
	return "h23";
}
function incompleteDateHash(dv) {
	return `${dv.year}|${dv.month}|${dv.day}|${dv.hour}|${dv.dayPeriod}|${dv.minute}|${dv.second}|${dv.era}`;
}
function incompleteDateEqual(a, b) {
	return incompleteDateHash(a) === incompleteDateHash(b);
}
function initDisplayValues(value, placeholderValue, hourCycle, count) {
	const calendar = placeholderValue.calendar;
	if (value?.length) return Array.from({ length: count }, (_, i) => value[i] ? new IncompleteDate(calendar, hourCycle, value[i]) : new IncompleteDate(calendar, hourCycle));
	return Array.from({ length: count }, () => new IncompleteDate(calendar, hourCycle));
}
var IncompleteDate = class _IncompleteDate {
	constructor(calendar, hourCycle, dateValue) {
		__publicField$1(this, "calendar");
		__publicField$1(this, "era");
		__publicField$1(this, "year");
		__publicField$1(this, "month");
		__publicField$1(this, "day");
		__publicField$1(this, "hour");
		__publicField$1(this, "hourCycle");
		__publicField$1(this, "dayPeriod");
		__publicField$1(this, "minute");
		__publicField$1(this, "second");
		__publicField$1(this, "millisecond");
		__publicField$1(this, "offset");
		this.era = dateValue?.era ?? null;
		this.calendar = calendar;
		this.year = dateValue?.year ?? null;
		this.month = dateValue?.month ?? null;
		this.day = dateValue?.day ?? null;
		this.hour = dateValue?.hour ?? null;
		this.hourCycle = hourCycle;
		this.dayPeriod = null;
		this.minute = dateValue?.minute ?? null;
		this.second = dateValue?.second ?? null;
		this.millisecond = dateValue?.millisecond ?? null;
		this.offset = "offset" in (dateValue ?? {}) ? dateValue.offset : null;
		if (this.hour != null) {
			const [dayPeriod, hour] = toHourCycle(this.hour, hourCycle);
			this.dayPeriod = dayPeriod;
			this.hour = hour;
		}
	}
	copy() {
		const res = new _IncompleteDate(this.calendar, this.hourCycle);
		res.era = this.era;
		res.year = this.year;
		res.month = this.month;
		res.day = this.day;
		res.hour = this.hour;
		res.dayPeriod = this.dayPeriod;
		res.minute = this.minute;
		res.second = this.second;
		res.millisecond = this.millisecond;
		res.offset = this.offset;
		return res;
	}
	/** Rebuild this date under a new hour cycle, preserving all field values. */
	withHourCycle(nextHourCycle) {
		if (nextHourCycle === this.hourCycle) return this;
		const hour24 = this.hour != null ? fromHourCycle(this.hour, this.dayPeriod ?? 0, this.hourCycle) : null;
		const res = new _IncompleteDate(this.calendar, nextHourCycle, {
			era: this.era,
			year: this.year,
			month: this.month,
			day: this.day,
			hour: hour24,
			minute: this.minute,
			second: this.second,
			millisecond: this.millisecond
		});
		res.offset = this.offset;
		if (this.hour == null && this.dayPeriod != null && (nextHourCycle === "h12" || nextHourCycle === "h11")) res.dayPeriod = this.dayPeriod;
		return res;
	}
	/** Checks whether all the specified segments have a value. */
	isComplete(segments) {
		return segments.every((segment) => this[segment] != null);
	}
	/** Checks if the date is empty (i.e. all specified segments are null). */
	isCleared(segments) {
		return segments.every((segment) => this[segment] === null);
	}
	/** Sets the given field. */
	set(field, value, placeholder) {
		const result = this.copy();
		result[field] = value;
		if (field === "hour" && result.dayPeriod == null && "hour" in placeholder) result.dayPeriod = toHourCycle(placeholder.hour, this.hourCycle)[0];
		if (field === "year" && result.era == null) result.era = placeholder.era;
		if (field !== "second" && field !== "literal" && field !== "timeZoneName") result.offset = null;
		return result;
	}
	/** Sets the given field to null. */
	clear(field) {
		const result = this.copy();
		result[field] = null;
		if (field === "year") result.era = null;
		result.offset = null;
		return result;
	}
	/** Increments or decrements the given field. If it is null, then it is set to the placeholder value (no increment on first press). */
	cycle(field, amount, placeholder, displaySegments) {
		const res = this.copy();
		if (res[field] == null && field !== "dayPeriod" && field !== "era") {
			if (field === "hour" && "hour" in placeholder) {
				const [dayPeriod, hour] = toHourCycle(placeholder.hour, this.hourCycle);
				res.dayPeriod = dayPeriod;
				res.hour = hour;
			} else res[field] = placeholder[field];
			if (field === "year" && res.era == null) res.era = placeholder.era;
			return res;
		}
		switch (field) {
			case "era": {
				const eras = this.calendar.getEras();
				let index = eras.indexOf(res.era);
				index = cycleValue(index, amount, 0, eras.length - 1);
				res.era = eras[index];
				break;
			}
			case "year": {
				let date = new $2aaf608024c21ca1$export$99faa760c7908e4f(this.calendar, this.era ?? placeholder.era, this.year ?? placeholder.year, this.month ?? 1, this.day ?? 1);
				date = date.cycle(field, amount, { round: field === "year" });
				res.era = date.era;
				res.year = date.year;
				break;
			}
			case "month":
				res.month = cycleValue(res.month ?? 1, amount, 1, this.calendar.getMaximumMonthsInYear());
				break;
			case "day":
				res.day = cycleValue(res.day ?? 1, amount, 1, this.calendar.getMaximumDaysInMonth());
				break;
			case "hour": {
				const hasDateSegments = displaySegments.some((s) => [
					"year",
					"month",
					"day"
				].includes(s));
				if ("timeZone" in placeholder && (!hasDateSegments || res.year != null && res.month != null && res.day != null)) {
					let date = this.toValue(placeholder);
					date = date.cycle("hour", amount, {
						hourCycle: this.hourCycle === "h12" ? 12 : 24,
						round: false
					});
					const [dayPeriod, adjustedHour] = toHourCycle(date.hour, this.hourCycle);
					res.hour = adjustedHour;
					res.dayPeriod = dayPeriod;
					res.offset = date.offset;
				} else {
					const hours = res.hour ?? 0;
					const limits = this.getSegmentLimits("hour");
					res.hour = cycleValue(hours, amount, limits.minValue, limits.maxValue);
					if (res.dayPeriod == null && "hour" in placeholder) res.dayPeriod = toHourCycle(placeholder.hour, this.hourCycle)[0];
				}
				break;
			}
			case "dayPeriod":
				res.dayPeriod = cycleValue(res.dayPeriod ?? 0, amount, 0, 1);
				break;
			case "minute":
				res.minute = cycleValue(res.minute ?? 0, amount, 0, 59, true);
				break;
			case "second": res.second = cycleValue(res.second ?? 0, amount, 0, 59, true);
		}
		return res;
	}
	/** Converts the incomplete date to a full date value, using the provided value for any unset fields. */
	toValue(value) {
		if ("hour" in value) {
			let hour = this.hour;
			if (hour != null) hour = fromHourCycle(hour, this.dayPeriod ?? 0, this.hourCycle);
			else if (this.hourCycle === "h12" || this.hourCycle === "h11") hour = this.dayPeriod === 1 ? 12 : 0;
			const res = value.set({
				era: this.era ?? value.era,
				year: this.year ?? value.year,
				month: this.month ?? value.month,
				day: this.day ?? value.day,
				hour: hour ?? value.hour,
				minute: this.minute ?? value.minute,
				second: this.second ?? value.second,
				millisecond: this.millisecond ?? value.millisecond
			});
			if ("offset" in res && this.offset != null && res.offset !== this.offset) return res.add({ milliseconds: res.offset - this.offset });
			return res;
		} else return value.set({
			era: this.era ?? value.era,
			year: this.year ?? value.year,
			month: this.month ?? value.month,
			day: this.day ?? value.day
		});
	}
	getSegmentLimits(type) {
		switch (type) {
			case "era": {
				const eras = this.calendar.getEras();
				return {
					value: this.era != null ? eras.indexOf(this.era) : eras.length - 1,
					minValue: 0,
					maxValue: eras.length - 1
				};
			}
			case "year": return {
				value: this.year,
				minValue: 1,
				maxValue: 9999
			};
			case "month": return {
				value: this.month,
				minValue: 1,
				maxValue: this.calendar.getMaximumMonthsInYear()
			};
			case "day": return {
				value: this.day,
				minValue: 1,
				maxValue: this.calendar.getMaximumDaysInMonth()
			};
			case "dayPeriod": return {
				value: this.dayPeriod,
				minValue: 0,
				maxValue: 1
			};
			case "hour": {
				let minValue = 0;
				let maxValue = 23;
				if (this.hourCycle === "h12") {
					minValue = 1;
					maxValue = 12;
				} else if (this.hourCycle === "h11") {
					minValue = 0;
					maxValue = 11;
				}
				return {
					value: this.hour,
					minValue,
					maxValue
				};
			}
			case "minute": return {
				value: this.minute,
				minValue: 0,
				maxValue: 59
			};
			case "second": return {
				value: this.second,
				minValue: 0,
				maxValue: 59
			};
		}
	}
	/** Returns a debug string of all non-null fields. Returns "-" when all fields are null. */
	toString() {
		const fields = {
			era: this.era,
			year: this.year,
			month: this.month,
			day: this.day,
			hour: this.hour,
			dayPeriod: this.dayPeriod,
			minute: this.minute,
			second: this.second
		};
		const parts = Object.entries(fields).filter(([, v]) => v != null).map(([k, v]) => `${k}=${v}`);
		return parts.length > 0 ? parts.join(",") : "-";
	}
};
function cycleValue(value, amount, min, max, round = false) {
	if (round) {
		value += Math.sign(amount);
		if (value < min) value = max;
		const div = Math.abs(amount);
		if (amount > 0) value = Math.ceil(value / div) * div;
		else value = Math.floor(value / div) * div;
		if (value > max) value = min;
	} else {
		value += amount;
		if (value < min) value = max - (min - value - 1);
		else if (value > max) value = min + (value - max - 1);
	}
	return value;
}
function toHourCycle(hour, hourCycle) {
	let dayPeriod = hour >= 12 ? 1 : 0;
	switch (hourCycle) {
		case "h11":
			if (hour >= 12) hour -= 12;
			break;
		case "h12":
			if (hour === 0) hour = 12;
			else if (hour > 12) hour -= 12;
			break;
		case "h23":
			dayPeriod = null;
			break;
		case "h24":
			hour += 1;
			dayPeriod = null;
	}
	return [dayPeriod, hour];
}
function fromHourCycle(hour, dayPeriod, hourCycle) {
	switch (hourCycle) {
		case "h11":
			if (dayPeriod === 1) hour += 12;
			break;
		case "h12":
			if (hour === 12) hour = 0;
			if (dayPeriod === 1) hour += 12;
			break;
		case "h24": hour -= 1;
	}
	return hour;
}
// ../node_modules/@zag-js/date-input/dist/utils/input.mjs
function isNumberString(value) {
	return !Number.isNaN(Number.parseInt(value));
}
function updateSegmentValue(ctx, segment, input) {
	const { context, prop } = ctx;
	const type = segment.type;
	const index = context.get("activeIndex");
	const formatter = prop("formatter");
	const enteredKeys = context.get("enteredKeys");
	const placeholderValue = context.get("placeholderValue");
	let dv = getActiveDisplayValue(ctx);
	let next;
	switch (type) {
		case "dayPeriod": {
			if (!("hour" in dv.toValue(placeholderValue))) return;
			const amPmFormatter = new $12a3c853105e5a70$export$ad991b66133851cf(prop("locale"), {
				hour: "numeric",
				hour12: true
			});
			const amDate = /* @__PURE__ */ new Date();
			amDate.setHours(0);
			const pmDate = /* @__PURE__ */ new Date();
			pmDate.setHours(12);
			const am = amPmFormatter.formatToParts(amDate).find((p) => p.type === "dayPeriod")?.value || "AM";
			const pm = amPmFormatter.formatToParts(pmDate).find((p) => p.type === "dayPeriod")?.value || "PM";
			const lowerInput = input.toLowerCase();
			if (am.toLowerCase().startsWith(lowerInput)) next = dv.set("dayPeriod", 0, placeholderValue);
			else if (pm.toLowerCase().startsWith(lowerInput)) next = dv.set("dayPeriod", 1, placeholderValue);
			else break;
			setDisplayValue(ctx, index, next);
			goToNextSegment(ctx);
			break;
		}
		case "era": {
			const eras = dv.calendar.getEras();
			const eraFormatter = new $12a3c853105e5a70$export$ad991b66133851cf(prop("locale"), { era: "short" });
			const lowerInput = input.toLowerCase();
			for (let i = 0; i < eras.length; i++) {
				const eraDate = dv.toValue(placeholderValue).set({ year: 1 }).toDate(prop("timeZone"));
				const formattedEra = eraFormatter.formatToParts(eraDate).find((p) => p.type === "era")?.value;
				if (formattedEra && formattedEra.toLowerCase().startsWith(lowerInput)) {
					next = dv.set("era", eras[i], placeholderValue);
					setDisplayValue(ctx, index, next);
					goToNextSegment(ctx);
					break;
				}
			}
			break;
		}
		case "day":
		case "hour":
		case "minute":
		case "second":
		case "month":
		case "year": {
			let newValue = enteredKeys + input;
			let numberValue = Number.parseInt(newValue);
			let segmentValue = numberValue;
			let allowsZero = segment.minValue === 0;
			if (!isNumberString(input)) return;
			if (segment.type === "hour" && formatter.resolvedOptions().hour12) {
				switch (formatter.resolvedOptions().hourCycle) {
					case "h11":
						if (numberValue > 11) segmentValue = Number.parseInt(input);
						break;
					case "h12":
						allowsZero = false;
						if (numberValue > 12) segmentValue = Number.parseInt(input);
				}
				if (segment.value !== void 0 && segment.value >= 12 && numberValue > 1) numberValue += 12;
			} else if (segment.maxValue !== void 0 && numberValue > segment.maxValue) segmentValue = Number.parseInt(input);
			if (isNaN(numberValue)) return;
			const shouldSetValue = segmentValue !== 0 || allowsZero;
			if (shouldSetValue) {
				dv = dv.set(type, segmentValue, placeholderValue);
				next = dv;
				setDisplayValue(ctx, index, dv);
			}
			if (segment.maxValue !== void 0 && (Number(numberValue + "0") > segment.maxValue || newValue.length >= String(segment.maxValue).length)) {
				context.set("enteredKeys", "");
				if (shouldSetValue) goToNextSegment(ctx);
			} else context.set("enteredKeys", newValue);
			break;
		}
	}
	return next;
}
// ../node_modules/@zag-js/date-input/dist/utils/placeholders.mjs
var LOCALE_PLACEHOLDERS = {
	ach: [
		"mwaka",
		"dwe",
		"nino"
	],
	af: [
		"jjjj",
		"mm",
		"dd"
	],
	am: [
		"ዓዓዓዓ",
		"ሚሜ",
		"ቀቀ"
	],
	an: [
		"aaaa",
		"mm",
		"dd"
	],
	ar: [
		"سنة",
		"شهر",
		"يوم"
	],
	ast: [
		"aaaa",
		"mm",
		"dd"
	],
	az: [
		"iiii",
		"aa",
		"gg"
	],
	be: [
		"гггг",
		"мм",
		"дд"
	],
	bg: [
		"гггг",
		"мм",
		"дд"
	],
	bn: [
		"yyyy",
		"মিমি",
		"dd"
	],
	br: [
		"bbbb",
		"mm",
		"dd"
	],
	bs: [
		"gggg",
		"mm",
		"dd"
	],
	ca: [
		"aaaa",
		"mm",
		"dd"
	],
	cak: [
		"jjjj",
		"ii",
		"q'q'"
	],
	ckb: [
		"ساڵ",
		"مانگ",
		"ڕۆژ"
	],
	cs: [
		"rrrr",
		"mm",
		"dd"
	],
	cy: [
		"bbbb",
		"mm",
		"dd"
	],
	da: [
		"åååå",
		"mm",
		"dd"
	],
	de: [
		"jjjj",
		"mm",
		"tt"
	],
	dsb: [
		"llll",
		"mm",
		"źź"
	],
	el: [
		"εεεε",
		"μμ",
		"ηη"
	],
	en: [
		"yyyy",
		"mm",
		"dd"
	],
	eo: [
		"jjjj",
		"mm",
		"tt"
	],
	es: [
		"aaaa",
		"mm",
		"dd"
	],
	et: [
		"aaaa",
		"kk",
		"pp"
	],
	eu: [
		"uuuu",
		"hh",
		"ee"
	],
	fa: [
		"سال",
		"ماه",
		"روز"
	],
	ff: [
		"hhhh",
		"ll",
		"ññ"
	],
	fi: [
		"vvvv",
		"kk",
		"pp"
	],
	fr: [
		"aaaa",
		"mm",
		"jj"
	],
	fy: [
		"jjjj",
		"mm",
		"dd"
	],
	ga: [
		"bbbb",
		"mm",
		"ll"
	],
	gd: [
		"bbbb",
		"mm",
		"ll"
	],
	gl: [
		"aaaa",
		"mm",
		"dd"
	],
	he: [
		"שנה",
		"חודש",
		"יום"
	],
	hr: [
		"gggg",
		"mm",
		"dd"
	],
	hsb: [
		"llll",
		"mm",
		"dd"
	],
	hu: [
		"éééé",
		"hh",
		"nn"
	],
	ia: [
		"aaaa",
		"mm",
		"dd"
	],
	id: [
		"tttt",
		"bb",
		"hh"
	],
	it: [
		"aaaa",
		"mm",
		"gg"
	],
	ja: [
		"年",
		"月",
		"日"
	],
	ka: [
		"წწწწ",
		"თთ",
		"რრ"
	],
	kk: [
		"жжжж",
		"аа",
		"кк"
	],
	kn: [
		"ವವವವ",
		"ಮಿಮೀ",
		"ದಿದಿ"
	],
	ko: [
		"연도",
		"월",
		"일"
	],
	lb: [
		"jjjj",
		"mm",
		"dd"
	],
	lo: [
		"ປປປປ",
		"ດດ",
		"ວວ"
	],
	lt: [
		"mmmm",
		"mm",
		"dd"
	],
	lv: [
		"gggg",
		"mm",
		"dd"
	],
	meh: [
		"aaaa",
		"mm",
		"dd"
	],
	ml: [
		"വർഷം",
		"മാസം",
		"തീയതി"
	],
	ms: [
		"tttt",
		"mm",
		"hh"
	],
	nl: [
		"jjjj",
		"mm",
		"dd"
	],
	nn: [
		"åååå",
		"mm",
		"dd"
	],
	no: [
		"åååå",
		"mm",
		"dd"
	],
	oc: [
		"aaaa",
		"mm",
		"jj"
	],
	pl: [
		"rrrr",
		"mm",
		"dd"
	],
	pt: [
		"aaaa",
		"mm",
		"dd"
	],
	rm: [
		"oooo",
		"mm",
		"dd"
	],
	ro: [
		"aaaa",
		"ll",
		"zz"
	],
	ru: [
		"гггг",
		"мм",
		"дд"
	],
	sc: [
		"aaaa",
		"mm",
		"dd"
	],
	scn: [
		"aaaa",
		"mm",
		"jj"
	],
	sk: [
		"rrrr",
		"mm",
		"dd"
	],
	sl: [
		"llll",
		"mm",
		"dd"
	],
	sr: [
		"гггг",
		"мм",
		"дд"
	],
	sv: [
		"åååå",
		"mm",
		"dd"
	],
	szl: [
		"rrrr",
		"mm",
		"dd"
	],
	tg: [
		"сссс",
		"мм",
		"рр"
	],
	th: [
		"ปปปป",
		"ดด",
		"วว"
	],
	tr: [
		"yyyy",
		"aa",
		"gg"
	],
	uk: [
		"рррр",
		"мм",
		"дд"
	],
	"sr-Latn": [
		"gggg",
		"mm",
		"dd"
	],
	"zh-CN": [
		"年",
		"月",
		"日"
	],
	"zh-TW": [
		"年",
		"月",
		"日"
	]
};
function getLocaleLanguage(locale) {
	if (typeof Intl !== "undefined" && Intl.Locale) return new Intl.Locale(locale).language;
	return locale.split("-")[0];
}
function getLocaleScript(locale) {
	if (typeof Intl !== "undefined" && Intl.Locale) return new Intl.Locale(locale).script || void 0;
	const script = locale.split("-").find((part) => part.length === 4);
	if (!script) return void 0;
	return script.charAt(0).toUpperCase() + script.slice(1).toLowerCase();
}
function getLocalePlaceholders(locale) {
	const exact = LOCALE_PLACEHOLDERS[locale];
	if (exact) return exact;
	const lang = getLocaleLanguage(locale);
	const script = getLocaleScript(locale);
	if (script) {
		const langScriptPlaceholder = LOCALE_PLACEHOLDERS[`${lang}-${script}`];
		if (langScriptPlaceholder) return langScriptPlaceholder;
	}
	return LOCALE_PLACEHOLDERS[lang] || LOCALE_PLACEHOLDERS.en;
}
var defaultTranslations = { placeholder(locale) {
	const [year, month, day] = getLocalePlaceholders(locale);
	return {
		day,
		month,
		year,
		hour: "––",
		minute: "––",
		second: "––",
		dayPeriod: "AM/PM",
		era: "era",
		timeZoneName: "timeZone",
		weekday: "weekday",
		unknown: "unknown",
		fractionalSecond: "ff"
	};
} };
// ../node_modules/@zag-js/date-input/dist/date-input.parse.mjs
function parse$1(value) {
	if (Array.isArray(value)) return value.map((v) => parse$1(v));
	if (value instanceof Date) return new $2aaf608024c21ca1$export$99faa760c7908e4f(value.getFullYear(), value.getMonth() + 1, value.getDate());
	return $58246871e4652552$export$6b862160d295c8e(value);
}
// ../node_modules/@zag-js/date-input/dist/date-input.machine.mjs
var machine = createMachine({
	props({ props }) {
		const locale = props.locale || "en-US";
		const timeZone = props.timeZone || "UTC";
		const selectionMode = props.selectionMode || "single";
		const granularity = props.granularity || "day";
		const calendar = resolveCalendar(locale, props.createCalendar);
		const defaultValue = props.defaultValue ? props.defaultValue.map((date) => constrainSegments(toTargetCalendar(date, calendar), props.min, props.max)) : [];
		const value = props.value ? props.value.map((date) => constrainSegments(toTargetCalendar(date, calendar), props.min, props.max)) : void 0;
		const placeholderValue = resolvePlaceholderValue(props, timeZone, granularity, value, defaultValue, calendar);
		const hourCycle = resolveHourCycleProp(props.hourCycle);
		const shouldForceLeadingZeros = props.shouldForceLeadingZeros ?? false;
		const digitStyle = shouldForceLeadingZeros ? "2-digit" : "numeric";
		const firstValue = value?.[0] ?? defaultValue?.[0] ?? placeholderValue;
		const hasTimeZone = firstValue != null && "timeZone" in firstValue;
		const formatter = props.formatter ?? new $12a3c853105e5a70$export$ad991b66133851cf(locale, getFormatterOptions({
			granularity,
			digitStyle,
			hourCycle,
			timeZone,
			hasTimeZone,
			hideTimeZone: props.hideTimeZone
		}));
		const allSegments = props.allSegments ?? resolveAllSegments(formatter);
		return {
			locale,
			timeZone,
			selectionMode,
			format: createFormatFn(formatter),
			...props,
			value,
			defaultValue,
			granularity,
			shouldForceLeadingZeros,
			formatter,
			placeholderValue: typeof props.placeholderValue === "undefined" ? void 0 : placeholderValue,
			defaultPlaceholderValue: placeholderValue,
			allSegments
		};
	},
	initialState() {
		return "idle";
	},
	refs() {
		return {
			announcer: null,
			segmentToAnnounceIndex: null
		};
	},
	effects: ["setupLiveRegion"],
	context({ prop, bindable }) {
		const hourCycle = resolvedHourCycle(prop("formatter"));
		const placeholderValue = prop("defaultPlaceholderValue");
		const initialValue = prop("value") || prop("defaultValue");
		const groupCount = getGroupCount(prop("selectionMode"));
		return {
			value: bindable(() => ({
				defaultValue: prop("defaultValue"),
				value: prop("value"),
				isEqual(a, b) {
					if (a?.length !== b?.length) return false;
					const len = Math.max(a.length, b.length);
					for (let i = 0; i < len; i++) if (!isDateEqual(a[i], b[i])) return false;
					return true;
				},
				hash: (v) => v.map((date) => date?.toString() ?? "").join(","),
				onChange(value) {
					const valueAsString = getValueAsString(value, prop);
					prop("onValueChange")?.({
						value,
						valueAsString
					});
				}
			})),
			activeIndex: bindable(() => ({
				defaultValue: 0,
				sync: true
			})),
			activeSegmentIndex: bindable(() => ({
				defaultValue: -1,
				sync: true
			})),
			placeholderValue: bindable(() => ({
				defaultValue: prop("defaultPlaceholderValue"),
				isEqual: isDateEqual,
				hash: (v) => v.toString(),
				onChange(placeholderValue2) {
					prop("onPlaceholderChange")?.({
						value: prop("value") ?? [],
						valueAsString: [],
						placeholderValue: placeholderValue2
					});
				}
			})),
			displayValues: bindable(() => ({
				defaultValue: initDisplayValues(initialValue, placeholderValue ?? getTodayDate(prop("timeZone")), hourCycle, groupCount),
				isEqual: (a, b) => b != null && a.length === b.length && a.every((d, i) => incompleteDateEqual(d, b[i])),
				hash: (v) => v.map(incompleteDateHash).join("||")
			})),
			enteredKeys: bindable(() => ({
				defaultValue: "",
				sync: true
			}))
		};
	},
	computed: {
		isInteractive: ({ prop }) => !prop("disabled") && !prop("readOnly"),
		groupCount: ({ prop }) => getGroupCount(prop("selectionMode")),
		valueAsString: ({ context, prop }) => getValueAsString(context.get("value"), prop),
		segments: memo$1(({ context, prop }) => [
			context.hash("value"),
			prop("selectionMode"),
			context.hash("placeholderValue"),
			context.hash("displayValues"),
			prop("allSegments"),
			prop("timeZone"),
			prop("translations"),
			prop("granularity"),
			JSON.stringify(prop("formatter").resolvedOptions()),
			prop("locale")
		], (_deps, { context, prop, computed }) => {
			const value = context.get("value");
			const placeholderValue = context.get("placeholderValue");
			const displayValues = context.get("displayValues");
			const allSegments = prop("allSegments");
			const translations = mergeWithDefault(defaultTranslations, prop("translations"));
			const granularity = prop("granularity");
			const formatter = prop("formatter");
			const locale = prop("locale");
			const allSegmentTypes = Object.keys(allSegments);
			return Array.from({ length: computed("groupCount") }, (_, i) => {
				const displayValue = displayValues[i] ?? new IncompleteDate(placeholderValue.calendar, resolvedHourCycle(formatter));
				const committedValue = value?.[i];
				const displayDate = committedValue && displayValue.isComplete(allSegmentTypes) ? displayValue.toValue(committedValue) : displayValue.toValue(placeholderValue);
				const segmentFormatter = displayValue.era === "BC" && displayValue.calendar.identifier === "gregory" ? new $12a3c853105e5a70$export$ad991b66133851cf(locale, {
					...formatter.resolvedOptions(),
					era: "short"
				}) : formatter;
				return processSegments({
					dateValue: toFormatterDate(displayDate, segmentFormatter),
					displayValue,
					formatter: segmentFormatter,
					locale,
					translations,
					granularity
				});
			});
		})
	},
	watch({ track, context, prop, action }) {
		track([() => context.hash("value")], () => {
			action(["syncDisplayValues"]);
		});
		track([() => resolvedHourCycle(prop("formatter"))], () => {
			action(["syncDisplayValueHourCycle"]);
		});
		track([() => context.get("activeSegmentIndex")], () => {
			action(["focusActiveSegment"]);
		});
		track([() => prop("placeholderValue")?.toString()], () => {
			action(["syncPlaceholderProp"]);
		});
		track([() => prop("defaultPlaceholderValue")?.toString()], () => {
			action(["syncDefaultPlaceholderValue"]);
		});
	},
	on: {
		"VALUE.SET": { actions: ["setDateValue"] },
		"VALUE.CLEAR": { actions: [
			"clearDateValue",
			"clearDisplayValues",
			"clearEnteredKeys"
		] }
	},
	states: {
		idle: { on: { "SEGMENT.FOCUS": {
			target: "focused",
			actions: ["setActiveSegmentIndex", "invokeOnFocus"]
		} } },
		focused: { on: {
			"SEGMENT.FOCUS": [{
				guard: "isActiveSegmentFocus",
				actions: ["setActiveSegmentIndex"]
			}, { actions: ["setActiveSegmentIndex", "clearEnteredKeys"] }],
			"SEGMENT.BLUR": {
				target: "idle",
				actions: [
					"confirmPlaceholder",
					"clearEnteredKeys",
					"invokeOnBlur"
				]
			},
			"SEGMENT.INPUT": { actions: ["setSegmentValue", "announceSegmentValue"] },
			"SEGMENT.ADJUST": { actions: [
				"invokeOnSegmentAdjust",
				"clearEnteredKeys",
				"announceSegmentValue"
			] },
			"SEGMENT.ARROW_LEFT": { actions: ["setPreviousActiveSegmentIndex", "clearEnteredKeys"] },
			"SEGMENT.ARROW_RIGHT": { actions: ["setNextActiveSegmentIndex", "clearEnteredKeys"] },
			"SEGMENT.BACKSPACE": [{
				guard: "isActiveSegmentPlaceholder",
				actions: ["setPreviousActiveSegmentIndex", "clearEnteredKeys"]
			}, { actions: ["clearSegmentValue", "announceSegmentValue"] }],
			"SEGMENT.HOME": { actions: [
				"setSegmentToLowestValue",
				"clearEnteredKeys",
				"announceSegmentValue"
			] },
			"SEGMENT.END": { actions: [
				"setSegmentToHighestValue",
				"clearEnteredKeys",
				"announceSegmentValue"
			] },
			"SEGMENT.PASTE": { actions: ["setPastedValue", "clearEnteredKeys"] }
		} }
	},
	implementations: {
		effects: { setupLiveRegion({ scope, refs }) {
			const liveRegion = createLiveRegion({
				level: "assertive",
				document: scope.getDoc()
			});
			refs.set("announcer", liveRegion);
			return () => liveRegion.destroy();
		} },
		guards: {
			isActiveSegmentFocus: ({ context, event }) => {
				return (event.dateIndex == null || event.dateIndex === context.get("activeIndex")) && event.segmentIndex === context.get("activeSegmentIndex");
			},
			isActiveSegmentPlaceholder: (ctx) => {
				if (ctx.context.get("enteredKeys") !== "") return false;
				return getActiveSegment(ctx)?.isPlaceholder === true;
			}
		},
		actions: {
			invokeOnFocus({ prop }) {
				prop("onFocusChange")?.({ focused: true });
			},
			invokeOnBlur({ prop }) {
				prop("onFocusChange")?.({ focused: false });
			},
			setActiveSegmentIndex({ context, event }) {
				if (event.dateIndex != null) context.set("activeIndex", event.dateIndex);
				context.set("activeSegmentIndex", event.segmentIndex);
			},
			clearDisplayValues({ context, prop, computed }) {
				const hourCycle = resolvedHourCycle(prop("formatter"));
				const placeholderValue = context.get("placeholderValue");
				context.set("displayValues", Array.from({ length: computed("groupCount") }, () => new IncompleteDate(placeholderValue.calendar, hourCycle)));
			},
			clearEnteredKeys({ context }) {
				context.set("enteredKeys", "");
			},
			setPreviousActiveSegmentIndex(ctx) {
				goToPreviousSegment(ctx);
			},
			setNextActiveSegmentIndex(ctx) {
				goToNextSegment(ctx);
			},
			focusActiveSegment({ scope, context }) {
				raf(() => {
					getSegmentEls(scope)[context.get("activeSegmentIndex")]?.focus({ preventScroll: true });
				});
			},
			clearSegmentValue(params) {
				const { context, prop, event } = params;
				const index = context.get("activeIndex");
				const allSegments = prop("allSegments");
				const allSegmentTypes = Object.keys(allSegments);
				const placeholderValue = context.get("placeholderValue");
				const segment = getActiveSegment(params) ?? event.segment;
				const type = segment.type;
				let displayValue = getActiveDisplayValue(params);
				if (type === "dayPeriod") {
					const cleared = displayValue.clear(type);
					setDisplayValue(params, index, cleared);
					if (cleared.isCleared(allSegmentTypes)) commitClear(params, index);
					return;
				}
				if (type === "hour") {
					const cleared = displayValue.clear(type);
					setDisplayValue(params, index, cleared);
					if (cleared.isCleared(allSegmentTypes)) commitClear(params, index);
					return;
				}
				const enteredKeys = context.get("enteredKeys");
				const newValue = (enteredKeys !== "" ? enteredKeys : segment.text).slice(0, -1);
				if (newValue === "" || newValue === "0") {
					context.set("enteredKeys", "");
					const cleared = displayValue.clear(type);
					setDisplayValue(params, index, cleared);
					if (cleared.isCleared(allSegmentTypes)) commitClear(params, index);
				} else {
					context.set("enteredKeys", newValue);
					displayValue = displayValue.set(type, Number(newValue), placeholderValue);
					setDisplayValue(params, index, displayValue);
				}
			},
			invokeOnSegmentAdjust(params) {
				const { context, prop, event } = params;
				const { amount } = event;
				const type = (getActiveSegment(params) ?? event.segment).type;
				const index = context.get("activeIndex");
				const allSegments = prop("allSegments");
				const allSegmentTypes = Object.keys(allSegments);
				const placeholderValue = context.get("placeholderValue");
				const displaySegmentTypes = allSegmentTypes;
				const next = getActiveDisplayValue(params).cycle(type, amount, placeholderValue, displaySegmentTypes);
				setDisplayValue(params, index, next);
				if (next.isComplete(allSegmentTypes)) commitValue(params, index, next);
			},
			setSegmentValue(params) {
				const { event, context, refs } = params;
				const { input } = event;
				const segment = getActiveSegment(params) ?? event.segment;
				refs.set("segmentToAnnounceIndex", context.get("activeSegmentIndex"));
				const index = context.get("activeIndex");
				const displayValue = updateSegmentValue(params, segment, input);
				const allSegmentTypes = Object.keys(params.prop("allSegments"));
				if (displayValue && displayValue.isComplete(allSegmentTypes) && context.get("enteredKeys") === "") commitValue(params, index, displayValue);
			},
			setSegmentToLowestValue(params) {
				const { event, context, prop } = params;
				const segment = getActiveSegment(params) ?? event.segment;
				const index = context.get("activeIndex");
				const allSegmentTypes = Object.keys(prop("allSegments"));
				const placeholderValue = context.get("placeholderValue");
				if (segment.minValue == null) return;
				const displayValue = getActiveDisplayValue(params).set(segment.type, segment.minValue, placeholderValue);
				setDisplayValue(params, index, displayValue);
				if (displayValue.isComplete(allSegmentTypes)) commitValue(params, index, displayValue);
			},
			setSegmentToHighestValue(params) {
				const { event, context, prop } = params;
				const segment = getActiveSegment(params) ?? event.segment;
				const index = context.get("activeIndex");
				const allSegmentTypes = Object.keys(prop("allSegments"));
				const placeholderValue = context.get("placeholderValue");
				if (segment.maxValue == null) return;
				const displayValue = getActiveDisplayValue(params).set(segment.type, segment.maxValue, placeholderValue);
				setDisplayValue(params, index, displayValue);
				if (displayValue.isComplete(allSegmentTypes)) commitValue(params, index, displayValue);
			},
			setDateValue({ context, event, prop }) {
				if (!Array.isArray(event.value)) return;
				const value = event.value.map((date) => constrainSegments(date, prop("min"), prop("max")));
				context.set("value", value);
			},
			clearDateValue({ context }) {
				context.set("value", []);
			},
			setPastedValue({ context, event, prop }) {
				try {
					const constrained = constrainSegments(parse$1(event.value), prop("min"), prop("max"));
					const index = context.get("activeIndex");
					const values = Array.from(context.get("value"));
					values[index] = constrained;
					context.set("value", values);
				} catch {}
			},
			syncDisplayValues({ context, prop, computed }) {
				const value = context.get("value");
				const hourCycle = resolvedHourCycle(prop("formatter"));
				const placeholderValue = context.get("placeholderValue");
				context.set("displayValues", initDisplayValues(value?.length ? value : void 0, placeholderValue, hourCycle, computed("groupCount")));
			},
			syncDisplayValueHourCycle({ context, prop }) {
				const hourCycle = resolvedHourCycle(prop("formatter"));
				const dvs = context.get("displayValues");
				context.set("displayValues", dvs.map((dv) => dv.withHourCycle(hourCycle)));
			},
			syncDefaultPlaceholderValue({ prop, context }) {
				if (prop("placeholderValue") != null) return;
				const defaultPlaceholder = prop("defaultPlaceholderValue");
				if (!defaultPlaceholder) return;
				context.set("placeholderValue", defaultPlaceholder);
			},
			syncPlaceholderProp({ prop, context, computed }) {
				const propValue = prop("placeholderValue");
				if (propValue) {
					context.set("placeholderValue", propValue);
					const value = context.get("value");
					if (value?.length) {
						const hourCycle = resolvedHourCycle(prop("formatter"));
						context.set("displayValues", initDisplayValues(value, propValue, hourCycle, computed("groupCount")));
					}
				}
			},
			announceSegmentValue({ refs, computed, context }) {
				const announcer = refs.get("announcer");
				if (!announcer) return;
				const index = context.get("activeIndex");
				const activeSegmentIndex = context.get("activeSegmentIndex");
				const segmentIndexToUse = refs.get("segmentToAnnounceIndex") ?? activeSegmentIndex;
				refs.set("segmentToAnnounceIndex", null);
				const allSegments = computed("segments");
				const segment = allSegments[index]?.[segmentIndexToUse - getGroupOffset(allSegments, index)];
				if (!segment || segment.type === "literal") return;
				const valueText = segment.isPlaceholder ? "Empty" : segment.text;
				announcer.announce(`${getSegmentLabel(segment.type)}, ${valueText}`);
			},
			confirmPlaceholder(params) {
				const { context, prop, computed } = params;
				const allSegments = prop("allSegments");
				const allSegmentTypes = Object.keys(allSegments);
				const dateCount = computed("groupCount");
				const placeholderValue = context.get("placeholderValue");
				let values = Array.from(context.get("value"));
				let shouldUpdateValue = false;
				for (let i = 0; i < dateCount; i++) {
					const displayValue = context.get("displayValues")[i];
					if (!displayValue) continue;
					const allExceptDayPeriod = allSegmentTypes.filter((s) => s !== "dayPeriod");
					if (allSegments.dayPeriod && displayValue.isComplete(allExceptDayPeriod) && displayValue.dayPeriod == null) {
						const filled = displayValue.set("dayPeriod", placeholderValue && "hour" in placeholderValue ? placeholderValue.hour >= 12 ? 1 : 0 : 0, placeholderValue);
						setDisplayValue(params, i, filled);
						values[i] = filled.toValue(placeholderValue);
						shouldUpdateValue = true;
					} else if (displayValue.isComplete(allSegmentTypes)) {
						values[i] = displayValue.toValue(placeholderValue);
						shouldUpdateValue = true;
					}
				}
				const min = prop("min");
				const max = prop("max");
				if ((min || max) && values.length > 0) {
					values = values.map((d) => constrainSegments(d, min, max));
					shouldUpdateValue = true;
				}
				if (shouldUpdateValue) context.set("value", values);
			}
		}
	}
});
function commitValue(params, index, displayValue) {
	const { context } = params;
	const placeholderValue = context.get("placeholderValue");
	const date = displayValue.toValue(placeholderValue);
	const values = Array.from(context.get("value"));
	values[index] = date;
	context.set("value", values);
}
function commitClear(params, index) {
	const { context } = params;
	const values = context.get("value");
	if (index < values.length) context.set("value", values.slice(0, index));
}
function toTargetCalendar(date, calendar) {
	if (!calendar) return date;
	if (date.calendar.identifier === calendar.identifier) return date;
	return $d07e34cce18680fd$export$b4a036af3fc0b032(date, calendar);
}
function resolveCalendar(locale, createCalendar) {
	if (!createCalendar) return void 0;
	const calendarId = new Intl.DateTimeFormat(locale).resolvedOptions().calendar;
	if (calendarId === "gregory" || calendarId === "iso8601") return void 0;
	return createCalendar(calendarId);
}
// ../node_modules/@zag-js/types/dist/prop-types.mjs
function createNormalizer(fn) {
	return new Proxy({}, { get(_target, key) {
		if (key === "style") return (props) => {
			return fn({ style: props }).style;
		};
		return fn;
	} });
}
// ../node_modules/@ark-ui/react/dist/utils/create-context.js
function getErrorMessage(hook, provider) {
	return `${hook} returned \`undefined\`. Seems you forgot to wrap component within ${provider}`;
}
function createContext$1(options = {}) {
	const { name, strict = true, hookName = "useContext", providerName = "Provider", errorMessage, defaultValue } = options;
	const Context = createContext(defaultValue);
	Context.displayName = name;
	function useContext$1() {
		const context = useContext(Context);
		if (!context && strict) {
			const error = new Error(errorMessage ?? getErrorMessage(hookName, providerName));
			error.name = "ContextError";
			if (hasProp(Error, "captureStackTrace") && isFunction(Error.captureStackTrace)) Error.captureStackTrace(error, useContext$1);
			throw error;
		}
		return context;
	}
	return [
		Context.Provider,
		useContext$1,
		Context
	];
}
// ../node_modules/@ark-ui/react/dist/providers/environment/use-environment-context.js
var [EnvironmentContextProvider, useEnvironmentContext] = createContext$1({
	name: "EnvironmentContext",
	hookName: "useEnvironmentContext",
	providerName: "<EnvironmentProvider />",
	strict: false,
	defaultValue: {
		getRootNode: () => document,
		getDocument: () => document,
		getWindow: () => window
	}
});
// ../node_modules/@ark-ui/react/dist/providers/locale/use-locale-context.js
var [LocaleContextProvider, useLocaleContext] = createContext$1({
	name: "LocaleContext",
	hookName: "useLocaleContext",
	providerName: "<LocaleProvider />",
	strict: false,
	defaultValue: {
		dir: "ltr",
		locale: "en-US"
	}
});
// ../node_modules/@zag-js/react/dist/use-layout-effect.mjs
var useSafeLayoutEffect = typeof globalThis.document !== "undefined" ? useLayoutEffect : useEffect;
// ../node_modules/@zag-js/react/dist/bindable.mjs
function useBindable(props) {
	const initial = props().value ?? props().defaultValue;
	const eq = props().isEqual ?? Object.is;
	const [initialValue] = useState(initial);
	const [value, setValue] = useState(initialValue);
	const controlled = props().value !== void 0;
	const valueRef = useRef(value);
	valueRef.current = controlled ? props().value : value;
	const prevValue = useRef(valueRef.current);
	useSafeLayoutEffect(() => {
		prevValue.current = valueRef.current;
	}, [value, props().value]);
	const setFn = (value2) => {
		const prev = prevValue.current;
		const next = isFunction(value2) ? value2(prev) : value2;
		if (props().debug) console.log(`[bindable > ${props().debug}] setValue`, {
			next,
			prev
		});
		if (!controlled) setValue(next);
		if (!eq(next, prev)) props().onChange?.(next, prev);
	};
	function get() {
		return controlled ? props().value : value;
	}
	return {
		initial: initialValue,
		ref: valueRef,
		get,
		set(value2) {
			(props().sync ? flushSync : identity)(() => setFn(value2));
		},
		invoke(nextValue, prevValue2) {
			props().onChange?.(nextValue, prevValue2);
		},
		hash(value2) {
			return props().hash?.(value2) ?? String(value2);
		}
	};
}
useBindable.cleanup = (fn) => {
	useEffect(() => fn, []);
};
useBindable.ref = (defaultValue) => {
	const value = useRef(defaultValue);
	return {
		get: () => value.current,
		set: (next) => {
			value.current = next;
		}
	};
};
// ../node_modules/@zag-js/react/dist/stable.mjs
function useConst(factory) {
	const ref = useRef(void 0);
	if (ref.current === void 0) ref.current = factory();
	return ref.current;
}
function useStableFn(fn) {
	const ref = useRef(fn);
	ref.current = fn;
	return useMemo(() => {
		return ((...args) => ref.current(...args));
	}, []);
}
// ../node_modules/@zag-js/react/dist/refs.mjs
function useRefs(refs) {
	const ref = useRef(refs);
	return useConst(() => ({
		get(key) {
			return ref.current[key];
		},
		set(key, value) {
			ref.current[key] = value;
		}
	}));
}
// ../node_modules/@zag-js/react/dist/track.mjs
var useTrack = (deps, effect) => {
	const render = useRef(false);
	const called = useRef(false);
	useEffect(() => {
		if (render.current && called.current) return effect();
		called.current = true;
	}, [...(deps ?? []).map((d) => typeof d === "function" ? d() : d)]);
	useEffect(() => {
		render.current = true;
		return () => {
			render.current = false;
		};
	}, []);
};
// ../node_modules/@zag-js/react/dist/machine.mjs
function useMachine(machine, userProps = {}) {
	const scope = useMemo(() => {
		const { id, ids, getRootNode } = userProps;
		return createScope({
			id,
			ids,
			getRootNode
		});
	}, [userProps]);
	const debug = (...args) => {
		if (machine.debug) console.log(...args);
	};
	const prop = useProp(machine.props?.({
		props: compact(userProps),
		scope
	}) ?? userProps);
	const context = machine.context?.({
		prop,
		bindable: useBindable,
		scope,
		flush,
		getContext() {
			return ctx;
		},
		getComputed() {
			return computed;
		},
		getRefs() {
			return refs;
		},
		getEvent() {
			return getEvent();
		}
	});
	const contextRef = useLiveRef(context);
	const ctx = useConst(() => ({
		get(key) {
			return contextRef.current?.[key].ref.current;
		},
		set(key, value) {
			contextRef.current?.[key].set(value);
		},
		initial(key) {
			return contextRef.current?.[key].initial;
		},
		hash(key) {
			const current = contextRef.current?.[key].get();
			return contextRef.current?.[key].hash(current);
		}
	}));
	const effects = useRef(/* @__PURE__ */ new Map());
	const transitionRef = useRef(null);
	const previousEventRef = useRef(null);
	const eventRef = useRef({ type: "" });
	const getEvent = () => ({
		...eventRef.current,
		current() {
			return eventRef.current;
		},
		previous() {
			return previousEventRef.current;
		}
	});
	const getState = () => ({
		...state,
		matches(...values) {
			return values.some((value) => matchesState(state.ref.current, value));
		},
		hasTag(tag) {
			return hasTag(machine, state.ref.current, tag);
		}
	});
	const refs = useRefs(machine.refs?.({
		prop,
		context: ctx
	}) ?? {});
	const send = useStableFn((event) => {
		queueMicrotask(() => {
			if (statusRef.current !== MachineStatus.Started) return;
			previousEventRef.current = eventRef.current;
			eventRef.current = event;
			const currentState = getCurrentState();
			const { transitions, source } = findTransition(machine, currentState, event.type);
			const transition = choose(transitions);
			if (!transition) return;
			transitionRef.current = transition;
			const target = resolveStateValue(machine, transition.target ?? currentState, source);
			debug("transition", event.type, transition.target || currentState, `(${transition.actions})`);
			if (target !== currentState) flushSync(() => state.set(target));
			else if (transition.reenter) state.invoke(currentState, currentState);
			else action(transition.actions ?? []);
		});
	});
	const computed = useStableFn((key) => {
		ensure(machine.computed, () => `[zag-js] No computed object found on machine`);
		const fn = machine.computed[key];
		return fn({
			context: ctx,
			event: getEvent(),
			prop,
			refs,
			scope,
			computed
		});
	});
	const getParams = () => ({
		state: getState(),
		context: ctx,
		event: getEvent(),
		prop,
		send,
		action,
		guard,
		track: useTrack,
		refs,
		computed,
		flush,
		scope,
		choose
	});
	const action = (keys) => {
		const strs = isFunction(keys) ? keys(getParams()) : keys;
		if (!strs) return;
		const fns = strs.map((s) => {
			const fn = machine.implementations?.actions?.[s];
			if (!fn) warn(`[zag-js] No implementation found for action "${JSON.stringify(s)}"`);
			return fn;
		});
		for (const fn of fns) fn?.(getParams());
	};
	const guard = (str) => {
		if (isFunction(str)) return str(getParams());
		const fn = machine.implementations?.guards?.[str];
		if (!fn) warn(`[zag-js] No implementation found for guard "${JSON.stringify(str)}"`);
		return fn?.(getParams());
	};
	const effect = (keys) => {
		const strs = isFunction(keys) ? keys(getParams()) : keys;
		if (!strs) return;
		const fns = strs.map((s) => {
			const fn = machine.implementations?.effects?.[s];
			if (!fn) warn(`[zag-js] No implementation found for effect "${JSON.stringify(s)}"`);
			return fn;
		});
		const cleanups = [];
		for (const fn of fns) {
			const cleanup = fn?.(getParams());
			if (cleanup) cleanups.push(cleanup);
		}
		return () => cleanups.forEach((fn) => fn?.());
	};
	const choose = (transitions) => {
		return toArray(transitions).find((t) => {
			let result = !t.guard;
			if (isString(t.guard)) result = !!guard(t.guard);
			else if (isFunction(t.guard)) result = t.guard(getParams());
			return result;
		});
	};
	const state = useBindable(() => ({
		defaultValue: resolveStateValue(machine, machine.initialState({ prop })),
		onChange(nextState, prevState) {
			const { exiting, entering } = getExitEnterStates(machine, prevState, nextState, transitionRef.current?.reenter);
			exiting.forEach((item) => {
				effects.current.get(item.path)?.();
				effects.current.delete(item.path);
			});
			exiting.forEach((item) => {
				action(item.state?.exit);
			});
			action(transitionRef.current?.actions);
			entering.forEach((item) => {
				const cleanup = effect(item.state?.effects);
				if (cleanup) {
					const existing = effects.current.get(item.path);
					effects.current.set(item.path, existing ? callAll(existing, cleanup) : cleanup);
				}
			});
			if (prevState === "__init__") {
				action(machine.entry);
				const cleanup = effect(machine.effects);
				if (cleanup) {
					const existing = effects.current.get(INIT_STATE);
					effects.current.set(INIT_STATE, existing ? callAll(existing, cleanup) : cleanup);
				}
			}
			entering.forEach((item) => {
				action(item.state?.entry);
			});
		}
	}));
	const hydratedStateRef = useRef(void 0);
	const statusRef = useRef(MachineStatus.NotStarted);
	const getStatus = useStableFn(() => statusRef.current);
	useSafeLayoutEffect(() => {
		queueMicrotask(() => {
			const started = statusRef.current === MachineStatus.Started;
			statusRef.current = MachineStatus.Started;
			debug(started ? "rehydrating..." : "initializing...");
			const initialState = hydratedStateRef.current ?? state.initial;
			state.invoke(initialState, started ? state.get() : INIT_STATE);
		});
		const fns = effects.current;
		return () => {
			const currentState = getCurrentState();
			debug("unmounting...");
			hydratedStateRef.current = currentState;
			statusRef.current = MachineStatus.Stopped;
			fns.forEach((fn) => fn?.());
			effects.current = /* @__PURE__ */ new Map();
			transitionRef.current = null;
			queueMicrotask(() => {
				action(machine.exit);
				statusRef.current = MachineStatus.Stopped;
			});
		};
	}, []);
	const getCurrentState = () => {
		if ("ref" in state) return state.ref.current;
		return state.get();
	};
	machine.watch?.(getParams());
	return {
		state: getState(),
		send,
		context: ctx,
		prop,
		scope,
		refs,
		computed,
		event: getEvent(),
		getStatus
	};
}
function useLiveRef(value) {
	const ref = useRef(value);
	ref.current = value;
	return ref;
}
function useProp(value) {
	const ref = useLiveRef(value);
	return useStableFn(function get(key) {
		return ref.current[key];
	});
}
function flush(fn) {
	queueMicrotask(() => {
		flushSync(() => fn());
	});
}
// ../node_modules/@zag-js/react/dist/normalize-props.mjs
var normalizeProps = createNormalizer((v) => v);
// ../node_modules/@ark-ui/react/dist/components/date-input/use-date-input.js
var useDateInput = (props = {}) => {
	const id = useId();
	const { dir, locale } = useLocaleContext();
	const { getRootNode } = useEnvironmentContext();
	return connect(useMachine(machine, {
		id,
		dir,
		locale,
		getRootNode,
		...props
	}), normalizeProps);
};
// ../node_modules/@ark-ui/react/dist/components/date-input/use-date-input-context.js
var [DateInputProvider, useDateInputContext] = createContext$1({
	name: "DateInputContext",
	hookName: "useDateInputContext",
	providerName: "<DateInput.Root />"
});
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-context.js
var DateInputContext = (props) => props.children(useDateInputContext());
// ../node_modules/@ark-ui/react/dist/utils/compose-refs.js
function composeRefs(...refs) {
	return (node) => {
		const cleanUps = [];
		let hasCustomCleanUp = false;
		for (const ref of refs) if (typeof ref === "function") {
			const cb = ref(node);
			if (typeof cb === "function") {
				hasCustomCleanUp = true;
				cleanUps.push(cb);
			} else cleanUps.push(() => ref(null));
		} else if (ref) {
			ref.current = node;
			cleanUps.push(() => {
				ref.current = null;
			});
		}
		if (hasCustomCleanUp) return () => {
			for (const cleanUp of cleanUps) cleanUp();
		};
	};
}
function useComposedRefs(...refs) {
	return useCallback(composeRefs(...refs), refs);
}
// ../node_modules/@ark-ui/react/dist/components/factory.js
function getRef(element) {
	let getter = Object.getOwnPropertyDescriptor(element.props, "ref")?.get;
	let mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
	if (mayWarn) return element.ref;
	getter = Object.getOwnPropertyDescriptor(element, "ref")?.get;
	mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
	if (mayWarn) return element.props.ref;
	return element.props.ref || element.ref;
}
var REACT_LAZY_TYPE = Symbol.for("react.lazy");
function isLazyElement(children) {
	return typeof children === "object" && children !== null && "$$typeof" in children && children.$$typeof === REACT_LAZY_TYPE;
}
function getAsChild(children) {
	if (isValidElement(children)) return children;
	if (isLazyElement(children)) return Children.toArray(children).find(isValidElement);
}
var withAsChild = (Component) => {
	const Comp = memo(forwardRef((props, ref) => {
		const { asChild, children, ...restProps } = props;
		const onlyChild = asChild ? getAsChild(children) : void 0;
		const childRef = onlyChild ? getRef(onlyChild) : void 0;
		const composedRef = useComposedRefs(ref, childRef);
		if (!asChild) return createElement(Component, {
			...restProps,
			ref
		}, children);
		if (!onlyChild) return null;
		return cloneElement(onlyChild, {
			...mergeProps(restProps, onlyChild.props),
			ref: ref ? composedRef : childRef
		});
	}));
	Comp.displayName = Component.displayName || Component.name;
	return Comp;
};
var jsxFactory = () => {
	const cache = /* @__PURE__ */ new Map();
	return new Proxy(withAsChild, {
		apply(_target, _thisArg, argArray) {
			return withAsChild(argArray[0]);
		},
		get(_, element) {
			const asElement = element;
			if (!cache.has(asElement)) cache.set(asElement, withAsChild(asElement));
			return cache.get(asElement);
		}
	});
};
var ark = jsxFactory();
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-label.js
var DateInputLabel = forwardRef((props, ref) => {
	const mergedProps = mergeProps(useDateInputContext().getLabelProps(), props);
	return /* @__PURE__ */ jsx(ark.label, {
		...mergedProps,
		ref
	});
});
DateInputLabel.displayName = "DateInputLabel";
// ../node_modules/@ark-ui/react/dist/utils/create-split-props.js
var createSplitProps = () => (props, keys) => keys.reduce((previousValue, currentValue) => {
	const [target, source] = previousValue;
	const key = currentValue;
	if (source[key] !== void 0) target[key] = source[key];
	delete source[key];
	return [target, source];
}, [{}, { ...props }]);
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-root.js
var splitRootProps = createSplitProps();
var DateInputRoot = forwardRef((props, ref) => {
	const [useDateInputProps, localProps] = splitRootProps(props, [
		"dir",
		"disabled",
		"getRootNode",
		"id",
		"ids",
		"invalid",
		"locale",
		"max",
		"min",
		"name",
		"form",
		"onFocusChange",
		"onPlaceholderChange",
		"onValueChange",
		"readOnly",
		"required",
		"selectionMode",
		"timeZone",
		"hideTimeZone",
		"translations",
		"value",
		"defaultValue",
		"hourCycle",
		"granularity",
		"shouldForceLeadingZeros",
		"allSegments",
		"formatter",
		"placeholderValue",
		"defaultPlaceholderValue",
		"format",
		"createCalendar",
		"isDateUnavailable"
	]);
	const dateInput = useDateInput(useDateInputProps);
	const mergedProps = mergeProps(dateInput.getRootProps(), localProps);
	return /* @__PURE__ */ jsx(DateInputProvider, {
		value: dateInput,
		children: /* @__PURE__ */ jsx(ark.div, {
			...mergedProps,
			ref
		})
	});
});
DateInputRoot.displayName = "DateInputRoot";
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-root-provider.js
var splitRootProviderProps = createSplitProps();
var DateInputRootProvider = forwardRef((props, ref) => {
	const [{ value: dateInput }, localProps] = splitRootProviderProps(props, ["value"]);
	const mergedProps = mergeProps(dateInput.getRootProps(), localProps);
	return /* @__PURE__ */ jsx(DateInputProvider, {
		value: dateInput,
		children: /* @__PURE__ */ jsx(ark.div, {
			...mergedProps,
			ref
		})
	});
});
DateInputRootProvider.displayName = "DateInputRootProvider";
// ../node_modules/@ark-ui/react/dist/components/date-input/use-date-input-segment-group-props-context.js
var [DateInputSegmentGroupPropsProvider, useDateInputSegmentGroupPropsContext] = createContext$1({
	name: "DateInputSegmentGroupPropsContext",
	hookName: "useDateInputSegmentGroupPropsContext",
	providerName: "<DateInput.SegmentGroup />",
	strict: true
});
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-segment.js
var splitSegmentProps = createSplitProps();
var DateInputSegment = forwardRef((props, ref) => {
	const [segmentProps, localProps] = splitSegmentProps(props, ["segment"]);
	const segmentGroupProps = useDateInputSegmentGroupPropsContext();
	const mergedProps = mergeProps(useDateInputContext().getSegmentProps({
		...segmentProps,
		index: segmentGroupProps.index
	}), localProps);
	return /* @__PURE__ */ jsx(ark.span, {
		...mergedProps,
		ref,
		children: segmentProps.segment.text
	});
});
DateInputSegment.displayName = "DateInputSegment";
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-segment-group.js
var splitSegmentGroupProps = createSplitProps();
var DateInputSegmentGroup = forwardRef((props, ref) => {
	const [segmentGroupProps, localProps] = splitSegmentGroupProps(props, ["index"]);
	const mergedProps = mergeProps(useDateInputContext().getSegmentGroupProps(segmentGroupProps), localProps);
	return /* @__PURE__ */ jsx(DateInputSegmentGroupPropsProvider, {
		value: segmentGroupProps,
		children: /* @__PURE__ */ jsx(ark.div, {
			...mergedProps,
			ref
		})
	});
});
DateInputSegmentGroup.displayName = "DateInputSegmentGroup";
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-hidden-input.js
var splitHiddenInputProps = createSplitProps();
var DateInputHiddenInput = forwardRef((props, ref) => {
	const [hiddenInputProps, localProps] = splitHiddenInputProps(props, ["index", "name"]);
	const mergedProps = mergeProps(useDateInputContext().getHiddenInputProps(hiddenInputProps), localProps);
	return /* @__PURE__ */ jsx(ark.input, {
		...mergedProps,
		ref
	});
});
DateInputHiddenInput.displayName = "DateInputHiddenInput";
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-control.js
var DateInputControl = forwardRef((props, ref) => {
	const mergedProps = mergeProps(useDateInputContext().getControlProps(), props);
	return /* @__PURE__ */ jsx(ark.div, {
		...mergedProps,
		ref
	});
});
DateInputControl.displayName = "DateInputControl";
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input-segment-context.js
var DateInputSegmentContext = (props) => {
	const dateInput = useDateInputContext();
	const segmentGroupProps = useDateInputSegmentGroupPropsContext();
	return dateInput.getSegments(segmentGroupProps).map((segment, index) => /* @__PURE__ */ jsx(Fragment, { children: props.children(segment) }, `${segment.type}-${index}`));
};
// ../node_modules/@ark-ui/react/dist/_virtual/_rolldown/runtime.js
var __defProp = Object.defineProperty;
var __exportAll = (all, no_symbols) => {
	let target = {};
	for (var name in all) __defProp(target, name, {
		get: all[name],
		enumerable: true
	});
	if (!no_symbols) __defProp(target, Symbol.toStringTag, { value: "Module" });
	return target;
};
// ../node_modules/@ark-ui/react/dist/components/date-input/date-input.js
var date_input_exports = /* @__PURE__ */ __exportAll({
	Context: () => DateInputContext,
	Control: () => DateInputControl,
	HiddenInput: () => DateInputHiddenInput,
	Label: () => DateInputLabel,
	Root: () => DateInputRoot,
	RootProvider: () => DateInputRootProvider,
	Segment: () => DateInputSegment,
	SegmentContext: () => DateInputSegmentContext,
	SegmentGroup: () => DateInputSegmentGroup
});
// src/components/inputs/time-field.tsx
const TIME_ZONE = "UTC";
const StyledControl = chakra(date_input_exports.Control, { base: {
	display: "inline-flex",
	alignItems: "center",
	height: "10",
	paddingX: "3",
	borderWidth: "1px",
	borderColor: "border",
	borderRadius: "md",
	background: "bg",
	fontVariantNumeric: "tabular-nums",
	_focusWithin: {
		borderColor: "brand.500",
		boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
	}
} });
const StyledSegment = chakra(date_input_exports.Segment, { base: {
	paddingX: "0.5",
	borderRadius: "sm",
	outline: "none",
	_focus: {
		background: "brand.solid",
		color: "brand.contrast"
	},
	_placeholderShown: { color: "fg.muted" }
} });
const toValue = (value) => {
	if (!value) return [];
	const [hours, minutes] = value.split(":");
	return [new $2aaf608024c21ca1$export$ca871e8dbb80966f(2e3, 1, 1, Number(hours) || 0, Number(minutes) || 0)];
};
const toString = (hours, minutes) => `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
const TimeField = ({ name, value, onChange, onBlur, disabled, locale = "fr-FR" }) => {
	const formatter = useMemo(() => new $12a3c853105e5a70$export$ad991b66133851cf(locale, {
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
		timeZone: TIME_ZONE
	}), [locale]);
	return /* @__PURE__ */ jsxs(date_input_exports.Root, {
		disabled,
		selectionMode: "single",
		granularity: "minute",
		hourCycle: 24,
		locale,
		timeZone: TIME_ZONE,
		formatter,
		value: toValue(value),
		onValueChange: (details) => {
			const time = details.value[0];
			onChange?.(time ? toString(time.hour, time.minute) : "");
		},
		onFocusChange: (details) => {
			if (!details.focused) onBlur?.();
		},
		children: [/* @__PURE__ */ jsx(StyledControl, { children: /* @__PURE__ */ jsx(date_input_exports.SegmentGroup, { children: /* @__PURE__ */ jsx(date_input_exports.SegmentContext, { children: (segment) => /* @__PURE__ */ jsx(StyledSegment, { segment }) }) }) }), /* @__PURE__ */ jsx(date_input_exports.HiddenInput, { name })]
	});
};
// src/components/inputs/time-input/constants.ts
const HOURS = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, index) => index.toString().padStart(2, "0"));
const TIME_PATTERN = /^\d{2}:\d{2}$/;
// src/components/inputs/time-input/time-column.tsx
const TimeColumn = forwardRef(({ label, items, value, onScrollSelect, onClickSelect }, ref) => {
	const viewportRef = useRef(null);
	const isProgrammaticScroll = useRef(false);
	const scrollDebounceTimer = useRef(null);
	useEffect(() => () => clearTimeout(scrollDebounceTimer.current ?? void 0), []);
	useImperativeHandle(ref, () => ({ scrollToValue(targetValue, behavior = "smooth") {
		const container = viewportRef.current;
		if (!container) return;
		const target = container.querySelector(`[data-value="${targetValue}"]`);
		if (!target) return;
		if (Math.abs(container.scrollTop - target.offsetTop) <= 1) return;
		isProgrammaticScroll.current = true;
		container.scrollTo({
			top: target.offsetTop,
			behavior
		});
	} }));
	const handleScroll = (e) => {
		if (isProgrammaticScroll.current) {
			isProgrammaticScroll.current = false;
			return;
		}
		const container = e.currentTarget;
		if (scrollDebounceTimer.current) clearTimeout(scrollDebounceTimer.current);
		scrollDebounceTimer.current = setTimeout(() => {
			const snappedTop = Math.round(container.scrollTop / 32) * 32;
			if (Math.abs(container.scrollTop - snappedTop) > .5) container.scrollTo({
				top: snappedTop,
				behavior: "instant"
			});
			const snappedValue = items[Math.round(container.scrollTop / 32)];
			if (snappedValue && snappedValue !== value) onScrollSelect(snappedValue);
		}, 150);
	};
	return /* @__PURE__ */ jsxs(Flex$1, {
		flexDirection: "column",
		gap: 2,
		align: "center",
		children: [/* @__PURE__ */ jsx(Text$1, {
			fontSize: "2xs",
			fontWeight: "bold",
			color: "fg.muted",
			textTransform: "uppercase",
			children: label
		}), /* @__PURE__ */ jsxs(Box$1, {
			position: "relative",
			children: [/* @__PURE__ */ jsx(Box$1, {
				position: "absolute",
				top: "0",
				left: "0",
				right: "0",
				height: `32px`,
				background: "gray.subtle",
				borderRadius: "sm",
				pointerEvents: "none"
			}), /* @__PURE__ */ jsx(ScrollArea.Root, {
				height: `130px`,
				width: "64px",
				style: {
					maskImage: `linear-gradient(to bottom, black 32px, transparent 100%)`,
					WebkitMaskImage: `linear-gradient(to bottom, black 32px, transparent 100%)`
				},
				children: /* @__PURE__ */ jsx(ScrollArea.Viewport, {
					ref: viewportRef,
					role: "listbox",
					"aria-label": label,
					onScroll: handleScroll,
					style: { scrollSnapType: "y mandatory" },
					children: /* @__PURE__ */ jsx(Flex$1, {
						flexDirection: "column",
						pb: `98px`,
						children: items.map((item) => /* @__PURE__ */ jsx(Button, {
							"data-value": item,
							role: "option",
							"aria-selected": value === item,
							colorPalette: "gray",
							variant: "ghost",
							height: `32px`,
							minHeight: `32px`,
							style: { scrollSnapAlign: "start" },
							fontSize: "sm",
							fontWeight: value === item ? "bold" : "normal",
							color: value === item ? "brand.solid" : "fg.muted",
							onClick: () => onClickSelect(item),
							children: item
						}, item))
					})
				})
			})]
		})]
	});
});
TimeColumn.displayName = "TimeColumn";
// src/components/inputs/time-input/utils.ts
function parseTime(raw) {
	const [hStr, mStr] = raw.includes(":") ? raw.split(":") : ["00", "00"];
	const hours = Number.parseInt(hStr || "00", 10);
	const minutes = Number.parseInt(mStr || "00", 10);
	return {
		hours: Number.isNaN(hours) ? 0 : hours,
		minutes: Number.isNaN(minutes) ? 0 : minutes
	};
}
function formatTime(hours, minutes) {
	return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}
function incrementTimeUnit(time, unit, delta) {
	const { hours, minutes } = parseTime(time);
	return unit === "hour" ? formatTime((hours + delta + 24) % 24, minutes) : formatTime(hours, (minutes + delta + 60) % 60);
}
// src/components/inputs/time-input/use-time-state.ts
function assignRef(ref, node) {
	if (typeof ref === "function") ref(node);
	else if (ref) ref.current = node;
}
function useTimeState({ defaultValue = "", onChange, onFocus, ref }) {
	const [time, setTime] = useState(defaultValue);
	const inputRef = useRef(null);
	const changeSource = useRef("input");
	const hourColumnRef = useRef(null);
	const minuteColumnRef = useRef(null);
	const maskRef = useMaskInput({
		mask: "datetime",
		options: {
			inputFormat: "HH:mm",
			showMaskOnFocus: false,
			showMaskOnHover: false
		}
	});
	const setInputRef = (node) => {
		maskRef(node);
		inputRef.current = node;
		if (node && defaultValue) node.value = defaultValue;
		assignRef(ref, node);
	};
	const handleInputChange = (raw) => {
		if (!TIME_PATTERN.test(raw)) return;
		changeSource.current = "input";
		setTime(raw);
		onChange?.(raw);
	};
	const updateTime = (next) => {
		setTime(next);
		onChange?.(next);
		if (inputRef.current) inputRef.current.value = next;
	};
	const handleKeyDown = (event) => {
		const input = event.currentTarget;
		const cursorIsOnHours = (input.selectionStart ?? 0) <= 2;
		if (event.key === "ArrowRight" && cursorIsOnHours) {
			event.preventDefault();
			input.setSelectionRange(3, 5);
			return;
		}
		if (event.key === "ArrowLeft" && !cursorIsOnHours) {
			event.preventDefault();
			input.setSelectionRange(0, 2);
			return;
		}
		if (event.key === "ArrowUp" || event.key === "ArrowDown") {
			event.preventDefault();
			const delta = event.key === "ArrowUp" ? -1 : 1;
			const next = incrementTimeUnit(time, cursorIsOnHours ? "hour" : "minute", delta);
			changeSource.current = "input";
			updateTime(next);
			requestAnimationFrame(() => {
				input.setSelectionRange(cursorIsOnHours ? 0 : 3, cursorIsOnHours ? 2 : 5);
			});
		}
	};
	useEffect(() => {
		if (changeSource.current === "scroll") return;
		const { hours, minutes } = parseTime(time);
		hourColumnRef.current?.scrollToValue(hours.toString().padStart(2, "0"));
		minuteColumnRef.current?.scrollToValue(minutes.toString().padStart(2, "0"));
	}, [time]);
	const handlePopoverOpenChange = (details) => {
		if (!details.open) return;
		const { hours, minutes } = parseTime(time);
		requestAnimationFrame(() => {
			hourColumnRef.current?.scrollToValue(hours.toString().padStart(2, "0"), "instant");
			minuteColumnRef.current?.scrollToValue(minutes.toString().padStart(2, "0"), "instant");
		});
	};
	const handleFocus = (event) => {
		if (!event.target.value) {
			updateTime("00:00");
			event.target.value = "00:00";
		}
		requestAnimationFrame(() => event.target.setSelectionRange(0, 2));
		onFocus?.(event);
	};
	const handleColumnChange = ({ unit, value, source }) => {
		changeSource.current = source;
		const [hours, minutes] = time.split(":");
		const next = unit === "hour" ? `${value}:${minutes ?? "00"}` : `${hours ?? "00"}:${value}`;
		updateTime(next);
	};
	return {
		time,
		hourColumnRef,
		minuteColumnRef,
		setInputRef,
		handleInputChange,
		handleFocus,
		handleKeyDown,
		handlePopoverOpenChange,
		handleColumnChange
	};
}
// src/components/inputs/time-input/time-input.tsx
const TimeInput = forwardRef(({ labels, defaultValue = "", onChange, ...inputProps }, ref) => {
	const { time, hourColumnRef, minuteColumnRef, setInputRef, handleInputChange, handleFocus, handleKeyDown, handlePopoverOpenChange, handleColumnChange } = useTimeState({
		defaultValue,
		onChange,
		onFocus: inputProps.onFocus,
		ref
	});
	const handleHourScroll = (value) => {
		handleColumnChange({
			unit: "hour",
			value,
			source: "scroll"
		});
	};
	const handleHourClick = (value) => {
		handleColumnChange({
			unit: "hour",
			value,
			source: "click"
		});
	};
	const handleMinuteScroll = (value) => {
		handleColumnChange({
			unit: "minute",
			value,
			source: "scroll"
		});
	};
	const handleMinuteClick = (value) => {
		handleColumnChange({
			unit: "minute",
			value,
			source: "click"
		});
	};
	return /* @__PURE__ */ jsxs(Popover$1.Root, {
		positioning: { placement: "bottom-start" },
		autoFocus: false,
		onOpenChange: handlePopoverOpenChange,
		children: [/* @__PURE__ */ jsx(Popover$1.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsx(InputGroup, {
				endElement: /* @__PURE__ */ jsx(ClockIcon, { size: "sm" }),
				children: /* @__PURE__ */ jsx(Input, {
					...inputProps,
					ref: setInputRef,
					type: "text",
					placeholder: "--:--",
					autoComplete: "off",
					onKeyDown: handleKeyDown,
					onChange: (e) => handleInputChange(e.target.value),
					onFocus: handleFocus
				})
			})
		}), /* @__PURE__ */ jsx(Portal$1, { children: /* @__PURE__ */ jsx(Popover$1.Positioner, { children: /* @__PURE__ */ jsx(Popover$1.Content, {
			width: "auto",
			onPointerDown: (e) => e.preventDefault(),
			children: /* @__PURE__ */ jsx(Popover$1.Body, {
				padding: 2,
				children: /* @__PURE__ */ jsxs(Flex$1, {
					flexDirection: "row",
					gap: 1,
					alignItems: "flex-start",
					children: [/* @__PURE__ */ jsx(TimeColumn, {
						ref: hourColumnRef,
						label: labels.hours,
						items: HOURS,
						value: time.split(":")[0] ?? "",
						onScrollSelect: handleHourScroll,
						onClickSelect: handleHourClick
					}), /* @__PURE__ */ jsx(TimeColumn, {
						ref: minuteColumnRef,
						label: labels.minutes,
						items: MINUTES,
						value: time.split(":")[1] ?? "",
						onScrollSelect: handleMinuteScroll,
						onClickSelect: handleMinuteClick
					})]
				})
			})
		}) }) })]
	});
});
TimeInput.displayName = "TimeInput";
// src/components/calendar/period-label.ts
function getPeriodLabel(date, view, locale) {
	const options = { locale: DATE_FNS_LOCALES[locale] };
	if (view === "day") return format(date, "EEEE d MMMM y", options);
	if (view === "month") return format(date, "MMMM y", options);
	const start = startOfWeek(date, options);
	const end = endOfWeek(date, options);
	if (start.getFullYear() !== end.getFullYear()) return `${format(start, "d MMM y", options)} – ${format(end, "d MMM y", options)}`;
	if (start.getMonth() !== end.getMonth()) return `${format(start, "d MMM", options)} – ${format(end, "d MMM y", options)}`;
	return `${format(start, "d", options)} – ${format(end, "d MMMM y", options)}`;
}
function formatPeriodLabel(date, view, locale) {
	const label = getPeriodLabel(date, view, locale);
	return label.charAt(0).toUpperCase() + label.slice(1);
}
/** Longest label of the year, to give the period button a stable width */
function getLongestPeriodLabel(year, view, locale) {
	let longest = "";
	for (const day of eachDayOfInterval({
		start: new Date(year, 0, 1),
		end: new Date(year, 11, 31)
	})) {
		const label = formatPeriodLabel(day, view, locale);
		if (label.length > longest.length) longest = label;
	}
	return longest;
}
// src/components/calendar/period-picker.tsx
const INTL_LOCALES = {
	fr: "fr-FR",
	es: "es-ES"
};
/** Label of the shown period, opening a date picker */
function PeriodPicker({ date, onDateChange, view, locale }) {
	const year = date.getFullYear();
	const longestLabel = useMemo(() => getLongestPeriodLabel(year, view, locale), [
		year,
		view,
		locale
	]);
	return /* @__PURE__ */ jsxs(DatePicker$1.Root, {
		width: "auto",
		locale: INTL_LOCALES[locale],
		value: [parseDate(format(date, "yyyy-MM-dd"))],
		onValueChange: ({ value: [picked] }) => {
			if (picked) onDateChange(new Date(picked.year, picked.month - 1, picked.day));
		},
		children: [/* @__PURE__ */ jsx(DatePicker$1.Control, {
			width: "auto",
			children: /* @__PURE__ */ jsx(DatePicker$1.Trigger, {
				asChild: true,
				children: /* @__PURE__ */ jsx(Button, {
					colorPalette: "gray",
					variant: "subtle",
					size: "xs",
					width: "auto",
					height: "8",
					paddingX: "2",
					fontWeight: "semibold",
					children: /* @__PURE__ */ jsxs(Grid, {
						justifyItems: "center",
						children: [/* @__PURE__ */ jsx(Box, {
							gridArea: "1 / 1",
							children: formatPeriodLabel(date, view, locale)
						}), /* @__PURE__ */ jsx(Box, {
							gridArea: "1 / 1",
							visibility: "hidden",
							"aria-hidden": true,
							children: longestLabel
						})]
					})
				})
			})
		}), /* @__PURE__ */ jsx(Portal$1, { children: /* @__PURE__ */ jsx(DatePicker$1.Positioner, { children: /* @__PURE__ */ jsx(DatePicker$1.Content, {}) }) })]
	});
}
// src/components/tooltip.tsx
const Tooltip$1 = forwardRef(function Tooltip$2(props, ref) {
	const { showArrow, children, disabled, portalled = true, content, contentProps, portalRef, ...rest } = props;
	if (disabled) return children;
	return /* @__PURE__ */ jsxs(Tooltip.Root, {
		...rest,
		children: [/* @__PURE__ */ jsx(Tooltip.Trigger, {
			asChild: true,
			children
		}), /* @__PURE__ */ jsx(Portal, {
			disabled: !portalled,
			container: portalRef,
			children: /* @__PURE__ */ jsx(Tooltip.Positioner, { children: /* @__PURE__ */ jsxs(Tooltip.Content, {
				ref,
				...contentProps,
				children: [showArrow && /* @__PURE__ */ jsx(Tooltip.Arrow, { children: /* @__PURE__ */ jsx(Tooltip.ArrowTip, {}) }), content]
			}) })
		})]
	});
});
// src/components/calendar/toolbar-segment.tsx
function ToolbarSegment({ value, items, labels, icons, onChange }) {
	return /* @__PURE__ */ jsxs(SegmentGroup.Root, {
		size: "sm",
		value,
		css: {
			"--segment-indicator-bg": "colors.brand.solid",
			"& [data-part=item][data-state=checked]": { color: "brand.contrast" }
		},
		onValueChange: ({ value: next }) => {
			if (next) onChange(next);
		},
		children: [/* @__PURE__ */ jsx(SegmentGroup.Indicator, {}), /* @__PURE__ */ jsx(SegmentGroup.Items, {
			paddingInline: icons ? "2" : void 0,
			items: items.map((item) => ({
				value: item,
				label: icons ? /* @__PURE__ */ jsx(Tooltip$1, {
					content: labels[item],
					openDelay: 200,
					closeDelay: 100,
					children: /* @__PURE__ */ jsxs(Flex, {
						as: "span",
						alignItems: "center",
						children: [icons[item], /* @__PURE__ */ jsx(VisuallyHidden, { children: labels[item] })]
					})
				}) : labels[item]
			}))
		})]
	});
}
// src/components/calendar/calendar-toolbar.tsx
const SHIFT_BY_VIEW = {
	day: addDays,
	week: addWeeks,
	month: addMonths
};
const DISPLAY_ICONS = {
	calendar: /* @__PURE__ */ jsx(CalendarDaysIcon, { size: "sm" }),
	list: /* @__PURE__ */ jsx(ListIcon, { size: "sm" })
};
function CalendarToolbar({ date, onDateChange, view, onViewChange, views, display, onDisplayChange, locale, labels }) {
	const shift = SHIFT_BY_VIEW[view];
	return /* @__PURE__ */ jsxs(Flex, {
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: "2",
		padding: "3",
		borderBottomWidth: "1px",
		borderColor: "border",
		children: [/* @__PURE__ */ jsxs(Flex, {
			alignItems: "center",
			gap: "2",
			children: [/* @__PURE__ */ jsxs(Flex, {
				alignItems: "center",
				gap: "1",
				children: [
					/* @__PURE__ */ jsx(IconButton$1, {
						"aria-label": labels.previous,
						colorPalette: "gray",
						variant: "subtle",
						size: "xs",
						onClick: () => onDateChange(shift(date, -1)),
						children: /* @__PURE__ */ jsx(ChevronLeftIcon, {})
					}),
					/* @__PURE__ */ jsx(PeriodPicker, {
						date,
						onDateChange,
						view,
						locale
					}),
					/* @__PURE__ */ jsx(IconButton$1, {
						"aria-label": labels.next,
						colorPalette: "gray",
						variant: "subtle",
						size: "xs",
						onClick: () => onDateChange(shift(date, 1)),
						children: /* @__PURE__ */ jsx(ChevronRightIcon, {})
					})
				]
			}), /* @__PURE__ */ jsx(Button, {
				colorPalette: "gray",
				variant: "subtle",
				size: "xs",
				onClick: () => onDateChange(/* @__PURE__ */ new Date()),
				children: labels.today
			})]
		}), /* @__PURE__ */ jsxs(Flex, {
			alignItems: "center",
			gap: "2",
			flexWrap: "wrap",
			children: [onDisplayChange && /* @__PURE__ */ jsx(ToolbarSegment, {
				value: display,
				items: ["calendar", "list"],
				labels: labels.displays,
				icons: DISPLAY_ICONS,
				onChange: onDisplayChange
			}), views.length > 1 && /* @__PURE__ */ jsx(ToolbarSegment, {
				value: view,
				items: views,
				labels: labels.views,
				onChange: onViewChange
			})]
		})]
	});
}
const MIN_EVENT_MINUTES = 20;
/**
* End used by rbc to lay events out in week and day views. An event too short
* to show one line is laid out as if it lasted that long: rbc then puts the
* next event beside it instead of under it, as Google Calendar does. It never
* spills over the next day
*/
function getVisualEnd(event) {
	const minimumEnd = addMinutes(event.start, MIN_EVENT_MINUTES);
	if (event.end >= minimumEnd) return event.end;
	return min([minimumEnd, endOfDay(event.start)]);
}
// src/components/calendar/calendar.styles.ts
/** Overrides of react-big-calendar's own CSS classes */
const calendarCss = {
	"& .rbc-month-view, & .rbc-time-view": { border: "none" },
	"& .rbc-month-row + .rbc-month-row, & .rbc-day-bg + .rbc-day-bg, & .rbc-timeslot-group, & .rbc-day-slot .rbc-events-container": { borderColor: "border" },
	"& .rbc-header": {
		border: "none",
		borderBottom: "1px solid",
		borderBottomColor: "border",
		fontWeight: "normal"
	},
	"& .rbc-header + .rbc-header": {
		borderLeft: "1px solid",
		borderLeftColor: "border"
	},
	"& .rbc-time-content > * + * > *": { borderLeft: "none" },
	"& .rbc-time-content > .rbc-day-slot": {
		borderLeft: "1px solid",
		borderLeftColor: "border"
	},
	"& .rbc-time-header-gutter": {
		borderBottom: "1px solid",
		borderBottomColor: "border"
	},
	"& .rbc-time-header:has(.rbc-time-header-cell-single-day) .rbc-time-header-gutter": { borderBottom: "none" },
	"& .rbc-time-gutter .rbc-label": {
		textStyle: "xs",
		fontWeight: "semibold",
		color: "fg.muted"
	},
	"& .rbc-header > a": { textDecoration: "none" },
	"& .rbc-today": { background: "transparent" },
	"& .rbc-date-cell": {
		textAlign: "center",
		paddingRight: "0",
		paddingY: "1"
	},
	"& .rbc-header.rbc-today": { color: "brand.solid" },
	"& .rbc-off-range-bg": { background: "bg.subtle" },
	"& .rbc-time-header.rbc-overflowing": { borderRight: "none" },
	"& .rbc-time-header-content": { borderLeftColor: "border" },
	"& .rbc-time-content": { borderTop: "none" },
	"& .rbc-day-slot .rbc-time-slot": { border: "none" },
	"& .rbc-timeslot-group": { minHeight: `60px` },
	"& .rbc-allday-cell": { display: "none" },
	"& .rbc-current-time-indicator": {
		background: "brand.solid",
		height: "2px",
		_before: {
			content: "\"\"",
			position: "absolute",
			top: "-3px",
			left: "-4px",
			width: "8px",
			height: "8px",
			borderRadius: "full",
			background: "brand.solid"
		}
	},
	"& .rbc-button-link": {
		_hover: { color: "brand.solid" },
		_focusVisible: { color: "brand.solid" }
	},
	"& .rbc-show-more": {
		width: "full",
		paddingX: "1.5",
		paddingY: "0.5",
		borderRadius: "lg",
		textAlign: "start",
		color: "fg",
		background: "transparent",
		textStyle: "xs",
		fontWeight: "bold",
		_hover: {
			background: "bg.muted",
			color: "fg"
		},
		_focusVisible: {
			outline: "2px solid",
			outlineColor: "brand.focusRing"
		}
	},
	"& .rbc-event, & .rbc-day-slot .rbc-background-event": {
		background: "transparent",
		padding: "0",
		border: "none",
		borderRadius: "lg",
		outline: "none",
		_focusVisible: {
			outline: "2px solid",
			outlineColor: "brand.focusRing"
		}
	},
	"& .rbc-day-slot .rbc-event": { minHeight: "0" },
	"& .rbc-event-label": { display: "none" },
	"& .rbc-event-content": { height: "full" },
	"& .rbc-row-segment": { paddingX: "2px" }
};
// src/components/calendar/external-store.ts
/**
* State read through useSyncExternalStore: only the subscribed components
* re-render when it changes, never the calendar holding the store
*/
function createExternalStore(initialState) {
	let state = initialState;
	const listeners = /* @__PURE__ */ new Set();
	return {
		get: () => state,
		set(next) {
			if (next !== state) {
				state = next;
				listeners.forEach((listener) => listener());
			}
		},
		subscribe(listener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		}
	};
}
// src/components/calendar/list-details-dialog.tsx
function createListDetailsStore() {
	const store = createExternalStore({
		event: null,
		isOpen: false
	});
	return {
		get: store.get,
		subscribe: store.subscribe,
		open(event) {
			store.set({
				event,
				isOpen: true
			});
		},
		close() {
			const state = store.get();
			if (state.isOpen) store.set({
				...state,
				isOpen: false
			});
		}
	};
}
function ListDetailsDialog({ store, render }) {
	const { event, isOpen } = useSyncExternalStore(store.subscribe, store.get);
	return /* @__PURE__ */ jsx(Dialog.Root, {
		placement: "center",
		open: isOpen,
		onOpenChange: ({ open }) => {
			if (!open) store.close();
		},
		children: /* @__PURE__ */ jsxs(Portal$1, { children: [/* @__PURE__ */ jsx(Dialog.Backdrop, {}), /* @__PURE__ */ jsx(Dialog.Positioner, { children: /* @__PURE__ */ jsx(Dialog.Content, { children: /* @__PURE__ */ jsx(Dialog.Body, {
			paddingY: "6",
			children: event !== null && render(event, {
				close: store.close,
				closeAll: store.close
			})
		}) }) })] })
	});
}
// src/components/calendar/popover-store.ts
function createPopoverStore() {
	const store = createExternalStore(null);
	return {
		get: store.get,
		subscribe: store.subscribe,
		open(anchor, data, positionAnchor) {
			store.set({
				anchor,
				positionAnchor,
				data
			});
		},
		close() {
			store.set(null);
		}
	};
}
/** Data of the open popover, re-rendering only the subscribing component */
function usePopoverData(store) {
	return useSyncExternalStore(store.subscribe, store.get)?.data;
}
// src/components/calendar/more-events.tsx
function MoreEvents({ date, events, locale, renderEvent, onEventClick, close, closeLabel, detailsStore }) {
	const openEvent = usePopoverData(detailsStore);
	return /* @__PURE__ */ jsxs(Flex, {
		flexDirection: "column",
		gap: "0.5",
		children: [/* @__PURE__ */ jsxs(Flex, {
			justifyContent: "space-between",
			alignItems: "center",
			gap: "2",
			position: "sticky",
			top: "-3",
			zIndex: "1",
			marginX: "-3",
			marginTop: "-3",
			paddingX: "3",
			paddingTop: "3",
			paddingBottom: "1",
			background: "bg.panel",
			children: [/* @__PURE__ */ jsx(Text, {
				textStyle: "sm",
				fontWeight: "semibold",
				_firstLetter: { textTransform: "uppercase" },
				children: format(date, "EEEE d MMMM", { locale: DATE_FNS_LOCALES[locale] })
			}), /* @__PURE__ */ jsx(Tooltip$1, {
				content: closeLabel,
				openDelay: 200,
				closeDelay: 100,
				children: /* @__PURE__ */ jsx(CloseButton, {
					"aria-label": closeLabel,
					colorPalette: "gray",
					size: "xs",
					onClick: close
				})
			})]
		}), events.map((event, index) => /* @__PURE__ */ jsx(Box, {
			as: onEventClick ? "button" : "div",
			textAlign: "start",
			flexShrink: "0",
			borderRadius: "lg",
			focusRing: "inside",
			"data-selected": event === openEvent || void 0,
			transition: "transform 0.15s, box-shadow 0.15s",
			_selected: {
				position: "relative",
				zIndex: "1",
				transform: "scale(1.02)",
				boxShadow: "md"
			},
			cursor: onEventClick ? "pointer" : void 0,
			onClick: (domEvent) => onEventClick?.(event, domEvent.currentTarget),
			children: renderEvent(event, { view: "month" })
		}, index))]
	});
}
// src/components/calendar/skeleton-events.tsx
const WEEK_OPTIONS = { weekStartsOn: 1 };
const EVENTS_PER_DAY = [
	2,
	1,
	2,
	0,
	1,
	2,
	1
];
const FIRST_HOUR = 9;
function getVisibleDays(date, view) {
	if (view === "day") return [date];
	if (view === "week") return eachDayOfInterval({
		start: startOfWeek(date, WEEK_OPTIONS),
		end: endOfWeek(date, WEEK_OPTIONS)
	});
	return eachDayOfInterval({
		start: startOfWeek(startOfMonth(date), WEEK_OPTIONS),
		end: endOfWeek(endOfMonth(date), WEEK_OPTIONS)
	});
}
function getSkeletonEvents(date, view) {
	return getVisibleDays(date, view).flatMap((day, dayIndex) => {
		const count = view === "day" ? 4 : EVENTS_PER_DAY[dayIndex % 7];
		return Array.from({ length: count }, (_, index) => {
			const start = addHours(startOfDay(day), FIRST_HOUR + index * 2 + dayIndex % 2);
			return {
				start,
				end: addHours(start, 1)
			};
		});
	});
}
function renderSkeletonEvent(_event, { view }) {
	return /* @__PURE__ */ jsx(Skeleton, {
		height: view === "month" ? "5" : "full",
		borderRadius: "lg"
	});
}
// src/components/calendar/use-calendar-popovers.ts
/** Event details and "+N" list popovers, and the details dialog of the list */
function useCalendarPopovers({ date, view, display }) {
	const [detailsStore] = useState(createPopoverStore);
	const [moreStore] = useState(createPopoverStore);
	const [listDetailsStore] = useState(createListDetailsStore);
	const showMoreAnchor = useRef(null);
	useEffect(() => moreStore.subscribe(() => {
		if (moreStore.get() === null) detailsStore.close();
	}), [moreStore, detailsStore]);
	useEffect(() => {
		detailsStore.close();
		moreStore.close();
	}, [
		date,
		view,
		display,
		detailsStore,
		moreStore
	]);
	const openDetails = (event, domEvent) => {
		const anchor = domEvent.target.closest(".rbc-event");
		if (anchor) detailsStore.open(anchor, event);
	};
	const openDetailsFromKeyboard = (event, domEvent) => {
		const { key } = domEvent;
		if (key === "Enter" || key === " ") {
			domEvent.preventDefault();
			openDetails(event, domEvent);
		}
	};
	const openDetailsFromMoreList = (event, item) => detailsStore.open(item, event, getAnchorBesidePopover(item));
	const captureShowMoreAnchor = (domEvent) => {
		const anchor = domEvent.target.closest(".rbc-show-more");
		if (anchor) showMoreAnchor.current = anchor;
	};
	const openMoreEvents = (moreEvents, moreDate) => {
		if (showMoreAnchor.current) {
			detailsStore.close();
			moreStore.open(showMoreAnchor.current, {
				date: moreDate,
				events: moreEvents
			}, getShowMoreAnchor(showMoreAnchor.current));
		}
	};
	const closeAll = () => {
		moreStore.close();
		detailsStore.close();
	};
	return {
		detailsStore,
		moreStore,
		listDetailsStore,
		openDetails,
		openDetailsFromKeyboard,
		openDetailsFromMoreList,
		captureShowMoreAnchor,
		openMoreEvents,
		closeAll
	};
}
// src/components/calendar/calendar.tsx
const DEFAULT_VIEWS = [
	"day",
	"week",
	"month"
];
const DEFAULT_SCROLL_TO_TIME = new Date(1970, 0, 1, 9);
function Calendar$1({ events, date, onDateChange, view, onViewChange, views = DEFAULT_VIEWS, display = "calendar", onDisplayChange, renderList, locale, labels, renderEvent, renderEventDetails, onSelectSlot, scrollToTime = DEFAULT_SCROLL_TO_TIME, isLoading = false, getEventId }) {
	const popovers = useCalendarPopovers({
		date,
		view,
		display
	});
	const showList = display === "list" && renderList !== void 0;
	const canOpenDetails = renderEventDetails !== void 0 && !isLoading;
	const resolvedRenderEvent = renderEvent ?? defaultRenderEvent;
	const contextValue = useMemo(() => ({
		locale,
		view,
		renderEvent: isLoading ? renderSkeletonEvent : resolvedRenderEvent
	}), [
		locale,
		view,
		isLoading,
		resolvedRenderEvent
	]);
	const skeletonEvents = useMemo(() => getSkeletonEvents(date, view), [date, view]);
	const eventIdProps = useMemo(() => ({ eventIdAccessor: (event) => isLoading ? void 0 : getEventId?.(event) }), [getEventId, isLoading]);
	const messages = useMemo(() => ({ showMore: labels.showMore }), [labels.showMore]);
	return /* @__PURE__ */ jsxs(Flex, {
		flexDirection: "column",
		height: "full",
		background: "bg",
		borderWidth: "1px",
		borderColor: "border",
		borderRadius: "l3",
		overflow: "hidden",
		children: [
			/* @__PURE__ */ jsx(CalendarToolbar, {
				date,
				onDateChange,
				view,
				onViewChange,
				views,
				display: showList ? "list" : "calendar",
				onDisplayChange: renderList ? onDisplayChange : void 0,
				locale,
				labels
			}),
			showList ? /* @__PURE__ */ jsx(Box, {
				flex: "1",
				minHeight: "0",
				overflowY: "auto",
				children: renderList({ openEventDetails: popovers.listDetailsStore.open })
			}) : /* @__PURE__ */ jsxs(Box, {
				flex: "1",
				minHeight: "0",
				css: calendarCss,
				"aria-busy": isLoading,
				onClickCapture: popovers.captureShowMoreAnchor,
				children: [/* @__PURE__ */ jsx(CalendarContext.Provider, {
					value: contextValue,
					children: /* @__PURE__ */ jsx(Calendar, {
						localizer,
						culture: locale,
						events: isLoading ? skeletonEvents : events,
						...eventIdProps,
						view,
						onView: (next) => onViewChange(next),
						views,
						date,
						onNavigate: onDateChange,
						toolbar: false,
						components: CALENDAR_COMPONENTS,
						messages,
						selected: null,
						popup: false,
						doShowMoreDrillDown: false,
						drilldownView: views.includes("day") ? "day" : null,
						onShowMore: popovers.openMoreEvents,
						showMultiDayTimes: true,
						selectable: Boolean(onSelectSlot),
						onSelectSlot: ({ start, end, action }) => onSelectSlot?.({
							start,
							end,
							action
						}),
						onSelectEvent: canOpenDetails ? popovers.openDetails : void 0,
						onKeyPressEvent: canOpenDetails ? popovers.openDetailsFromKeyboard : void 0,
						scrollToTime,
						dayLayoutAlgorithm: "no-overlap",
						endAccessor: getVisualEnd,
						style: { height: "100%" }
					})
				}), /* @__PURE__ */ jsx(CalendarPopover, {
					store: popovers.moreStore,
					positioning: "belowOrAbove",
					render: (more, { close }) => /* @__PURE__ */ jsx(MoreEvents, {
						date: more.date,
						events: more.events,
						locale,
						renderEvent: resolvedRenderEvent,
						onEventClick: renderEventDetails ? popovers.openDetailsFromMoreList : void 0,
						close,
						closeLabel: labels.close,
						detailsStore: popovers.detailsStore
					})
				})]
			}),
			renderEventDetails && /* @__PURE__ */ jsx(CalendarPopover, {
				store: popovers.detailsStore,
				positioning: "beside",
				render: (event, { close }) => renderEventDetails(event, {
					close,
					closeAll: popovers.closeAll
				})
			}),
			renderEventDetails && /* @__PURE__ */ jsx(ListDetailsDialog, {
				store: popovers.listDetailsStore,
				render: renderEventDetails
			})
		]
	});
}
// src/components/checkbox.tsx
const Checkbox$1 = forwardRef(function Checkbox$2(props, ref) {
	const { icon, children, inputProps, rootRef, ...rest } = props;
	return /* @__PURE__ */ jsxs(Checkbox.Root, {
		ref: rootRef,
		...rest,
		children: [
			/* @__PURE__ */ jsx(Checkbox.HiddenInput, {
				ref,
				...inputProps
			}),
			/* @__PURE__ */ jsx(Checkbox.Control, { children: icon || /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
			children !== null && /* @__PURE__ */ jsx(Checkbox.Label, { children })
		]
	});
});
// src/components/checkbox-card.tsx
const CheckboxCard$1 = Object.assign(CheckboxCard.Root, {
	Indicator: CheckboxCard.Indicator,
	Content: CheckboxCard.Content,
	HiddenInput: CheckboxCard.HiddenInput,
	Control: CheckboxCard.Control,
	Addon: CheckboxCard.Addon,
	Label: CheckboxCard.Label,
	Description: CheckboxCard.Description
});
// src/components/clipboard.tsx
const ClipboardIconButton = (props) => /* @__PURE__ */ jsx(Box$1, { children: /* @__PURE__ */ jsx(Clipboard$1.Trigger, {
	asChild: true,
	children: /* @__PURE__ */ jsx(IconButton$1, {
		variant: "surface",
		size: "xs",
		marginInlineEnd: "-2",
		...props,
		children: /* @__PURE__ */ jsx(Clipboard$1.Indicator, {
			copied: /* @__PURE__ */ jsx(CheckIcon, {}),
			children: /* @__PURE__ */ jsx(CopyIcon, {})
		})
	})
}) });
const Clipboard$1 = Object.assign(Clipboard.Root, {
	Trigger: Clipboard.Trigger,
	Input: Clipboard.Input,
	Label: Clipboard.Label,
	Indicator: Clipboard.Indicator,
	IconButton: ClipboardIconButton
});
// src/components/color-picker.tsx
const Sliders = forwardRef(function Sliders(props, ref) {
	return /* @__PURE__ */ jsxs(Flex, {
		ref,
		gap: "2",
		alignItems: "center",
		...props,
		children: [/* @__PURE__ */ jsx(ColorPicker.EyeDropper, {
			size: "xs",
			colorPalette: "gray",
			variant: "outline"
		}), /* @__PURE__ */ jsxs(ColorPicker.ChannelSlider, {
			channel: "hue",
			children: [/* @__PURE__ */ jsx(ColorPicker.ChannelSliderTrack, {}), /* @__PURE__ */ jsx(ColorPicker.ChannelSliderThumb, {
				cursor: "grab",
				_active: { cursor: "grabbing" }
			})]
		})]
	});
});
const Trigger = forwardRef(function Trigger(props, ref) {
	return /* @__PURE__ */ jsx(ColorPicker.Trigger, {
		ref,
		background: "white",
		...props
	});
});
const ColorPicker$1 = {
	...ColorPicker,
	Trigger,
	Sliders
};
// src/components/combobox.tsx
const ComboboxContent = forwardRef(function ComboboxContent(props, ref) {
	const { portalled = true, portalRef, ...rest } = props;
	return /* @__PURE__ */ jsx(Portal, {
		disabled: !portalled,
		container: portalRef,
		children: /* @__PURE__ */ jsx(Combobox.Positioner, { children: /* @__PURE__ */ jsx(Combobox.Content, {
			ref,
			...rest
		}) })
	});
});
const Combobox$1 = {
	...Combobox,
	Content: ComboboxContent
};
// src/components/drag-and-drop/board.tsx
const BoardContext = createContext(null);
const useBoardColumnItems = (columnId) => {
	const context = useContext(BoardContext);
	return context ? context.columns[columnId] ?? [] : [];
};
const DragAndDropBoard = ({ defaultColumns, onReorder, children, renderOverlay }) => {
	const [activeId, setActiveId] = useState(null);
	const [columns, setColumnsState] = useState(defaultColumns);
	const [prevDefaultColumns, setPrevDefaultColumns] = useState(defaultColumns);
	const columnsRef = useRef(columns);
	const dragStartColumns = useRef(columns);
	const setColumns = (next) => {
		columnsRef.current = next;
		setColumnsState(next);
	};
	if (defaultColumns !== prevDefaultColumns) {
		setPrevDefaultColumns(defaultColumns);
		setColumnsState(defaultColumns);
	}
	useEffect(() => {
		columnsRef.current = columns;
	}, [columns]);
	const findColumnId = (id) => {
		const current = columnsRef.current;
		if (typeof id === "string" && id in current) return id;
		return Object.keys(current).find((columnId) => current[columnId].some((item) => item.id === id)) ?? null;
	};
	const activeItem = activeId === null ? null : Object.values(columns).flat().find((item) => item.id === activeId) ?? null;
	const handleDragStart = (event) => {
		dragStartColumns.current = columnsRef.current;
		setActiveId(event.active.id);
	};
	const handleDragOver = (event) => {
		const { active, over } = event;
		if (!over) return;
		const activeColumnId = findColumnId(active.id);
		const overColumnId = findColumnId(over.id);
		if (!activeColumnId || !overColumnId || activeColumnId === overColumnId) return;
		const current = columnsRef.current;
		const activeItems = current[activeColumnId];
		const overItems = current[overColumnId];
		const activeIndex = activeItems.findIndex((item) => item.id === active.id);
		if (activeIndex === -1) return;
		const moved = activeItems[activeIndex];
		const overIndex = overItems.findIndex((item) => item.id === over.id);
		const insertIndex = overIndex === -1 ? overItems.length : overIndex;
		setColumns({
			...current,
			[activeColumnId]: activeItems.filter((item) => item.id !== active.id),
			[overColumnId]: [
				...overItems.slice(0, insertIndex),
				moved,
				...overItems.slice(insertIndex)
			]
		});
	};
	const handleDragEnd = (event) => {
		const { active, over } = event;
		setActiveId(null);
		if (!over) return;
		const snapshot = dragStartColumns.current;
		const fromColumnId = findColumnId(active.id);
		const overColumnId = findColumnId(over.id);
		if (!fromColumnId || !overColumnId) return;
		let next = columnsRef.current;
		if (fromColumnId === overColumnId) {
			const items = next[fromColumnId];
			const oldIndex = items.findIndex((item) => item.id === active.id);
			const newIndex = items.findIndex((item) => item.id === over.id);
			if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) next = {
				...next,
				[fromColumnId]: arrayMove(items, oldIndex, newIndex)
			};
		}
		const startColumnId = Object.keys(snapshot).find((columnId) => snapshot[columnId].some((item) => item.id === active.id)) ?? fromColumnId;
		const startIndex = snapshot[startColumnId].findIndex((item) => item.id === active.id);
		const endColumnId = findColumnId(active.id) ?? fromColumnId;
		const endIndex = next[endColumnId].findIndex((item) => item.id === active.id);
		if (startColumnId === endColumnId && startIndex === endIndex) {
			setColumns(snapshot);
			return;
		}
		setColumns(next);
		if (onReorder({
			columns: next,
			activeId: active.id,
			from: {
				columnId: startColumnId,
				index: startIndex
			},
			to: {
				columnId: endColumnId,
				index: endIndex
			}
		}) === false) setColumns(snapshot);
	};
	const handleDragCancel = () => {
		setColumns(dragStartColumns.current);
		setActiveId(null);
	};
	return /* @__PURE__ */ jsx(BoardContext.Provider, {
		value: { columns },
		children: /* @__PURE__ */ jsxs(DndContext, {
			collisionDetection: closestCorners,
			onDragStart: handleDragStart,
			onDragOver: handleDragOver,
			onDragEnd: handleDragEnd,
			onDragCancel: handleDragCancel,
			children: [children(columns), /* @__PURE__ */ jsx(DragOverlay, {
				style: { transformOrigin: "0 0" },
				children: activeId && renderOverlay && activeItem ? renderOverlay({ activeItem }) : null
			})]
		})
	});
};
const DragAndDropColumn = ({ id, children }) => {
	const { setNodeRef } = useDroppable({ id });
	const itemsWithId = useBoardColumnItems(id).map((item) => item.id);
	return /* @__PURE__ */ jsx(SortableContext, {
		id,
		items: itemsWithId,
		strategy: verticalListSortingStrategy,
		children: /* @__PURE__ */ jsx(Box$1, {
			ref: setNodeRef,
			flexGrow: 1,
			children
		})
	});
};
// src/components/drag-and-drop/drag-handle.tsx
const DragHandle = (props) => {
	const { handleProps, size = "xl", className } = props;
	const baseStyles = {
		cursor: "grab",
		display: "inline-flex",
		zIndex: 10
	};
	if (props.variant === "inline") return /* @__PURE__ */ jsx(Box$1, {
		...handleProps,
		...baseStyles,
		className,
		children: /* @__PURE__ */ jsx(VerticalGripIcon, { size })
	});
	const { position } = props;
	const positions = {
		"top-left": {
			top: 2,
			left: 2
		},
		"top-right": {
			top: 2,
			right: 2
		},
		"bottom-left": {
			bottom: 2,
			left: 2
		},
		"bottom-right": {
			bottom: 2,
			right: 2
		}
	};
	return /* @__PURE__ */ jsx(Box$1, {
		position: "absolute",
		className,
		...handleProps,
		...positions[position],
		...baseStyles,
		children: /* @__PURE__ */ jsx(VerticalGripIcon, { size })
	});
};
// src/components/drag-and-drop/draggable-item.tsx
const DraggableItem = ({ children, isDragging, styles }) => /* @__PURE__ */ jsx(Box$1, {
	opacity: isDragging ? .5 : 1,
	transition: "all 0.2s",
	position: "relative",
	...styles,
	children
});
// src/components/drag-and-drop/provider.tsx
const DragAndDropProvider = ({ defaultItems, onReorder, children, renderOverlay }) => {
	const [activeId, setActiveId] = useState(null);
	const [items, setItems] = useState(defaultItems);
	const [prevDefaultItems, setPrevDefaultItems] = useState(defaultItems);
	if (defaultItems !== prevDefaultItems) {
		setPrevDefaultItems(defaultItems);
		setItems(defaultItems);
	}
	const handleDragStart = useCallback((event) => {
		setActiveId(event.active.id);
	}, []);
	const handleDragEnd = useCallback((event) => {
		const { active, over } = event;
		const activeId = active.id;
		const overId = over?.id;
		if (activeId && overId && activeId !== overId) {
			const oldIndex = items.findIndex((item) => activeId === item.id);
			const newIndex = items.findIndex((item) => overId === item.id);
			if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
				const newItems = arrayMove(items, oldIndex, newIndex);
				setItems(newItems);
				onReorder({
					items: newItems,
					activeId,
					overId,
					oldIndex,
					newIndex
				});
			}
		}
		setActiveId(null);
	}, [items, onReorder]);
	const handleDragCancel = () => {
		setActiveId(null);
	};
	const itemIds = items.map((item) => item.id);
	const activeItem = activeId ? items.find((item) => item.id === activeId) : null;
	return /* @__PURE__ */ jsxs(DndContext, {
		collisionDetection: closestCenter,
		onDragStart: handleDragStart,
		onDragEnd: handleDragEnd,
		onDragCancel: handleDragCancel,
		children: [/* @__PURE__ */ jsx(SortableContext, {
			items: itemIds,
			strategy: rectSortingStrategy,
			children: children(items)
		}), /* @__PURE__ */ jsx(DragOverlay, {
			style: { transformOrigin: "0 0" },
			children: activeId && renderOverlay && activeItem ? renderOverlay({
				activeItem,
				activeId
			}) : null
		})]
	});
};
// src/components/drag-and-drop/sortable-item.tsx
const SortableItem = ({ id, asChild, children }) => {
	const { isDragging, attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
	const style = {
		transform: CSS.Transform.toString(transform ? {
			...transform,
			scaleX: 1,
			scaleY: 1
		} : null),
		transition: transition || void 0,
		zIndex: isDragging ? 1 : 0,
		width: "100%"
	};
	const dragHandleProps = {
		...attributes,
		...listeners
	};
	if (asChild) return children({
		dragHandleProps,
		isDragging,
		ref: setNodeRef,
		style
	});
	return /* @__PURE__ */ jsx("div", {
		ref: setNodeRef,
		style,
		children: children({
			dragHandleProps,
			isDragging,
			ref: setNodeRef,
			style
		})
	});
};
// src/components/drag-and-drop/useDragAndDrop.ts
const useDragAndDrop = (initialItems, onReorderCallback) => {
	const [items, setItems] = useState(initialItems);
	return {
		items,
		setItems,
		handleReorder: useCallback((event) => {
			setItems(event.items);
			onReorderCallback?.(event);
		}, [onReorderCallback])
	};
};
// src/components/drag-and-drop/index.ts
const DragAndDrop = {
	Root: DragAndDropProvider,
	Board: DragAndDropBoard,
	Column: DragAndDropColumn,
	SortableItem,
	DraggableItem,
	DragHandle
};
// src/components/file-upload.tsx
const MAX_FILES$1 = 1;
const DEFAULT_DROPZONE_LABEL = "Glissez un fichier ou cliquez pour le sélectionner";
const DEFAULT_REMOVE_LABEL = "Supprimer le fichier";
const DEFAULT_ERROR_MESSAGES = {
	FILE_TOO_LARGE: "Fichier trop volumineux",
	FILE_INVALID_TYPE: "Format de fichier non pris en charge"
};
const FileUploadRoot = forwardRef(function FileUploadRoot(props, ref) {
	const uniqueId = useId();
	const { children, inputProps, ...rest } = props;
	return /* @__PURE__ */ jsxs(FileUpload.Root, {
		maxFiles: MAX_FILES$1,
		alignItems: "stretch",
		...rest,
		ids: { hiddenInput: `file-upload:${uniqueId}:input` },
		children: [/* @__PURE__ */ jsx(FileUpload.HiddenInput, {
			ref,
			...inputProps
		}), children]
	});
});
const FileUploadDropzone = forwardRef(function FileUploadDropzone(props, ref) {
	const { label = DEFAULT_DROPZONE_LABEL, description, ...rest } = props;
	return /* @__PURE__ */ jsxs(FileUpload.Dropzone, {
		ref,
		...rest,
		children: [/* @__PURE__ */ jsx(UploadIcon, {
			size: "md",
			color: "fg.muted"
		}), /* @__PURE__ */ jsxs(FileUpload.DropzoneContent, { children: [/* @__PURE__ */ jsx(Box$1, { children: label }), description && /* @__PURE__ */ jsx(Box$1, {
			color: "fg.muted",
			children: description
		})] })]
	});
});
const FileUploadErrorMessage = ({ messages }) => {
	const error = useFileUploadContext().rejectedFiles[0]?.errors[0];
	if (!error) return null;
	return /* @__PURE__ */ jsx(Text$1, {
		color: "fg.error",
		textStyle: "sm",
		children: {
			...DEFAULT_ERROR_MESSAGES,
			...messages
		}[error] ?? error
	});
};
const FileUploadCurrentFile = ({ name, url, onRemove, removeLabel = DEFAULT_REMOVE_LABEL, disabled = false }) => {
	return /* @__PURE__ */ jsxs(Flex$1, {
		width: "full",
		alignItems: "center",
		gap: "2",
		borderWidth: "1px",
		borderRadius: "md",
		paddingX: "3",
		paddingY: "2",
		children: [
			/* @__PURE__ */ jsx(FileIcon, {
				size: "sm",
				color: "fg.muted",
				flexShrink: "0"
			}),
			url ? /* @__PURE__ */ jsx(Link, {
				href: url,
				target: "_blank",
				rel: "noreferrer",
				minWidth: "0",
				children: /* @__PURE__ */ jsx(Text$1, {
					truncate: true,
					children: name
				})
			}) : /* @__PURE__ */ jsx(Text$1, {
				truncate: true,
				minWidth: "0",
				children: name
			}),
			/* @__PURE__ */ jsx(IconButton$1, {
				"aria-label": removeLabel,
				variant: "ghost",
				colorPalette: "gray",
				size: "xs",
				marginInlineStart: "auto",
				disabled,
				onClick: onRemove,
				children: /* @__PURE__ */ jsx(Trash2Icon, {})
			})
		]
	});
};
const FileUpload$1 = {
	Root: FileUploadRoot,
	Dropzone: FileUploadDropzone,
	ErrorMessage: FileUploadErrorMessage,
	CurrentFile: FileUploadCurrentFile,
	Label: FileUpload.Label,
	Trigger: FileUpload.Trigger
};
// src/components/image-crop/context.tsx
const ImageCropContext = createContext(null);
const useImageCrop = () => {
	const context = useContext(ImageCropContext);
	if (!context) throw new Error("useImageCrop must be used within an ImageCrop.Root component");
	return context;
};
// src/components/image-crop/actions.tsx
const ImageCropActions = ({ children }) => {
	return /* @__PURE__ */ jsx(Flex$1, {
		flexDirection: "row",
		justifyContent: "flex-end",
		gap: 2,
		children
	});
};
const ImageCropApply = ({ label }) => {
	const { applyCrop } = useImageCrop();
	return /* @__PURE__ */ jsxs(Button, {
		onClick: applyCrop,
		children: [
			/* @__PURE__ */ jsx(CropIcon, {}),
			" ",
			label
		]
	});
};
const ImageCropReset = ({ label }) => {
	const { resetCrop } = useImageCrop();
	return /* @__PURE__ */ jsxs(Button, {
		variant: "outline",
		onClick: resetCrop,
		children: [
			/* @__PURE__ */ jsx(RotateCCWIcon, {}),
			" ",
			label
		]
	});
};
// src/components/image-crop/content.tsx
const ImageCropContent = ({ style, className, ...props }) => {
	const { file, cropperRef, aspectRatio } = useImageCrop();
	if (!file) return null;
	const defaultStyle = {
		width: "100%",
		height: "auto",
		maxHeight: "350px",
		...style
	};
	return /* @__PURE__ */ jsx(Box$1, {
		className,
		...props,
		children: /* @__PURE__ */ jsx(Cropper, {
			ref: cropperRef,
			src: URL.createObjectURL(file),
			style: defaultStyle,
			className,
			aspectRatio,
			guides: true,
			autoCropArea: 1
		})
	});
};
// src/components/image-crop/utils.ts
const getCroppedFileAsync = async (cropperRef, originalFile) => {
	if (!cropperRef.current || !originalFile) return null;
	const fileName = originalFile.name || "image.jpg";
	const fileType = originalFile.type || "image/jpeg";
	try {
		const canvas = cropperRef.current.cropper.getCroppedCanvas();
		if (!canvas) {
			console.error("Failed to get cropped canvas");
			return null;
		}
		return new Promise((resolve, reject) => {
			canvas.toBlob((blob) => {
				if (!blob) {
					reject(/* @__PURE__ */ new Error("Failed to create blob from canvas"));
					return;
				}
				resolve(new File([blob], fileName, {
					type: fileType,
					lastModified: Date.now()
				}));
			}, fileType);
		});
	} catch (error) {
		console.error("Error during crop:", error);
		return null;
	}
};
// src/components/image-crop/root.tsx
const ImageCropRoot = ({ file, aspectRatio, onCrop, children }) => {
	const cropperRef = useRef(null);
	const applyCrop = useCallback(async () => {
		try {
			const croppedFile = await getCroppedFileAsync(cropperRef, file);
			if (croppedFile && onCrop) onCrop(croppedFile);
		} catch (error) {
			console.error("Error applying crop:", error);
		}
	}, [file, onCrop]);
	const resetCrop = useCallback(() => {
		if (cropperRef.current) cropperRef.current.cropper.reset();
	}, []);
	const contextValue = useMemo(() => ({
		file,
		cropperRef,
		aspectRatio,
		applyCrop,
		resetCrop,
		onCrop
	}), [
		file,
		aspectRatio,
		applyCrop,
		resetCrop,
		onCrop
	]);
	return /* @__PURE__ */ jsx(ImageCropContext.Provider, {
		value: contextValue,
		children
	});
};
// src/components/image-crop/index.ts
const ImageCrop = {
	Root: ImageCropRoot,
	Content: ImageCropContent,
	Actions: ImageCropActions,
	Apply: ImageCropApply,
	Reset: ImageCropReset
};
// src/components/image-upload.tsx
const ACCEPT_IMAGE_TYPES = "image/png,image/jpg,image/jpeg,image/webp";
const MAX_FILES = 1;
const MAX_FILE_SIZE = 5242880;
const ERROR_MESSAGE = "5Mo maximum pour les images";
const ImageUploadRoot = forwardRef(function ImageUploadRoot(props, ref) {
	const uniqueId = useId();
	const { children, inputProps, ...rest } = props;
	return /* @__PURE__ */ jsxs(FileUpload.Root, {
		accept: ACCEPT_IMAGE_TYPES,
		maxFiles: MAX_FILES,
		maxFileSize: MAX_FILE_SIZE,
		...rest,
		ids: { hiddenInput: `file-upload:${uniqueId}:input` },
		children: [/* @__PURE__ */ jsx(FileUpload.HiddenInput, {
			ref,
			...inputProps
		}), children]
	});
});
const ImageUploadTrigger = forwardRef(function ImageUploadTrigger(props, ref) {
	const { children, asChild = false } = props;
	if (asChild && children) return /* @__PURE__ */ jsx(FileUpload.Trigger, {
		asChild: true,
		children
	});
	return /* @__PURE__ */ jsx(FileUpload.Trigger, {
		asChild: true,
		children: /* @__PURE__ */ jsxs(Button, {
			variant: "outline",
			size: "sm",
			ref,
			colorPalette: "gray",
			children: [/* @__PURE__ */ jsx(UploadIcon, {}), children || "Upload Image"]
		})
	});
});
const ImageUploadList = () => {
	const files = useFileUploadContext().acceptedFiles;
	if (files.length === 0) return null;
	return /* @__PURE__ */ jsx(FileUpload.ItemGroup, { children: files.map((file) => /* @__PURE__ */ jsxs(FileUpload.Item, {
		file,
		children: [/* @__PURE__ */ jsx(FileUpload.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsx(FileUpload.ItemPreviewImage, { cursor: "pointer" })
		}), /* @__PURE__ */ jsx(FileUpload.ItemDeleteTrigger, {
			asChild: true,
			children: /* @__PURE__ */ jsx(CloseButton, {})
		})]
	}, file.name)) });
};
const ImageUploadErrorMessage = () => {
	const rejectedFiles = useFileUploadContext().rejectedFiles;
	if (rejectedFiles.length === 0) return null;
	const errors = rejectedFiles.flatMap((rejectedFile) => {
		return rejectedFile.errors.map((error) => {
			if (error === "FILE_TOO_LARGE") return ERROR_MESSAGE;
			return error;
		});
	});
	if (errors.length === 0) return null;
	return /* @__PURE__ */ jsx(Text$1, {
		color: "red.500",
		fontSize: "sm",
		margin: "auto",
		children: errors[0]
	});
};
const ImageUpload = {
	Root: ImageUploadRoot,
	Trigger: ImageUploadTrigger,
	List: ImageUploadList,
	ErrorMessage: ImageUploadErrorMessage,
	Label: FileUpload.Label,
	ClearTrigger: FileUpload.ClearTrigger,
	FileText: FileUpload.FileText
};
// src/components/image-upload-crop/context.tsx
const ImageUploadCropContext = createContext(null);
const useImageUploadCrop = () => {
	const context = useContext(ImageUploadCropContext);
	if (!context) throw new Error("useImageUploadCrop must be used within an ImageUploadCrop.Root component");
	return context;
};
// src/components/image-upload-crop/clear-trigger.tsx
const ImageUploadCropClearTrigger = ({ children }) => {
	const { currentImage, onClear } = useImageUploadCrop();
	if (!currentImage || !onClear) return null;
	return /* @__PURE__ */ jsx("span", {
		onClick: (e) => {
			e.stopPropagation();
			onClear();
		},
		children
	});
};
// src/components/image-upload-crop/cropper.tsx
const ImageUploadCropCropper = ({ actionButtonLabels }) => {
	const { selectedFile, onCropComplete, ratio } = useImageUploadCrop();
	if (!selectedFile) return null;
	const aspectRatio = ratio ? ratio.x / ratio.y : void 0;
	return /* @__PURE__ */ jsx(Flex$1, {
		flexDirection: "column",
		gap: 4,
		children: /* @__PURE__ */ jsxs(ImageCrop.Root, {
			file: selectedFile,
			aspectRatio,
			onCrop: onCropComplete,
			children: [/* @__PURE__ */ jsx(ImageCrop.Content, {}), /* @__PURE__ */ jsxs(ImageCrop.Actions, { children: [/* @__PURE__ */ jsx(ImageCrop.Reset, { label: actionButtonLabels.reset }), /* @__PURE__ */ jsx(ImageCrop.Apply, { label: actionButtonLabels.apply })] })]
		})
	});
};
// src/components/image-upload-crop/dialog.tsx
const ImageUploadCropDialog = ({ title, actionButtonLabels }) => {
	const { selectedFile, isDialogOpen, closeDialog } = useImageUploadCrop();
	const onOpenChange = ({ open }) => {
		if (!open) closeDialog();
	};
	if (!selectedFile) return null;
	return /* @__PURE__ */ jsx(Dialog.Root, {
		open: isDialogOpen,
		onOpenChange,
		size: "lg",
		placement: "center",
		children: /* @__PURE__ */ jsxs(Portal$1, { children: [/* @__PURE__ */ jsx(Dialog.Backdrop, {}), /* @__PURE__ */ jsx(Dialog.Positioner, { children: /* @__PURE__ */ jsxs(Dialog.Content, { children: [/* @__PURE__ */ jsxs(Dialog.Header, { children: [/* @__PURE__ */ jsx(Dialog.Title, { children: title }), /* @__PURE__ */ jsx(Dialog.CloseTrigger, {})] }), /* @__PURE__ */ jsx(Dialog.Body, { children: /* @__PURE__ */ jsx(ImageUploadCropCropper, { actionButtonLabels }) })] }) })] })
	});
};
// src/components/image-upload-crop/file-text.tsx
const ImageUploadCropFileText = ({ placeholder = "Sélectionner une image" }) => {
	const { currentImage } = useImageUploadCrop();
	return /* @__PURE__ */ jsx(Text$1, {
		truncate: true,
		maxWidth: "200px",
		color: currentImage ? void 0 : "fg.subtle",
		children: currentImage ?? placeholder
	});
};
// src/components/image-upload-crop/preview-content.tsx
function PreviewContent({ isLoading, currentImage, placeholder, isRounded = false }) {
	if (isLoading) return /* @__PURE__ */ jsx(Skeleton, {
		height: "100%",
		width: "100%"
	});
	if (currentImage) {
		if (isRounded) return /* @__PURE__ */ jsx(Image$2, {
			src: currentImage,
			alt: "Image actuelle",
			width: "100%",
			height: "100%",
			objectFit: "cover",
			borderRadius: "full"
		});
		return /* @__PURE__ */ jsx(Image$2, {
			src: currentImage,
			alt: "Image actuelle",
			width: "100%",
			maxH: "200px",
			height: "100%",
			borderRadius: "sm"
		});
	}
	if (placeholder) return /* @__PURE__ */ jsx(Fragment$1, { children: placeholder });
	return /* @__PURE__ */ jsx(Flex$1, {
		flexDirection: "column",
		justifyContent: "center",
		alignContent: "center",
		fontSize: "xs",
		color: "gray.500",
		paddingY: 2.5,
		paddingX: .5,
		height: "100%",
		children: "Cliquez pour sélectionner une image"
	});
}
// src/components/image-upload-crop/usePreviewDimensions.ts
const useContainerWidth = (containerRef) => {
	const [width, setWidth] = useState(null);
	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		setWidth(container.offsetWidth);
		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) setWidth(entry.contentRect.width);
		});
		resizeObserver.observe(container);
		return () => {
			resizeObserver.disconnect();
		};
	}, [containerRef]);
	return width;
};
const useContainerHeight = (containerRef, ratio) => {
	const width = useContainerWidth(containerRef);
	return useMemo(() => {
		if (!ratio || !width) return null;
		return width * ratio.y / ratio.x;
	}, [ratio, width]);
};
const usePreviewDimensions = (ratio) => {
	const containerRef = useRef(null);
	const height = useContainerHeight(containerRef, ratio);
	const getHeight = () => height ? `${height}px` : "auto";
	return {
		containerRef,
		height,
		getHeight
	};
};
// src/components/image-upload-crop/preview.tsx
const ImageUploadCropPreview = forwardRef(({ placeholder, className, isLoading = false, isRounded = false, ...props }, ref) => {
	const { currentImage, ratio } = useImageUploadCrop();
	const { containerRef, getHeight } = usePreviewDimensions(ratio);
	useImperativeHandle(ref, () => containerRef.current, [containerRef]);
	const borderRadius = isRounded ? "full" : "sm";
	return /* @__PURE__ */ jsx(Box$1, {
		borderWidth: "1px",
		borderStyle: "dashed",
		borderColor: {
			base: "gray.300",
			_hover: "brand.solid"
		},
		borderRadius,
		padding: 1,
		width: "100%",
		className,
		...props,
		children: /* @__PURE__ */ jsx(Box$1, {
			ref: containerRef,
			cursor: "pointer",
			textAlign: "center",
			width: "100%",
			height: getHeight(),
			position: "relative",
			borderRadius,
			overflow: "hidden",
			children: /* @__PURE__ */ jsx(PreviewContent, {
				isLoading,
				currentImage,
				placeholder,
				isRounded
			})
		})
	});
});
ImageUploadCropPreview.displayName = "ImageUploadCropPreview";
// src/components/image-upload-crop/root.tsx
const ImageUploadCropRoot = ({ currentImage, onCrop, onClear, ratio, children }) => {
	const [selectedFile, setSelectedFile] = useState(null);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const handleFileSelect = useCallback((files) => {
		const file = files[0];
		if (!file) return;
		setSelectedFile(file);
		setIsDialogOpen(true);
	}, []);
	const closeDialog = useCallback(() => {
		setIsDialogOpen(false);
		setSelectedFile(null);
	}, []);
	const onCropComplete = useCallback((croppedImage) => {
		if (onCrop) onCrop(croppedImage);
		closeDialog();
	}, [onCrop, closeDialog]);
	const contextValue = useMemo(() => ({
		ratio,
		currentImage,
		selectedFile,
		isDialogOpen,
		handleFileSelect,
		closeDialog,
		onCropComplete,
		onClear
	}), [
		ratio,
		currentImage,
		selectedFile,
		isDialogOpen,
		handleFileSelect,
		closeDialog,
		onCropComplete,
		onClear
	]);
	return /* @__PURE__ */ jsx(ImageUploadCropContext.Provider, {
		value: contextValue,
		children: /* @__PURE__ */ jsxs(ImageUpload.Root, {
			onFileAccept: (details) => handleFileSelect(details.files),
			children: [/* @__PURE__ */ jsx(ImageUpload.ErrorMessage, {}), children]
		}, selectedFile?.name)
	});
};
// src/components/image-upload-crop/index.ts
const ImageUploadCrop = {
	Root: ImageUploadCropRoot,
	Preview: ImageUploadCropPreview,
	Dialog: ImageUploadCropDialog,
	Trigger: forwardRef(function ImageUploadCropTrigger({ children, ...props }, ref) {
		return /* @__PURE__ */ jsx(FileUpload.Trigger, {
			ref,
			...props,
			children
		});
	}),
	FileText: ImageUploadCropFileText,
	ClearTrigger: ImageUploadCropClearTrigger
};
// src/components/menu.tsx
const MenuContent = forwardRef(function MenuContent(props, ref) {
	const { portalled = true, portalRef, ...rest } = props;
	return /* @__PURE__ */ jsx(Portal, {
		disabled: !portalled,
		container: portalRef,
		children: /* @__PURE__ */ jsx(Menu.Positioner, { children: /* @__PURE__ */ jsx(Menu.Content, {
			ref,
			...rest
		}) })
	});
});
const MenuArrow = forwardRef(function MenuArrow(props, ref) {
	return /* @__PURE__ */ jsx(Menu.Arrow, {
		ref,
		...props,
		children: /* @__PURE__ */ jsx(Menu.ArrowTip, {})
	});
});
const MenuCheckboxItem = forwardRef(function MenuCheckboxItem(props, ref) {
	return /* @__PURE__ */ jsxs(Menu.CheckboxItem, {
		ps: "8",
		ref,
		...props,
		children: [/* @__PURE__ */ jsx(AbsoluteCenter, {
			axis: "horizontal",
			insetStart: "4",
			asChild: true,
			children: /* @__PURE__ */ jsx(Menu.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, {}) })
		}), props.children]
	});
});
const MenuRadioItem = forwardRef(function MenuRadioItem(props, ref) {
	const { children, ...rest } = props;
	return /* @__PURE__ */ jsxs(Menu.RadioItem, {
		ps: "8",
		ref,
		...rest,
		children: [/* @__PURE__ */ jsx(AbsoluteCenter, {
			axis: "horizontal",
			insetStart: "4",
			asChild: true,
			children: /* @__PURE__ */ jsx(Menu.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, {}) })
		}), /* @__PURE__ */ jsx(Menu.ItemText, { children })]
	});
});
const MenuItemGroup = forwardRef(function MenuItemGroup(props, ref) {
	const { title, children, ...rest } = props;
	return /* @__PURE__ */ jsxs(Menu.ItemGroup, {
		ref,
		...rest,
		children: [title && /* @__PURE__ */ jsx(Menu.ItemGroupLabel, {
			userSelect: "none",
			children: title
		}), children]
	});
});
const MenuTriggerItem = forwardRef(function MenuTriggerItem(props, ref) {
	const { startIcon, children, ...rest } = props;
	return /* @__PURE__ */ jsxs(Menu.TriggerItem, {
		ref,
		color: "#555555",
		...rest,
		children: [
			startIcon,
			children,
			/* @__PURE__ */ jsx(ChevronRightIcon, {
				boxSize: "4",
				color: "fg.muted",
				marginInlineStart: "auto"
			})
		]
	});
});
const MenuDangerItem = forwardRef(function MenuDangerItem(props, ref) {
	return /* @__PURE__ */ jsx(Menu.Item, {
		color: "fg.error",
		_hover: {
			bg: "bg.error",
			color: "fg.error"
		},
		ref,
		...props,
		children: props.children
	});
});
const MenuTrigger = forwardRef(function MenuTrigger(props, ref) {
	return /* @__PURE__ */ jsx(Menu.Trigger, {
		ref,
		cursor: "pointer",
		...props,
		children: props.children
	});
});
const MenuRadioItemGroup = Menu.RadioItemGroup;
const MenuContextTrigger = Menu.ContextTrigger;
const MenuRoot = Menu.Root;
const MenuSeparator = Menu.Separator;
const MenuItem = forwardRef(function MenuTrigger(props, ref) {
	return /* @__PURE__ */ jsx(Menu.Item, {
		ref,
		color: "#555555",
		...props,
		children: props.children
	});
});
const MenuItemText = Menu.ItemText;
const MenuItemCommand = Menu.ItemCommand;
const Menu$1 = Object.assign(MenuRoot, {
	Arrow: MenuArrow,
	CheckboxItem: MenuCheckboxItem,
	Content: MenuContent,
	ContextTrigger: MenuContextTrigger,
	ItemGroup: MenuItemGroup,
	Item: MenuItem,
	DangerItem: MenuDangerItem,
	ItemText: MenuItemText,
	RadioItemGroup: MenuRadioItemGroup,
	RadioItem: MenuRadioItem,
	Separator: MenuSeparator,
	TriggerItem: MenuTriggerItem,
	Trigger: MenuTrigger,
	Command: MenuItemCommand
});
// src/components/pagination.tsx
function Controls({ count, pageSize, page, onPageChange }) {
	return /* @__PURE__ */ jsx(Pagination.Root, {
		count,
		pageSize,
		page,
		onPageChange,
		colorPalette: "gray",
		children: /* @__PURE__ */ jsxs(ButtonGroup, {
			variant: "ghost",
			size: "sm",
			wrap: "wrap",
			children: [
				/* @__PURE__ */ jsx(Pagination.PrevTrigger, {
					asChild: true,
					children: /* @__PURE__ */ jsx(IconButton$1, { children: /* @__PURE__ */ jsx(ChevronLeftIcon, {}) })
				}),
				/* @__PURE__ */ jsx(Pagination.Items, { render: (page) => /* @__PURE__ */ jsx(IconButton$1, {
					variant: {
						base: "ghost",
						_selected: "outline"
					},
					children: page.value
				}) }),
				/* @__PURE__ */ jsx(Pagination.NextTrigger, {
					asChild: true,
					children: /* @__PURE__ */ jsx(IconButton$1, { children: /* @__PURE__ */ jsx(ChevronRightIcon, {}) })
				})
			]
		})
	});
}
const Pagination$1 = {
	Root: Pagination.Root,
	Context: Pagination.Context,
	Ellipsis: Pagination.Ellipsis,
	Item: Pagination.Item,
	Items: Pagination.Items,
	NextTrigger: Pagination.NextTrigger,
	PrevTrigger: Pagination.PrevTrigger,
	PageText: Pagination.PageText,
	Controls
};
// src/components/panel.tsx
function Panel({ title, subtitle, actions, fillHeight = false, children, ...rest }) {
	const hasHeader = title || actions;
	return /* @__PURE__ */ jsxs(Flex, {
		flexDirection: "column",
		gap: "2",
		padding: "4",
		borderRadius: "xl",
		background: "white",
		border: "1px solid",
		borderColor: "gray.200",
		height: fillHeight ? void 0 : "fit-content",
		flex: fillHeight ? "1" : void 0,
		minHeight: fillHeight ? "0" : void 0,
		...rest,
		children: [hasHeader && /* @__PURE__ */ jsxs(Flex, {
			alignItems: "center",
			justifyContent: "space-between",
			flexWrap: "wrap",
			gap: "2",
			minHeight: "35px",
			paddingBottom: "2",
			children: [(title || subtitle) && /* @__PURE__ */ jsxs(Flex, {
				direction: "column",
				gap: 1,
				children: [title && /* @__PURE__ */ jsx(Flex, {
					alignItems: "center",
					gap: "2",
					color: "brand.solid",
					fontWeight: "semibold",
					fontSize: "18px",
					letterSpacing: "1px",
					children: title
				}), subtitle && /* @__PURE__ */ jsx(Text$1, {
					textStyle: "sm",
					color: "gray.solid",
					children: subtitle
				})]
			}), actions && /* @__PURE__ */ jsx(Flex, {
				alignItems: "center",
				gap: 2,
				children: actions
			})]
		}), children]
	});
}
// src/components/progress-circle.tsx
const ProgressCircle$1 = forwardRef(function ProgressCircle$2(props, ref) {
	const { showValueText, valueText, trackColor, color, cap, thickness, ...rest } = props;
	return /* @__PURE__ */ jsxs(ProgressCircle.Root, {
		...rest,
		ref,
		children: [/* @__PURE__ */ jsxs(ProgressCircle.Circle, {
			css: { "--thickness": thickness },
			children: [/* @__PURE__ */ jsx(ProgressCircle.Track, { stroke: trackColor }), /* @__PURE__ */ jsx(ProgressCircle.Range, {
				stroke: color,
				strokeLinecap: cap
			})]
		}), showValueText && /* @__PURE__ */ jsx(AbsoluteCenter, { children: /* @__PURE__ */ jsx(ProgressCircle.ValueText, { children: valueText }) })]
	});
});
// src/components/qr-code.tsx
const QrCodeRoot = forwardRef(function QrCodeRoot({ overlay, children, ...props }, ref) {
	return /* @__PURE__ */ jsxs(QrCode.Root, {
		ref,
		...props,
		children: [
			/* @__PURE__ */ jsx(QrCode.Frame, { children: /* @__PURE__ */ jsx(QrCode.Pattern, {}) }),
			overlay && /* @__PURE__ */ jsx(QrCode.Overlay, { children: overlay }),
			children
		]
	});
});
const QrCode$1 = Object.assign(QrCodeRoot, { DownloadTrigger: QrCode.DownloadTrigger });
// src/components/rich-text-editor/context.tsx
const RichTextEditorContext = createContext(null);
const useRichTextEditorContext = () => {
	const context = useContext(RichTextEditorContext);
	if (!context) throw new Error("useRichTextEditorContext must be used within RichTextEditor");
	return context;
};
// src/components/rich-text-editor/components/button-factory.tsx
const createToolbarButton = (config) => {
	const ToolbarButton = ({ label, ariaLabel } = {}) => {
		const { editor } = useRichTextEditorContext();
		const buttonLabel = label ?? config.label;
		const buttonAriaLabel = ariaLabel ?? buttonLabel;
		const editorState = useEditorState({
			editor,
			selector: (ctx) => {
				if (!ctx.editor) return {
					isActive: false,
					isDisabled: true
				};
				return {
					isActive: config.isActive?.(ctx.editor) ?? false,
					isDisabled: config.isDisabled?.(ctx.editor) ?? false
				};
			}
		});
		if (!editor) return null;
		return /* @__PURE__ */ jsx(Tooltip$1, {
			content: buttonLabel,
			openDelay: 200,
			closeDelay: 100,
			positioning: { placement: "top" },
			children: /* @__PURE__ */ jsx(IconButton$1, {
				variant: editorState?.isActive ? "surface" : "ghost",
				colorPalette: "gray",
				size: "xs",
				onClick: () => config.command(editor),
				disabled: editorState?.isDisabled,
				"aria-label": buttonAriaLabel,
				children: config.icon
			})
		});
	};
	ToolbarButton.displayName = `ToolbarButton(${config.label})`;
	return ToolbarButton;
};
// src/components/rich-text-editor/components/buttons.tsx
const BoldButton = createToolbarButton({
	label: "Gras",
	icon: /* @__PURE__ */ jsx(BoldIcon, {}),
	command: (editor) => editor.chain().focus().toggleBold().run(),
	isActive: (editor) => editor?.isActive("bold") ?? false,
	isDisabled: (editor) => !editor?.can().chain().toggleBold().run()
});
const ItalicButton = createToolbarButton({
	label: "Italique",
	icon: /* @__PURE__ */ jsx(ItalicIcon, {}),
	command: (editor) => editor.chain().focus().toggleItalic().run(),
	isActive: (editor) => editor?.isActive("italic") ?? false,
	isDisabled: (editor) => !editor?.can().chain().toggleItalic().run()
});
const UnderlineButton = createToolbarButton({
	label: "Souligné",
	icon: /* @__PURE__ */ jsx(UnderlineIcon, {}),
	command: (editor) => editor.chain().focus().toggleUnderline().run(),
	isActive: (editor) => editor?.isActive("underline") ?? false,
	isDisabled: (editor) => !editor?.can().chain().toggleUnderline().run()
});
const H1Button = createToolbarButton({
	label: "Titre 1",
	icon: /* @__PURE__ */ jsx(H1Icon, {}),
	command: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
	isActive: (editor) => editor?.isActive("heading", { level: 1 }) ?? false
});
const H2Button = createToolbarButton({
	label: "Titre 2",
	icon: /* @__PURE__ */ jsx(H2Icon, {}),
	command: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
	isActive: (editor) => editor?.isActive("heading", { level: 2 }) ?? false
});
const BulletListButton = createToolbarButton({
	label: "Liste non ordonnée",
	icon: /* @__PURE__ */ jsx(UnorderListIcon, {}),
	command: (editor) => editor.chain().focus().toggleBulletList().run(),
	isActive: (editor) => editor?.isActive("bulletList") ?? false
});
const OrderedListButton = createToolbarButton({
	label: "Liste ordonnée",
	icon: /* @__PURE__ */ jsx(OrderListIcon, {}),
	command: (editor) => editor.chain().focus().toggleOrderedList().run(),
	isActive: (editor) => editor?.isActive("orderedList") ?? false
});
const QuoteButton = createToolbarButton({
	label: "Citation",
	icon: /* @__PURE__ */ jsx(QuoteIcon, {}),
	command: (editor) => editor.chain().focus().toggleBlockquote().run(),
	isActive: (editor) => editor?.isActive("blockquote") ?? false
});
const UndoButton = createToolbarButton({
	label: "Annuler",
	icon: /* @__PURE__ */ jsx(UndoIcon, {}),
	command: (editor) => editor.chain().focus().undo().run(),
	isDisabled: (editor) => !editor?.can().chain().undo().run()
});
const RedoButton = createToolbarButton({
	label: "Rétablir",
	icon: /* @__PURE__ */ jsx(RedoIcon, {}),
	command: (editor) => editor.chain().focus().redo().run(),
	isDisabled: (editor) => !editor?.can().chain().redo().run()
});
// src/components/rich-text-editor/components/header.tsx
function Header({ children }) {
	return /* @__PURE__ */ jsx(Flex$1, {
		alignItems: "center",
		justifyContent: "center",
		position: "sticky",
		top: "0",
		zIndex: "docked",
		width: "full",
		background: "white",
		paddingTop: "2.5",
		css: { "& > *": { minWidth: 0 } },
		children
	});
}
// src/components/rich-text-editor/components/image/context.tsx
const ImageContext = createContext(null);
const useImage = () => {
	const context = useContext(ImageContext);
	if (!context) throw new Error("useImage must be used within a RichTextEditor.Image.Root component");
	return context;
};
// src/components/rich-text-editor/components/image/button.tsx
const ImageButton = ({ tooltip = "Image", ariaLabel = "Ajouter une image" }) => {
	const { imageSizes } = useImage();
	if (!imageSizes || imageSizes.length === 0) return /* @__PURE__ */ jsx(Tooltip$1, {
		content: tooltip,
		openDelay: 200,
		closeDelay: 100,
		positioning: { placement: "top" },
		children: /* @__PURE__ */ jsx(Box$1, { children: /* @__PURE__ */ jsx(ImageUploadCrop.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsx(IconButton$1, {
				variant: "ghost",
				colorPalette: "gray",
				size: "xs",
				"aria-label": ariaLabel,
				children: /* @__PURE__ */ jsx(ImageIcon, {})
			})
		}) })
	});
	return /* @__PURE__ */ jsx(Tooltip$1, {
		content: tooltip,
		openDelay: 200,
		closeDelay: 100,
		positioning: { placement: "top" },
		children: /* @__PURE__ */ jsx(Box$1, { children: /* @__PURE__ */ jsx(Popover$1.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsx(IconButton$1, {
				variant: "ghost",
				colorPalette: "gray",
				size: "xs",
				children: /* @__PURE__ */ jsx(ImageIcon, {})
			})
		}) })
	});
};
// src/components/rich-text-editor/constants.ts
const IMAGE_CONFIGS = {
	small: {
		ratio: {
			x: 400,
			y: 300
		},
		suffix: "-small",
		label: "Petite",
		icon: {
			w: 12,
			h: 8
		}
	},
	medium: {
		ratio: {
			x: 800,
			y: 600
		},
		suffix: "-medium",
		label: "Moyenne",
		icon: {
			w: 16,
			h: 12
		}
	},
	large: {
		ratio: {
			x: 1200,
			y: 800
		},
		suffix: "-large",
		label: "Grande",
		icon: {
			w: 20,
			h: 16
		}
	}
};
// src/components/rich-text-editor/components/image/popover.tsx
const DEFAULT_SIZE_LABELS = {
	small: "Petite",
	medium: "Moyenne",
	large: "Grande"
};
const ImagePopover = ({ title = "Choisir la taille de l'image", sizeLabels }) => {
	const { imageSizes, setSelectedSize, setIsPopoverOpen } = useImage();
	if (!imageSizes || imageSizes.length === 0) return null;
	const labels = {
		...DEFAULT_SIZE_LABELS,
		...sizeLabels
	};
	return /* @__PURE__ */ jsx(Portal$1, { children: /* @__PURE__ */ jsx(Popover$1.Positioner, { children: /* @__PURE__ */ jsx(Popover$1.Content, {
		width: "440px",
		children: /* @__PURE__ */ jsxs(Popover$1.Body, { children: [/* @__PURE__ */ jsx(Text$1, {
			fontSize: "sm",
			fontWeight: "medium",
			marginBottom: 4,
			children: title
		}), /* @__PURE__ */ jsx(Flex$1, {
			gap: 2,
			children: imageSizes.map((size) => {
				const config = IMAGE_CONFIGS[size];
				return /* @__PURE__ */ jsx(ImageUploadCrop.Trigger, {
					asChild: true,
					children: /* @__PURE__ */ jsxs(Flex$1, {
						direction: "column",
						alignItems: "center",
						gap: 2,
						flex: "1 1 0px",
						border: "1px solid",
						borderRadius: "sm",
						borderColor: "gray.200",
						padding: 2,
						cursor: "pointer",
						onClick: () => {
							setSelectedSize(size);
							setIsPopoverOpen(false);
						},
						children: [/* @__PURE__ */ jsx(Box$1, {
							alignContent: "center",
							children: labels[size]
						}), /* @__PURE__ */ jsx(Flex$1, {
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
							width: "100%",
							background: "gray.100",
							borderRadius: "sm",
							children: /* @__PURE__ */ jsx(Flex$1, {
								justifyContent: "center",
								alignItems: "center",
								width: config.icon.w,
								height: config.icon.h,
								background: "gray.100",
								borderRadius: "sm",
								border: "2px solid",
								borderColor: "gray.400",
								borderStyle: "dashed",
								margin: 4,
								children: /* @__PURE__ */ jsx(SquareImageIcon, { color: "gray.400" })
							})
						})]
					})
				}, size);
			})
		})] })
	}) }) });
};
// src/components/rich-text-editor/utils/addSuffixToFileName.ts
const addSuffixToFileName = (fileName, suffix) => {
	if (!suffix) return fileName;
	const lastDot = fileName.lastIndexOf(".");
	if (lastDot === -1) return fileName + suffix;
	return `${fileName.slice(0, lastDot)}${suffix}${fileName.slice(lastDot)}`;
};
// src/components/rich-text-editor/extensions.ts
const extensions = [StarterKit.configure({
	link: { openOnClick: false },
	dropcursor: {
		color: "var(--chakra-colors-brand-solid)",
		width: 2
	}
}), Image.configure({ inline: false })];
// src/components/rich-text-editor/utils/draftToTiptap.ts
function isDraftRawContent(value) {
	return typeof value === "object" && value !== null && "blocks" in value && Array.isArray(value.blocks);
}
function isTipTapJSON(value) {
	return typeof value === "object" && value !== null && "type" in value && value.type === "doc";
}
function isHTMLString(value) {
	return typeof value === "string";
}
const cleanTipTapContent = (content) => {
	if (!content || !content.content) return content;
	return {
		...content,
		content: content.content.map((node) => {
			if (node.type === "text" && (!node.text || node.text === "")) return null;
			if (node.content) {
				const cleaned = cleanTipTapContent(node);
				if (cleaned.content && cleaned.content.length === 0) return {
					...cleaned,
					content: void 0
				};
				return cleaned;
			}
			return node;
		}).filter((node) => node !== null)
	};
};
const convertDraftToTiptap = (raw) => {
	const entityMap = Object.fromEntries(Object.entries(raw.entityMap ?? {}).map(([key, entity]) => [key, {
		...entity,
		type: entity.type === "atomic:image" || entity.type === "image" ? "IMAGE" : entity.type
	}]));
	return cleanTipTapContent(new DraftConverter().convert({
		...raw,
		entityMap
	}));
};
const getTiptapContent = (value) => {
	try {
		if (!value) return null;
		if (isTipTapJSON(value)) return cleanTipTapContent(value);
		if (isHTMLString(value)) return value;
		return null;
	} catch (error) {
		console.error("Conversion failed:", error);
		return null;
	}
};
const getEditorValue = (rawContent) => {
	if (!rawContent) return null;
	if (typeof rawContent === "string") try {
		const parsed = JSON.parse(rawContent);
		if (isTipTapJSON(parsed)) return parsed;
		const draftContent = typeof parsed === "string" ? JSON.parse(parsed) : parsed;
		if (isDraftRawContent(draftContent)) return convertDraftToTiptap(draftContent);
		return null;
	} catch (error) {
		console.error("Error parsing rawContent:", error);
		return rawContent;
	}
	if (isTipTapJSON(rawContent)) return rawContent;
	return null;
};
const isEditorEmpty = (editorValue) => {
	if (!editorValue) return true;
	if (isTipTapJSON(editorValue)) {
		if (!editorValue.content || editorValue.content.length === 0) return true;
		return !editorValue.content.some((node) => {
			if (node.type === "paragraph") return node.content && node.content.length > 0;
			return true;
		});
	}
	if (typeof editorValue === "string") {
		const trimmed = editorValue.trim();
		return !trimmed || trimmed === "<p></p>" || trimmed === "<p><br></p>";
	}
	return true;
};
const serializeEditorValue = ({ rawContent, htmlContent }) => {
	if (isTipTapJSON(rawContent)) return {
		rawContent: JSON.stringify(rawContent),
		htmlContent: htmlContent || generateHTML(rawContent, extensions)
	};
	return {
		rawContent: null,
		htmlContent: ""
	};
};
const extractTipTapText = (node) => {
	let text = "";
	if (node.text) text += node.text;
	if (node.content && Array.isArray(node.content)) node.content.forEach((child) => {
		text += extractTipTapText(child);
	});
	return text;
};
const splitPlainText = (plainText, maxCharacters) => {
	return plainText.length > maxCharacters ? plainText.slice(0, maxCharacters) + "..." : plainText;
};
const getSampleFromEditorValue = (editorValue, maxCharacters) => {
	try {
		if (isTipTapJSON(editorValue)) {
			const plainText = extractTipTapText(editorValue);
			return splitPlainText(plainText, maxCharacters);
		}
		if (typeof editorValue === "string") {
			const plainText = editorValue.replaceAll(/<[^>]*>/g, "");
			return splitPlainText(plainText, maxCharacters);
		}
		return "";
	} catch (error) {
		console.error("Error in getSample:", error);
		return "";
	}
};
const generateJSONFromHTML = (html) => {
	return generateJSON(html, extensions);
};
// src/components/rich-text-editor/components/image/root.tsx
const ImageRoot = ({ children }) => {
	const { editor, imageSizes, onImageUpload } = useRichTextEditorContext();
	const [selectedSize, setSelectedSize] = useState(null);
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const handleImageCrop = useCallback(async (croppedFile) => {
		try {
			if (!imageSizes || imageSizes.length === 0) {
				const result = await onImageUpload?.(croppedFile);
				if (result?.assetUrl && editor) editor.commands.setImage({
					src: result.assetUrl,
					alt: `${croppedFile.name}-initial`
				});
				return;
			}
			if (!selectedSize) return;
			const config = IMAGE_CONFIGS[selectedSize];
			const fileName = addSuffixToFileName(croppedFile.name, config.suffix);
			const renamedFile = new File([croppedFile], fileName, {
				type: croppedFile.type,
				lastModified: croppedFile.lastModified
			});
			const result = await onImageUpload?.(renamedFile, selectedSize);
			if (result?.assetUrl && editor) editor.commands.setImage({
				src: result.assetUrl,
				alt: `${croppedFile.name}-${selectedSize}`
			});
			setSelectedSize(null);
			setIsPopoverOpen(false);
		} catch (error) {
			console.error("Error upload image:", error);
		}
	}, [
		imageSizes,
		selectedSize,
		onImageUpload,
		editor
	]);
	const contextValue = useMemo(() => ({
		imageSizes,
		selectedSize,
		isPopoverOpen,
		onImageUpload,
		setSelectedSize,
		setIsPopoverOpen,
		handleImageCrop
	}), [
		imageSizes,
		selectedSize,
		isPopoverOpen,
		onImageUpload,
		handleImageCrop
	]);
	const handlePopoverOpenChange = ({ open }) => {
		setIsPopoverOpen(open);
	};
	if (!imageSizes || imageSizes.length === 0) return /* @__PURE__ */ jsx(Box$1, {
		width: "fit-content",
		children: /* @__PURE__ */ jsx(ImageUploadCrop.Root, {
			onCrop: handleImageCrop,
			children: /* @__PURE__ */ jsx(ImageContext.Provider, {
				value: contextValue,
				children
			})
		})
	});
	return /* @__PURE__ */ jsx(Box$1, {
		width: "fit-content",
		children: /* @__PURE__ */ jsx(ImageUploadCrop.Root, {
			onCrop: handleImageCrop,
			children: /* @__PURE__ */ jsx(Popover$1.Root, {
				open: isPopoverOpen,
				onOpenChange: handlePopoverOpenChange,
				positioning: { placement: "bottom-end" },
				children: /* @__PURE__ */ jsx(ImageContext.Provider, {
					value: contextValue,
					children
				})
			})
		})
	});
};
// src/components/rich-text-editor/components/image/index.ts
const Image$1 = {
	Root: ImageRoot,
	Button: ImageButton,
	Popover: ImagePopover,
	UploadCrop: ImageUploadCrop.Dialog
};
// src/components/rich-text-editor/components/link-button.tsx
const URL_REGEX = /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/;
const DEFAULT_PROPS = {
	button: {
		tooltip: "Lien",
		ariaLabel: "Ajouter un lien"
	},
	titles: {
		add: "Ajouter un lien",
		edit: "Modifier le lien"
	},
	placeholder: "https://example.com",
	actionButtons: {
		add: "Ajouter",
		edit: "Modifier",
		remove: "Supprimer",
		cancel: "Annuler"
	},
	invalidUrlMessage: "URL invalide (ex: https://...)"
};
const RichTextEditorLinkButton = (props = {}) => {
	const button = {
		...DEFAULT_PROPS.button,
		...props.button
	};
	const titles = {
		...DEFAULT_PROPS.titles,
		...props.titles
	};
	const placeholder = props.placeholder ?? DEFAULT_PROPS.placeholder;
	const actionButtons = {
		...DEFAULT_PROPS.actionButtons,
		...props.actionButtons
	};
	const { editor } = useRichTextEditorContext();
	const editorState = useEditorState({
		editor,
		selector: (ctx) => {
			if (!ctx.editor) return {
				isActive: false,
				isDisabled: true
			};
			return {
				isActive: ctx.editor.isActive("link") ?? false,
				isDisabled: !ctx.editor?.can()?.chain()?.toggleLink()
			};
		}
	});
	const invalidUrlMessage = props.invalidUrlMessage;
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const [url, setUrl] = useState("");
	const isUrlInvalid = url.length > 0 && !URL_REGEX.test(url);
	const onInputChange = (event) => {
		setUrl(event.target.value);
	};
	const onInputKeyDown = (event) => {
		if (event.key === "Enter" && !isUrlInvalid) handleAddLink();
	};
	const handlePopoverOpenChange = ({ open }) => {
		setIsPopoverOpen(open);
		if (open) {
			if (editor?.isActive("link")) editor?.chain().focus().extendMarkRange("link").run();
			const currentUrl = editor?.getAttributes("link").href ?? "";
			setUrl(currentUrl);
		} else setUrl("");
	};
	const handleAddLink = () => {
		if (url && editor) {
			editor.chain().focus().setLink({
				href: url,
				target: "_blank"
			}).run();
			setIsPopoverOpen(false);
			setUrl("");
		}
	};
	const handleRemoveLink = () => {
		if (editor) {
			editor.chain().focus().unsetLink().run();
			setIsPopoverOpen(false);
		}
	};
	const isActive = editorState?.isActive;
	if (!editor) return null;
	return /* @__PURE__ */ jsxs(Popover$1.Root, {
		open: isPopoverOpen,
		onOpenChange: handlePopoverOpenChange,
		positioning: { placement: "bottom-end" },
		children: [/* @__PURE__ */ jsx(Tooltip$1, {
			content: button.tooltip,
			openDelay: 200,
			closeDelay: 100,
			positioning: { placement: "top" },
			children: /* @__PURE__ */ jsx(Box$1, { children: /* @__PURE__ */ jsx(Popover$1.Trigger, {
				asChild: true,
				children: /* @__PURE__ */ jsx(IconButton$1, {
					variant: isActive ? "surface" : "ghost",
					colorPalette: "gray",
					size: "xs",
					"aria-label": button.ariaLabel,
					children: /* @__PURE__ */ jsx(LinkIcon, {})
				})
			}) })
		}), /* @__PURE__ */ jsx(Portal$1, { children: /* @__PURE__ */ jsx(Popover$1.Positioner, { children: /* @__PURE__ */ jsx(Popover$1.Content, {
			width: "320px",
			children: /* @__PURE__ */ jsx(Popover$1.Body, { children: /* @__PURE__ */ jsxs(Flex$1, {
				direction: "column",
				gap: 4,
				children: [
					/* @__PURE__ */ jsx(Text$1, {
						fontSize: "sm",
						fontWeight: "medium",
						marginBottom: 0,
						children: isActive ? titles.edit : titles.add
					}),
					/* @__PURE__ */ jsxs(Field$1.Root, {
						invalid: isUrlInvalid,
						children: [/* @__PURE__ */ jsx(Input, {
							placeholder,
							value: url,
							onChange: onInputChange,
							onKeyDown: onInputKeyDown,
							autoFocus: true
						}), /* @__PURE__ */ jsx(Field$1.ErrorText, { children: invalidUrlMessage })]
					}),
					/* @__PURE__ */ jsxs(Flex$1, {
						gap: 2,
						justifyContent: "flex-end",
						children: [
							isActive && /* @__PURE__ */ jsx(Button, {
								variant: "outline",
								size: "sm",
								onClick: handleRemoveLink,
								colorPalette: "red",
								children: actionButtons.remove
							}),
							/* @__PURE__ */ jsx(Button, {
								variant: "outline",
								size: "sm",
								onClick: () => setIsPopoverOpen(false),
								children: actionButtons.cancel
							}),
							/* @__PURE__ */ jsx(Button, {
								size: "sm",
								onClick: handleAddLink,
								disabled: !url || isUrlInvalid,
								color: "brand.contrast",
								children: isActive ? actionButtons.edit : actionButtons.add
							})
						]
					})
				]
			}) })
		}) }) })]
	});
};
// src/components/rich-text-editor/components/separator.tsx
const Separator$1 = () => /* @__PURE__ */ jsx(Box$1, {
	width: "1px",
	height: "24px",
	backgroundColor: "gray.300",
	alignSelf: "center"
});
// src/components/rich-text-editor/components/use-horizontal-scroll.ts
/**
* Tracks whether a horizontally scrollable element hides content on each side
* and scrolls it by most of its visible width.
* `isEnabled` lets the caller re-attach the listeners once the elements are rendered.
*/
const useHorizontalScroll = (isEnabled) => {
	const scrollRef = useRef(null);
	const contentRef = useRef(null);
	const [hiddenSides, setHiddenSides] = useState({
		start: false,
		end: false
	});
	useEffect(() => {
		const scrollElement = scrollRef.current;
		const contentElement = contentRef.current;
		if (!isEnabled || !scrollElement || !contentElement) return;
		const updateHiddenSides = () => {
			const { scrollLeft, clientWidth, scrollWidth } = scrollElement;
			setHiddenSides({
				start: scrollLeft > 0,
				end: scrollLeft + clientWidth < scrollWidth - 1
			});
		};
		updateHiddenSides();
		const resizeObserver = new ResizeObserver(updateHiddenSides);
		resizeObserver.observe(scrollElement);
		resizeObserver.observe(contentElement);
		scrollElement.addEventListener("scroll", updateHiddenSides, { passive: true });
		return () => {
			resizeObserver.disconnect();
			scrollElement.removeEventListener("scroll", updateHiddenSides);
		};
	}, [isEnabled]);
	const scroll = (direction) => {
		const scrollElement = scrollRef.current;
		if (!scrollElement) return;
		const distance = scrollElement.clientWidth * .8;
		scrollElement.scrollBy({
			left: direction === "start" ? -distance : distance,
			behavior: "smooth"
		});
	};
	return {
		scrollRef,
		contentRef,
		hiddenSides,
		scroll
	};
};
// src/components/rich-text-editor/components/toolbar.tsx
/**
* Toolbar container for RichTextEditor.
* Controls stay on one line and scroll horizontally, with arrows shown when
* some of them are hidden. Controls passed to `end` are always visible.
*
* @example
* ```tsx
* <RichTextEditor.Toolbar end={<RichTextEditor.UndoButton />}>
*   <RichTextEditor.BoldButton />
*   <RichTextEditor.ItalicButton />
*   <RichTextEditor.Separator />
*   <RichTextEditor.Image />
* </RichTextEditor.Toolbar>
* ```
*/
function RichTextEditorToolbar({ children, end, scrollStartLabel = "Voir les options précédentes", scrollEndLabel = "Voir plus d'options" }) {
	const { editor } = useRichTextEditorContext();
	const { scrollRef, contentRef, hiddenSides, scroll } = useHorizontalScroll(Boolean(editor));
	if (!editor) return null;
	return /* @__PURE__ */ jsxs(Flex$1, {
		alignItems: "center",
		gap: "1",
		width: "fit-content",
		maxWidth: "full",
		minWidth: "0",
		marginX: "auto",
		boxShadow: "md",
		borderRadius: "md",
		padding: "1",
		background: "white",
		children: [/* @__PURE__ */ jsxs(Box$1, {
			position: "relative",
			flex: "0 1 auto",
			minWidth: "0",
			children: [
				/* @__PURE__ */ jsx(Flex$1, {
					ref: scrollRef,
					overflowX: "auto",
					padding: "1",
					css: {
						scrollbarWidth: "none",
						"&::-webkit-scrollbar": { display: "none" }
					},
					children: /* @__PURE__ */ jsx(Flex$1, {
						ref: contentRef,
						alignItems: "center",
						gap: "1",
						flexShrink: "0",
						children
					})
				}),
				hiddenSides.start && /* @__PURE__ */ jsx(ScrollArrow, {
					direction: "start",
					label: scrollStartLabel,
					onClick: () => scroll("start")
				}),
				hiddenSides.end && /* @__PURE__ */ jsx(ScrollArrow, {
					direction: "end",
					label: scrollEndLabel,
					onClick: () => scroll("end")
				})
			]
		}), end && /* @__PURE__ */ jsxs(Flex$1, {
			alignItems: "center",
			gap: "1",
			padding: "1",
			children: [/* @__PURE__ */ jsx(Separator$1, {}), end]
		})]
	});
}
const ScrollArrow = ({ direction, label, onClick }) => {
	const isStart = direction === "start";
	return /* @__PURE__ */ jsx(Flex$1, {
		position: "absolute",
		top: "0",
		bottom: "0",
		left: isStart ? "0" : void 0,
		right: isStart ? void 0 : "0",
		alignItems: "center",
		paddingLeft: isStart ? "0" : "4",
		paddingRight: isStart ? "4" : "0",
		background: `linear-gradient(to ${isStart ? "right" : "left"}, white 60%, transparent)`,
		children: /* @__PURE__ */ jsx(Tooltip$1, {
			content: label,
			openDelay: 200,
			closeDelay: 100,
			positioning: { placement: "top" },
			children: /* @__PURE__ */ jsx(IconButton$1, {
				variant: "ghost",
				colorPalette: "gray",
				size: "xs",
				"aria-label": label,
				onClick,
				children: isStart ? /* @__PURE__ */ jsx(ChevronLeftIcon, {}) : /* @__PURE__ */ jsx(ChevronRightIcon, {})
			})
		})
	});
};
// src/components/rich-text-editor/root.tsx
const editorStyles = { "& .tiptap": {
	padding: "4",
	minHeight: "200px",
	outline: "none",
	backgroundColor: "white",
	"& h1": {
		fontSize: "1.375rem",
		fontWeight: "bold",
		marginTop: "1.5rem",
		lineHeight: "1.2"
	},
	"& h2": {
		fontSize: "1.125rem",
		fontWeight: "bold",
		marginTop: "1.25rem",
		marginBottom: "0.75rem",
		lineHeight: "1.3"
	},
	"& p": {
		fontSize: "1rem",
		lineHeight: "1.7",
		"& > *": { wordBreak: "break-all" },
		"&.is-editor-empty:first-of-type:before": {
			color: "gray.400",
			content: "attr(data-placeholder)",
			float: "left",
			height: 0,
			pointerEvents: "none"
		}
	},
	"& ul, & ol": {
		paddingLeft: "1.5rem",
		marginBottom: "0.75rem"
	},
	"& ul": { listStyleType: "disc" },
	"& ol": {
		listStyleType: "decimal",
		"& li::marker": {
			fontSize: "1rem",
			lineHeight: "1.7"
		}
	},
	"& li": { marginBottom: "0.25rem" },
	"& blockquote": {
		borderLeft: "4px solid",
		borderColor: "gray.300",
		paddingLeft: "1rem",
		marginLeft: "0",
		marginBottom: "0.75rem",
		fontStyle: "italic",
		color: "gray.600"
	},
	"& em": { fontStyle: "italic" },
	"& strong": { fontWeight: "bold" },
	"& img": {
		maxWidth: "100%",
		height: "auto",
		borderRadius: "md",
		margin: "auto"
	},
	"& img[alt$=\"-initial\"]": { width: "100%" },
	"& img[alt$=\"-small\"]": { width: "33%" },
	"& img[alt$=\"-medium\"]": {
		width: "66%",
		margin: "auto"
	},
	"& img[alt$=\"-large\"]": { width: "100%" },
	"& img.ProseMirror-selectednode": {
		outline: "2px solid",
		outlineColor: "brand.solid",
		outlineOffset: "4px"
	},
	"& a": {
		color: "brand.solid",
		fontWeight: "bold",
		textDecoration: "underline"
	}
} };
const RichTextEditorRoot = ({ editor: instance, children, minHeight, maxHeight }) => {
	const { editor } = instance;
	return /* @__PURE__ */ jsx(RichTextEditorContext.Provider, {
		value: instance,
		children: /* @__PURE__ */ jsx(Field$1.Root, {
			css: { display: "contents" },
			children: /* @__PURE__ */ jsx(Box$1, {
				borderWidth: "1px",
				borderColor: "gray.300",
				borderRadius: "sm",
				overflow: "hidden",
				width: "full",
				children: /* @__PURE__ */ jsxs(Flex$1, {
					alignItems: "center",
					direction: "column",
					paddingX: "2.5",
					paddingBottom: "2.5",
					gap: "5",
					backgroundColor: "white",
					overflowY: maxHeight ? "scroll" : "auto",
					position: "relative",
					maxHeight,
					minHeight,
					children: [children, /* @__PURE__ */ jsx(Box$1, {
						asChild: true,
						css: editorStyles,
						width: "full",
						children: /* @__PURE__ */ jsx(EditorContent, { editor })
					})]
				})
			})
		})
	});
};
// src/components/rich-text-editor/use-rich-text-editor.ts
const useRichTextEditor = ({ value, onChange, features, onImageUpload, imageSizes, placeholder = "Start typing your content here..." }) => {
	return {
		editor: useEditor({
			extensions: [...extensions, Placeholder.configure({ placeholder })],
			content: getTiptapContent(value),
			onUpdate: ({ editor }) => {
				onChange?.({
					rawContent: editor.getJSON(),
					htmlContent: editor.getHTML()
				});
			}
		}),
		features,
		onImageUpload,
		imageSizes
	};
};
// src/components/rich-text-editor/index.ts
const RichTextEditor = {
	Root: RichTextEditorRoot,
	Header,
	Toolbar: RichTextEditorToolbar,
	Image: Image$1,
	LinkButton: RichTextEditorLinkButton,
	BoldButton,
	ItalicButton,
	UnderlineButton,
	H1Button,
	H2Button,
	BulletListButton,
	OrderedListButton,
	QuoteButton,
	UndoButton,
	RedoButton,
	Separator: Separator$1
};
// src/components/switch.tsx
const Switch$1 = forwardRef(function Switch$2(props, ref) {
	const { inputProps, children, rootRef, trackLabel, thumbLabel, ...rest } = props;
	return /* @__PURE__ */ jsxs(Switch.Root, {
		ref: rootRef,
		...rest,
		children: [
			/* @__PURE__ */ jsx(Switch.HiddenInput, {
				ref,
				...inputProps
			}),
			/* @__PURE__ */ jsxs(Switch.Control, { children: [/* @__PURE__ */ jsx(Switch.Thumb, { children: thumbLabel && /* @__PURE__ */ jsx(Switch.ThumbIndicator, {
				fallback: thumbLabel?.off,
				children: thumbLabel?.on
			}) }), trackLabel && /* @__PURE__ */ jsx(Switch.Indicator, {
				fallback: trackLabel.off,
				children: trackLabel.on
			})] }),
			children != null && /* @__PURE__ */ jsx(Switch.Label, { children })
		]
	});
});
// src/components/table/context.tsx
const TableContext = createContext({});
const useTableColumnCount = () => {
	const { columnCount } = useContext(TableContext);
	if (columnCount === void 0) throw new Error("useTableColumnCount must be used within a Table.Root with a columnCount prop");
	return columnCount;
};
// src/components/table/row.tsx
const Row = forwardRef(function Row({ isEmpty, ...props }, ref) {
	return /* @__PURE__ */ jsx(Table.Row, {
		ref,
		css: {
			"&:nth-of-type(odd) td": { bg: isEmpty ? "bg" : "bg.subtle" },
			"& td": isEmpty ? { borderBottomColor: "bg" } : void 0
		},
		...props
	});
});
function MessageRow({ height = 36, children }) {
	const columnCount = useTableColumnCount();
	return /* @__PURE__ */ jsx(Row, { children: /* @__PURE__ */ jsx(Table.Cell, {
		colSpan: columnCount,
		height,
		textAlign: "center",
		children
	}) });
}
function FillerRow({ height = 12 }) {
	const columnCount = useTableColumnCount();
	return /* @__PURE__ */ jsx(Row, {
		isEmpty: true,
		children: /* @__PURE__ */ jsx(Table.Cell, {
			colSpan: columnCount,
			height
		})
	});
}
function FillerRows({ count, height }) {
	return Array.from({ length: Math.max(count, 0) }).map((_, index) => /* @__PURE__ */ jsx(FillerRow, { height }, index));
}
// src/components/table/skeleton.tsx
function SkeletonCell({ height = 12 }) {
	return /* @__PURE__ */ jsx(Table.Cell, {
		height,
		children: /* @__PURE__ */ jsx(Skeleton, { height: "5" })
	});
}
function SkeletonActionsCell({ count = 1, height = 12 }) {
	return /* @__PURE__ */ jsx(Table.Cell, {
		height,
		children: /* @__PURE__ */ jsx(Flex$1, {
			alignItems: "center",
			justifyContent: "flex-end",
			gap: "2",
			children: Array.from({ length: count }).map((_, index) => /* @__PURE__ */ jsx(Skeleton, {
				height: "5",
				width: "5"
			}, index))
		})
	});
}
function SkeletonRow({ height }) {
	const columnCount = useTableColumnCount();
	return /* @__PURE__ */ jsx(Row, { children: Array.from({ length: columnCount }).map((_, index) => /* @__PURE__ */ jsx(SkeletonCell, { height }, index)) });
}
// src/components/table/data-body.tsx
function DataBody({ isPending, isError = false, isEmpty, emptyContent, errorContent, skeletonRowCount = 5, renderSkeletonRow, rowHeight = 12, messageRowHeight = rowHeight * 3, children }) {
	if (isPending) return /* @__PURE__ */ jsx(Table.Body, { children: Array.from({ length: skeletonRowCount }).map((_, index) => /* @__PURE__ */ jsx(Fragment, { children: renderSkeletonRow ? renderSkeletonRow() : /* @__PURE__ */ jsx(SkeletonRow, { height: rowHeight }) }, index)) });
	if (isError) return /* @__PURE__ */ jsx(Table.Body, { children: /* @__PURE__ */ jsx(MessageRow, {
		height: messageRowHeight,
		children: errorContent
	}) });
	if (isEmpty) return /* @__PURE__ */ jsx(Table.Body, { children: /* @__PURE__ */ jsx(MessageRow, {
		height: messageRowHeight,
		children: /* @__PURE__ */ jsxs(Flex$1, {
			direction: "column",
			alignItems: "center",
			gap: "2",
			color: "gray.focusRing",
			children: [/* @__PURE__ */ jsx(CircleOffIcon, { size: "md" }), emptyContent]
		})
	}) });
	return /* @__PURE__ */ jsx(Table.Body, { children });
}
// src/components/table/root.tsx
function Root({ striped, columnCount, ...props }) {
	return /* @__PURE__ */ jsx(TableContext.Provider, {
		value: { columnCount },
		children: /* @__PURE__ */ jsx(Table.Root, {
			striped,
			css: striped ? {
				borderCollapse: "separate",
				borderSpacing: "0",
				"& td, & th": { borderBottomWidth: "0" }
			} : void 0,
			...props
		})
	});
}
// src/components/table/index.ts
const Table$1 = {
	Root,
	Row,
	Body: Table.Body,
	DataBody,
	SkeletonRow,
	SkeletonCell,
	SkeletonActionsCell,
	MessageRow,
	FillerRow,
	FillerRows,
	Caption: Table.Caption,
	Cell: Table.Cell,
	Column: Table.Column,
	ColumnGroup: Table.ColumnGroup,
	ColumnHeader: Table.ColumnHeader,
	Footer: Table.Footer,
	Header: Table.Header,
	ScrollArea: Table.ScrollArea
};
// src/components/toaster/ToastAction.tsx
function ToastAction({ children, onClick }) {
	return /* @__PURE__ */ jsx(Button, {
		size: "xs",
		variant: "outline",
		onClick,
		colorPalette: "accent",
		color: "black",
		borderColor: "gray.300",
		background: "white",
		_hover: { background: "gray.50" },
		_active: { background: "gray.100" },
		_focus: { background: "gray.100" },
		children
	});
}
ToastAction.displayName = "ToastAction";
// src/components/toaster/toaster-store.ts
const toaster = createToaster({
	placement: "top-end",
	pauseOnPageIdle: true,
	overlap: true,
	max: 5,
	duration: 5e3
});
function createToast(type, title, options) {
	return toaster.create({
		type,
		title,
		description: options?.description,
		closable: true,
		meta: options?.action ? { action: options.action } : void 0,
		...options?.duration ? { duration: options.duration } : {},
		...options?.id ? { id: options.id } : {}
	});
}
const toast = {
	success: (title, options) => createToast("success", title, options),
	error: (title, options) => createToast("error", title, {
		duration: Infinity,
		...options
	}),
	warning: (title, options) => createToast("warning", title, {
		duration: Infinity,
		...options
	}),
	info: (title, options) => createToast("info", title, options),
	loading: (title, options) => createToast("loading", title, options),
	custom: (render, options) => toaster.create({
		meta: { custom: render },
		...options?.duration ? { duration: options.duration } : {},
		...options?.id ? { id: options.id } : {}
	}),
	dismiss: toaster.dismiss,
	update: toaster.update,
	promise: toaster.promise
};
// src/components/toaster/ToastIndicator.tsx
const STATUS_ICON = {
	success: CircleCheckIcon,
	error: CircleXIcon,
	warning: InfoIcon,
	info: InfoIcon
};
function ToastIndicator({ type }) {
	if (type === "loading") return /* @__PURE__ */ jsx(Flex$1, {
		boxSize: "5",
		alignItems: "center",
		justifyContent: "center",
		children: /* @__PURE__ */ jsx(Spinner, {
			boxSize: "4",
			color: "colorPalette.solid"
		})
	});
	const StatusIcon = STATUS_ICON[type] ?? STATUS_ICON.info;
	return /* @__PURE__ */ jsx(StatusIcon, {
		boxSize: "5",
		color: "colorPalette.solid"
	});
}
ToastIndicator.displayName = "ToastIndicator";
// src/components/toaster/useToastProgress.ts
function useToastProgress(duration) {
	const ref = useRef(null);
	useEffect(() => {
		const bar = ref.current;
		if (!bar) return;
		const root = bar.closest("[data-scope=\"toast\"][data-part=\"root\"]");
		let frame = 0;
		let elapsed = 0;
		let last = performance.now();
		const tick = (now) => {
			const delta = now - last;
			last = now;
			if (root?.dataset.paused === void 0) elapsed += delta;
			const progress = Math.min(1, elapsed / duration);
			bar.style.transform = `scaleX(${progress})`;
			if (elapsed < duration) frame = requestAnimationFrame(tick);
		};
		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [duration]);
	return ref;
}
// src/components/toaster/ToastProgress.tsx
function ToastProgress({ duration }) {
	const ref = useToastProgress(duration);
	return /* @__PURE__ */ jsx(Box$1, {
		ref,
		position: "absolute",
		bottom: "0",
		insetInline: "0",
		height: "3px",
		transformOrigin: "left",
		transform: "scaleX(0)",
		background: "colorPalette.solid",
		borderEndRadius: "md"
	});
}
ToastProgress.displayName = "ToastProgress";
// src/components/toaster/Toaster.tsx
const STATUS_PALETTE = {
	success: "green",
	error: "red",
	warning: "yellow",
	info: "blue",
	loading: "gray"
};
function Toaster$1() {
	return /* @__PURE__ */ jsx(Portal$1, { children: /* @__PURE__ */ jsx(Toaster, {
		toaster,
		insetInline: { mdDown: "4" },
		children: (toast) => {
			const meta = toast.meta;
			if (meta?.custom) return /* @__PURE__ */ jsx(Toast.Root, {
				unstyled: true,
				children: meta.custom(toast.id ?? "")
			}, toast.id);
			const type = toast.type ?? "info";
			const colorPalette = STATUS_PALETTE[type] ?? STATUS_PALETTE.info;
			const duration = toast.duration;
			const showProgress = type !== "loading" && Number.isFinite(duration);
			return /* @__PURE__ */ jsxs(Toast.Root, {
				colorPalette,
				position: "relative",
				overflow: "hidden",
				width: { md: "sm" },
				gap: "1.5",
				alignItems: "flex-start",
				background: "colorPalette.50",
				borderRadius: "md",
				boxShadow: "sm",
				children: [
					/* @__PURE__ */ jsx(ToastIndicator, { type }),
					/* @__PURE__ */ jsxs(Flex$1, {
						flexDirection: "column",
						flex: "1",
						gap: "0",
						maxWidth: "100%",
						children: [
							toast.title && /* @__PURE__ */ jsx(Toast.Title, {
								color: "black",
								fontWeight: "bold",
								children: toast.title
							}),
							toast.description && /* @__PURE__ */ jsx(Toast.Description, {
								color: "fg",
								children: toast.description
							}),
							meta?.action && /* @__PURE__ */ jsx(Box$1, {
								marginTop: "2",
								children: meta.action
							})
						]
					}),
					/* @__PURE__ */ jsx(Toast.CloseTrigger, {
						asChild: true,
						children: /* @__PURE__ */ jsx(CloseButton, {
							size: "2xs",
							color: "gray.500"
						})
					}),
					showProgress && typeof duration === "number" && /* @__PURE__ */ jsx(ToastProgress, { duration })
				]
			}, toast.id);
		}
	}) });
}
Toaster$1.displayName = "Toaster";
// src/components/toggle-tip.tsx
const ToggleTip = forwardRef(function ToggleTip(props, ref) {
	const { showArrow, children, portalled = true, content, contentProps, portalRef, ...rest } = props;
	return /* @__PURE__ */ jsxs(Popover.Root, {
		...rest,
		positioning: {
			...rest.positioning,
			gutter: 4
		},
		children: [/* @__PURE__ */ jsx(Popover.Trigger, {
			asChild: true,
			children
		}), /* @__PURE__ */ jsx(Portal, {
			disabled: !portalled,
			container: portalRef,
			children: /* @__PURE__ */ jsx(Popover.Positioner, { children: /* @__PURE__ */ jsxs(Popover.Content, {
				width: "auto",
				padding: "3",
				textStyle: "xs",
				rounded: "sm",
				ref,
				...contentProps,
				children: [showArrow && /* @__PURE__ */ jsx(Popover.Arrow, { children: /* @__PURE__ */ jsx(Popover.ArrowTip, {}) }), content]
			}) })
		})]
	});
});
const InfoTip = forwardRef(function InfoTip(props, ref) {
	const { children, buttonProps, ...rest } = props;
	return /* @__PURE__ */ jsx(ToggleTip, {
		content: children,
		...rest,
		ref,
		children: /* @__PURE__ */ jsx(IconButton, {
			variant: "ghost",
			"aria-label": "info",
			size: "2xs",
			colorPalette: "gray",
			color: "fg.muted",
			...buttonProps,
			children: /* @__PURE__ */ jsx(InfoIcon, {})
		})
	});
});
export { Skeleton as $, ImageCrop as A, IconButton$1 as At, DownloadTrigger as B, ProgressCircle$1 as C, InputGroup as Ct, Menu$1 as D, parseDate as Dt, Pagination$1 as E, DatePicker$1 as Et, FileUpload$1 as F, Alert as Ft, useListCollection as G, useCombobox as H, Link as I, ActionBar$1 as It, Clipboard$1 as J, ColorPicker$1 as K, Drawer as L, Accordion as Lt, Highlight as M, Button as Mt, Heading as N, Box$1 as Nt, ImageUploadCrop as O, CalendarEventChip as Ot, Grid$1 as P, Badge as Pt, Calendar$1 as Q, DragAndDrop as R, QrCode$1 as S, Flex$1 as St, Panel as T, Field$1 as Tt, useComboboxContext as U, Combobox$1 as V, useFilter as W, Checkbox$1 as X, CheckboxCard$1 as Y, CheckboxGroup as Z, getEditorValue as _, Group as _t, toaster as a, Portal$1 as at, serializeEditorValue as b, PasswordInput as bt, Table$1 as c, ScrollArea as ct, Separator as d, TagsInput$1 as dt, SkeletonCircle as et, Select as f, useTagsInput as ft, generateJSONFromHTML as g, RadioCardCollapsible as gt, useRichTextEditor as h, RadioGroup$1 as ht, toast as i, TimeInput as it, Image$2 as j, CloseButton as jt, ImageUpload as k, CalendarEventTime as kt, Switch$1 as l, TimeField as lt, RichTextEditor as m, Radio as mt, Toaster$1 as n, Dialog as nt, ToastAction as o, Popover$1 as ot, createListCollection as p, SegmentGroup as pt, parseColor as q, ToastIndicator as r, Tooltip$1 as rt, Tabs as s, Text$1 as st, InfoTip as t, SkeletonText as tt, Spinner as u, Textarea as ut, getSampleFromEditorValue as v, Collapsible as vt, Progress as w, Input as wt, useRichTextEditorContext as x, PasswordStrengthMeter as xt, isEditorEmpty as y, RadioCard$1 as yt, useDragAndDrop as z };

//# sourceMappingURL=components-9RkKXUJL.js.map